import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  Users, Search, X, ChevronRight, DollarSign,
  CreditCard, Check, Calendar, GraduationCap, Bus, Utensils,
  AlertCircle, FileText, ArrowLeft, Loader2, Plus,
  Filter, ChevronDown, Eye, Download, Edit, Calculator, HelpCircle, UserMinus, Network, Globe, Star, Archive, MoreHorizontal,
  User2, History
} from 'lucide-react';

import { Button } from './ui/button';
import { useFeatures } from '../contexts/FeaturesContext';
import { api } from '../services/api';
import ModalModifierFamille from './ModalModifierFamille';
import LoadingSpinner from './LoadingSpinner';
import { Toast } from '../components/ui/Toast';
import { LongToast, getLongToastTitle, shouldUseLongToast } from './ui/LongToast';
import StatistiquesEnfants from './StatistiquesEnfants';
import ModalAjouterEleve from './ModalAjouterEleve';
import ModalModifierReductionEleve from './ModalModifierReductionEleve';
import ModalCreerFamilleDepuisEleves from './ModalCreerFamilleDepuisEleves';
import ConfirmDialog from './ConfirmDialog';
import ModalMoisNonScolarises from './ModalMoisNonScolarises';
import ModalRattacherFamilleInterSite from './ModalRattacherFamilleInterSite';
import ArchiveFamilles from './ArchiveFamilles';
import GuideInterSiteFamilles from './GuideInterSiteFamilles';
import GuideInterSiteDetail from './GuideInterSiteDetail';

// Statuts renvoyés par GET /familles/:id/paiements-par-mois pour lesquels le mois
// ne doit plus être encaissable (déjà réglé, soldé ailleurs, ou non facturable).
const STATUTS_MOIS_NON_PAYABLES = [
  'Payé',
  'Payé au prorata',
  'Soldé',
  'Non scolarisé',
  'Partiel (Non scolarisé)'
];

const estMoisNonPayable = (mois) => STATUTS_MOIS_NON_PAYABLES.includes(mois?.statut);

// Ce que raconte une ligne d'historique de valeur. Les noms de types sont des
// identifiants techniques : « montant_mensuel_total » ne dit rien a qui lit une
// fiche de famille.
const LIBELLES_HISTORIQUE_VALEUR = {
  reduction_mensuel: 'Réduction mensuelle',
  reduction_inscription: "Réduction sur les frais de début d'année",
  montant_mensuel_total: 'Montant mensuel total de la famille',
  nettoyage_reduction_reference: 'Valeur de référence remise à zéro',
  mois_non_scolarises: 'Mois non scolarisés',
  prorata_famille: 'Prorata appliqué à la famille'
};

// Ce que raconte une ligne d'historique inter-sites, en français plutôt qu'en
// nom de type. Ces opérations engagent une autre école : le lecteur d'ici ne
// peut pas en deviner l'origine, il faut la lui nommer.
const LIBELLES_OPERATION_INTERSITE = {
  rattachement_intersite: 'Rattachement à une famille du groupe',
  retrait_eleve_intersite: 'Retrait d\'un élève de la famille du groupe',
  reduction_propagee_intersite: 'Réduction propagée par le site de référence',
  sortie_famille_intersite: 'Sortie du groupe inter-sites'
};

// Un champ d'historique peut contenir une valeur simple ou un objet sérialisé.
const estJson = (valeur) => {
  const texte = String(valeur ?? '').trim();
  return texte.startsWith('{') || texte.startsWith('[');
};

const lireJson = (valeur) => {
  try { return JSON.parse(String(valeur ?? '')); } catch { return null; }
};

/**
 * Ce qu'une ligne d'historique raconte, en français.
 *
 * Retourne null quand la ligne se lit déjà telle quelle — une réduction passée de
 * 500 à 1200 n'a pas besoin d'être reformulée. Ne sert que pour les types dont le
 * contenu est un objet, illisible sans interprétation.
 */
const decrireChangementValeur = (ligne) => {
  if (ligne.type === 'prorata_famille') {
    const apres = lireJson(ligne.apres) || {};
    const nb = Number(apres.nb_eleves_appliques || 0);
    const erreurs = Number(apres.nb_erreurs || 0);
    const enFcfa = `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(apres.montant_total_prorata) || 0))} FCFA`;
    return `${enFcfa} répartis sur ${nb} élève${nb > 1 ? 's' : ''}`
      + (apres.mois ? ` pour ${apres.mois}` : '')
      + (erreurs > 0 ? ` — ${erreurs} en erreur` : '');
  }

  if (ligne.type !== 'mois_non_scolarises') return null;

  const apres = lireJson(ligne.apres) || {};
  const detail = Array.isArray(apres.detail) ? apres.detail : [];
  const marques = Number(apres.nb_mois_marques || 0);
  const ignores = Number(apres.nb_mois_ignores || 0);

  const parEleve = detail
    .filter((item) => Number(item.nb_mois || 0) > 0)
    .map((item) => `${item.eleve || `Élève #${item.eleve_id}`} (${item.nb_mois} mois)`)
    .join(', ');

  const debut = marques > 0
    ? `${marques} mois marqué${marques > 1 ? 's' : ''} non scolarisé${marques > 1 ? 's' : ''}`
    : 'Aucun mois marqué';

  return [
    debut,
    parEleve ? ` — ${parEleve}` : '',
    ignores > 0 ? `. ${ignores} ignoré${ignores > 1 ? 's' : ''} car déjà payé${ignores > 1 ? 's' : ''}` : ''
  ].join('');
};

// Origine d'une réduction, telle que le moteur la qualifie.
//
// Construite depuis les sources, jamais depuis le seul drapeau « utilise la
// réduction famille » : un élève peut n'avoir qu'une réduction exceptionnelle — il
// était alors étiqueté « aucune » — ou en cumuler deux, l'exceptionnelle s'ajoutant
// toujours à la valeur de famille ou à la personnalisée.
const BADGE_ORIGINE_REDUCTION = {
  famille: { libelle: 'Valeur famille', classe: 'border-[#cfe3ec] bg-[#eef7fb] text-[#246990]' },
  personnalisee: { libelle: 'Personnalisée', classe: 'border-purple-200 bg-purple-50 text-purple-700' },
  exceptionnelle: { libelle: 'Exceptionnelle', classe: 'border-amber-200 bg-amber-50 text-amber-700' },
  mixte: { libelle: 'Cumul de deux', classe: 'border-indigo-200 bg-indigo-50 text-indigo-700' },
  aucune: null
};

// Largeur fixe : les badges se lisent alors comme une colonne, pas comme une
// suite de pastilles de tailles variables collées aux noms.
// Les noms de classe vont de « CM1 » a « Terminal D » : sans largeur fixe, la
// colonne ondule d'une ligne a l'autre.
const BADGE_CLASSE_CLASSES = 'inline-flex items-center justify-center w-[104px] h-6 rounded bg-blue-100 text-blue-700 text-xs font-medium whitespace-nowrap px-2';

const BADGE_ORIGINE_CLASSES = 'inline-flex items-center justify-center w-[112px] h-6 rounded-full border px-2 text-[10px] font-semibold whitespace-nowrap';

// Décompose une ligne de réduction en ses sources, pour ne l'afficher que lorsque
// plusieurs se cumulent — un total seul ne dit pas d'où il vient.
const detaillerSourcesReduction = (ligne) => {
  if (!ligne) return [];
  return [
    ligne.famille > 0 && `${ligne.famille.toLocaleString('fr-FR')} famille`,
    ligne.personnalisee > 0 && `${ligne.personnalisee.toLocaleString('fr-FR')} perso.`,
    ligne.exceptionnelle > 0 && `${ligne.exceptionnelle.toLocaleString('fr-FR')} except.`
  ].filter(Boolean);
};

// Taille standard des badges de statut : largeur et hauteur fixes, texte insécable.
// Tous les badges s'affichent donc à l'identique quelle que soit la longueur du libellé
// (132px = le plus long libellé mesuré, "Partiel (non scol.)").
const BADGE_STATUT_CLASSES = 'inline-flex items-center justify-center gap-1 w-[132px] h-6 px-2 rounded-full text-xs font-medium whitespace-nowrap';

const BADGES_STATUT_MOIS = {
  'Payé': { tone: 'text-green-700 bg-green-100', Icon: Check, label: 'Payé' },
  'Payé au prorata': { tone: 'text-blue-700 bg-blue-100', Icon: Calculator, label: 'Payé au prorata' },
  'Soldé': { tone: 'text-teal-700 bg-teal-100', Icon: Check, label: 'Soldé' },
  'Partiel': { tone: 'text-orange-700 bg-orange-100', Icon: AlertCircle, label: 'Partiel' },
  'Non payé': { tone: 'text-red-700 bg-red-100', Icon: X, label: 'Non payé' },
  'Non scolarisé': { tone: 'text-gray-700 bg-gray-200', Icon: Calendar, label: 'Non scolarisé' },
  'Partiel (Non scolarisé)': { tone: 'text-gray-600 bg-gray-100', Icon: Calendar, label: 'Partiel (non scol.)' }
};

const BadgeStatutMois = ({ statut }) => {
  const badge = BADGES_STATUT_MOIS[statut];
  if (!badge) return null;

  const { Icon } = badge;
  return (
    <span className={`${BADGE_STATUT_CLASSES} ${badge.tone}`} title={statut}>
      <Icon className="w-3 h-3 shrink-0" />
      {badge.label}
    </span>
  );
};

// `lienDirect` / `onLienDirectTraite` : ouverture pilotée par l'adresse (panneau
// des alertes critiques), lue et retirée par GestionPaiementsEleves. Sans lien,
// l'écran se comporte exactement comme avant.
const FamillesNombreuses = ({ onClose, lienDirect = null, onLienDirectTraite }) => {
  // ========== ÉTATS DE BASE ==========
  const [viewMode, setViewMode] = useState('liste');
  const [selectedFamille, setSelectedFamille] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNbEnfants, setFilterNbEnfants] = useState('');
  const [loading, setLoading] = useState(false);

  // 🆕 États pour le dialog de confirmation
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [eleveARetirer, setEleveARetirer] = useState(null);
  const [motifRetraitEleve, setMotifRetraitEleve] = useState('');

  // ETAT POUR LES ARCHIVES
  const [showArchives, setShowArchives] = useState(false);
  const [showConfirmArchiver, setShowConfirmArchiver] = useState(false);
  const [motifArchivage, setMotifArchivage] = useState('');
  const [archivageLoading, setArchivageLoading] = useState(false);

  //ETAT POUR LA RADIATION
  const [radierElevesArchivage, setRadierElevesArchivage] = useState(true);
  // ETAT POUR SELECT MOTIF ARCHIVAGE
  const [isMotifDropdownOpen, setIsMotifDropdownOpen] = useState(false);

  // ETAT POUR LE GUIDE FAMILLE INTER SITE 
  // L'inter-sites traverse tout cet ecran — bouton de liaison, guide, filtre,
  // pastilles, sortie de reseau, motif d'archivage. Rien de tout cela n'a de
  // sens sans le module : une ecole seule n'a pas de reseau dont sortir.
  const { estActive } = useFeatures();
  const interSitesOuvert = estActive('module.intersites');

  const [showGuideInterSite, setShowGuideInterSite] = useState(false);
  const [showGuideInterSiteDetail, setShowGuideInterSiteDetail] = useState(false);

  const [filterInterSite, setFilterInterSite] = useState('');


  // ETAT POUR DESACTIVER UNE FAMILLE 
  const [showConfirmDesactiverIS, setShowConfirmDesactiverIS] = useState(false);
  const [motifDesactivationIS, setMotifDesactivationIS] = useState('');
  const [desactivationLoading, setDesactivationLoading] = useState(false);

  // ETAT POUR LE MENU V2
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // ETAT POUR RELIER UNE FAMILLE AU HUB(Famille qui avait deja était intersite)
  const [relierHubLoading, setRelierHubLoading] = useState(false);
  const [showConfirmRelierHub, setShowConfirmRelierHub] = useState(false);

  //Etats pour les mois non scolarisé 
  const [showModalMoisNonScolarises, setShowModalMoisNonScolarises] = useState(false);


  // ETAT POUR LA PAGINATION DE LA LISTE DES FAMILLES 
  const [countsType, setCountsType] = useState({ intersite: 0, locale: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFamilles, setTotalFamilles] = useState(0);
  const FAMILLES_PER_PAGE = 12;


  // ============ ÉTATS INTER-SITE ============
  const [interSiteInfo, setInterSiteInfo] = useState(null);
  const [showModalRattacher, setShowModalRattacher] = useState(false);
  const [vueConsolidee, setVueConsolidee] = useState(null);
  const [activationLoading, setActivationLoading] = useState(false);
  const [showConfirmInterSite, setShowConfirmInterSite] = useState(false);
  // Ce que le dialogue d'activation annonce doit venir du serveur. Il lisait
  // l'état client de la ligne selectionnee : une valeur remise a zero ailleurs
  // continuait d'y apparaitre, et l'ecran promettait de propager une reduction
  // qui n'existait plus.
  const [apercuActivationIS, setApercuActivationIS] = useState(null);

  // 🆕 États pour les données réelles
  const [famillesData, setFamillesData] = useState([]);
  const [anneeScolaireId, setAnneeScolaireId] = useState(null);
  const [elevesFamille, setElevesFamille] = useState([]);
  const [statsGlobales, setStatsGlobales] = useState(null);
  const [historiqueValeurs, setHistoriqueValeurs] = useState([]);
  const [historiqueValeursOuvert, setHistoriqueValeursOuvert] = useState(false);
  const [rapportParMois, setRapportParMois] = useState([]);
  // Entrées et sorties de l'année, avec ce que la famille a réellement encaissé
  // pendant la présence de chacun. Affiché une fois en tête du rapport.
  const [mouvementsComposition, setMouvementsComposition] = useState([]);
  const [operationsInterSites, setOperationsInterSites] = useState([]);
  const [membresARegulariser, setMembresARegulariser] = useState([]);
  const [totaux, setTotaux] = useState(null);
  const [moisScolaires, setMoisScolaires] = useState([]);
  const [modesPaiement, setModesPaiement] = useState([]);

  // États pour le paiement
  const [montantPaiement, setMontantPaiement] = useState('');
  const [typePaiement, setTypePaiement] = useState('total');
  const [moisSelectionnes, setMoisSelectionnes] = useState([]);
  const [modePaiement, setModePaiement] = useState('');
  const [observations, setObservations] = useState('');
  const [showRapportFinancier, setShowRapportFinancier] = useState(false);
  const [activeCategoriePaiement, setActiveCategoriePaiement] = useState('scolarite');
  const [annexesFamille, setAnnexesFamille] = useState({});
  const [annexesPonctuelsFamille, setAnnexesPonctuelsFamille] = useState({});
  const [paiementPonctuelFamille, setPaiementPonctuelFamille] = useState({
    date_service: new Date().toISOString().split('T')[0],
    type_paiement: 'journee',
    sens_transport: 'aller',
    montant: '',
    observations: ''
  });
  const [elevesPonctuelsSelectionnes, setElevesPonctuelsSelectionnes] = useState([]);
  const [loadingAnnexes, setLoadingAnnexes] = useState(false);

  const [modePaiementSearchTerm, setModePaiementSearchTerm] = useState('');
  const [isModePaiementDropdownOpen, setIsModePaiementDropdownOpen] = useState(false);

  // 🆕 États pour le dropdown des mois
  const [moisSearchTerm, setMoisSearchTerm] = useState('');
  const [isMoisDropdownOpen, setIsMoisDropdownOpen] = useState(false);


  // États pour le sélecteur de période
  const [selectedTrimestre, setSelectedTrimestre] = useState('1');
  const [selectedMois, setSelectedMois] = useState('');
  const [moisDisponibles, setMoisDisponibles] = useState([]);


  // États pour les sections pliables
  const [showListeEleves, setShowListeEleves] = useState(true);
  const [showTableauReductions, setShowTableauReductions] = useState(true);
  // Repliée par défaut : l'aide doit être disponible sans encombrer ceux qui
  // savent déjà lire le tableau.
  const [showAideLecture, setShowAideLecture] = useState(false);
  const [showAccordFinancier, setShowAccordFinancier] = useState(true);
  const [showTracabilite, setShowTracabilite] = useState(false);
  const [showResume, setShowResume] = useState(true);

  // 🆕 AJOUTEZ CETTE LIGNE
  const [elevesMoisSelectionne, setElevesMoisSelectionne] = useState([]);
  const [showModalModif, setShowModalModif] = useState(false);

  // 🆕 ETAT POUR LES STATS 
  const [statsEnfants, setStatsEnfants] = useState([]);


  //Etats pour ajouts élèves dans familles 
  const [showModalAjouterEleve, setShowModalAjouterEleve] = useState(false);
  // Modification de la réduction d'un membre : le seul autre geste par élève
  // nommé, à côté du retrait.
  const [eleveReductionAModifier, setEleveReductionAModifier] = useState(null);
  const [showModalCreerFamille, setShowModalCreerFamille] = useState(false);

  const [loadingTrimestre, setLoadingTrimestre] = useState(false);
  const [loadingPaiement, setLoadingPaiement] = useState(false);
  const [retraitLoading, setRetraitLoading] = useState(false);
  const periodeSelectionManuelleRef = useRef(false);
  // 🔔 Toast local
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [longToast, setLongToast] = useState({ show: false, title: '', message: '', type: 'error' });

  const fraisDebutAnneeLabels = React.useMemo(() => {
    const elevesActifs = (elevesFamille || []).filter((eleve) => eleve.statut_eleve === 'actif');
    const elevesReference = elevesActifs.length > 0 ? elevesActifs : (elevesFamille || []);
    const isReinscription = (eleve) =>
      eleve?.frais_debut_annee_type === 'reinscription'
      || String(eleve?.type_inscription || '').toLowerCase() === 'renewal'
      || String(eleve?.type_inscription || '').toLowerCase() === 'reinscription';

    const hasReinscription = elevesReference.some(isReinscription);
    const hasInscription = elevesReference.some((eleve) => !isReinscription(eleve));

    if (hasReinscription && !hasInscription) {
      return {
        table: "Frais de réinscription (début d'année)",
        resume: "Frais de réinscription (payés en début d'année)"
      };
    }

    if (hasReinscription && hasInscription) {
      return {
        table: "Frais inscription / réinscription",
        resume: "Frais inscription / réinscription (début d'année)"
      };
    }

    return {
      table: "Frais d'inscription (début d'année)",
      resume: "Frais d'inscription (payés en début d'année)"
    };
  }, [elevesFamille]);
  const categoriesPaiementFamille = React.useMemo(() => ([
    {
      id: 'scolarite',
      label: 'Scolarite',
      description: 'Paiements scolaires de la famille',
      icon: GraduationCap,
      ready: true
    },
    {
      id: 'transport',
      label: 'Transport',
      description: 'Frais de bus de la famille',
      icon: Bus,
      ready: true
    },
    {
      id: 'cantine',
      label: 'Cantine',
      description: 'Frais de cantine de la famille',
      icon: Utensils,
      ready: true
    }
  ]), []);

  const categoriePaiementActive = categoriesPaiementFamille.find((categorie) => categorie.id === activeCategoriePaiement) || categoriesPaiementFamille[0];



  // ========== 🆕 CHARGEMENT DES DONNÉES INITIALES ==========
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      let activeYearId = anneeScolaireId || localStorage.getItem('annee_scolaire_id') || null;
      // 1. Charger l'année scolaire active
      const anneeResult = await api.anneesScolaires.getActive();
      if (anneeResult.success && anneeResult.data) {
        activeYearId = localStorage.getItem('annee_scolaire_id') || anneeResult.data.id;
        setAnneeScolaireId(activeYearId);

        // 2. Charger les mois scolaires
        const moisResult = await api.moisScolaires?.getAll?.(activeYearId);
        if (moisResult?.success) {
          setMoisScolaires(moisResult.data);
        }
      }


      // 3. Charger les modes de paiement
      try {
        const modesResult = await (api.modesPaiement?.getAll?.() || api.finance.getModesPaiement());
        console.log('Modes de paiement chargés:', modesResult); // 🔍 Debug
        if (modesResult?.success && modesResult.data) {
          setModesPaiement(modesResult.data);
        } else {
          console.error('Erreur modes de paiement:', modesResult);
        }
      } catch (error) {
        console.error('Erreur chargement modes de paiement:', error);
        // Fallback : modes de paiement par défaut
        setModesPaiement([
          { id: 1, nom: 'Espèces' },
          { id: 2, nom: 'Virement bancaire' },
          { id: 3, nom: 'Chèque' },
          { id: 4, nom: 'Mobile Money' }
        ]);
      }


      // 4. Charger les familles (page 1)
      const famillesResult = await api.familles.getAll({
        page: 1,
        limit: FAMILLES_PER_PAGE,
        search: searchTerm || '',
        type: filterInterSite || '',
        nb_enfants: filterNbEnfants !== '' ? filterNbEnfants : '',
        annee_scolaire_id: activeYearId
      });
      if (famillesResult.success) {
        setFamillesData(famillesResult.data);
        if (famillesResult.pagination) {
          setCurrentPage(famillesResult.pagination.page);
          setTotalPages(famillesResult.pagination.totalPages);
          setTotalFamilles(famillesResult.pagination.total);
        }
        if (famillesResult.counts) {
          setCountsType(famillesResult.counts);
        }
      }

      // 5. Charger les statistiques
      const statsResult = await api.familles.getStatistiquesEnfants(activeYearId);
      if (statsResult.success) {
        setStatsEnfants(statsResult.data.statistiques);
      }

    } catch (error) {
      console.error('Erreur chargement données initiales:', error);
      showNotification('error', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };


  const loadFamilles = async (page = 1) => {
    setLoading(true);
    try {
      const result = await api.familles.getAll({
        page,
        limit: FAMILLES_PER_PAGE,
        search: searchTerm || '',
        type: filterInterSite || '',
        nb_enfants: filterNbEnfants !== '' ? filterNbEnfants : '',
        annee_scolaire_id: anneeScolaireId || localStorage.getItem('annee_scolaire_id')
      });
      if (result.success) {
        setFamillesData(result.data);
        if (result.pagination) {
          setCurrentPage(result.pagination.page);
          setTotalPages(result.pagination.totalPages);
          setTotalFamilles(result.pagination.total);
        }
        if (result.counts) {
          setCountsType(result.counts);
        }
      }
    } catch (error) {
      console.error('Erreur chargement familles:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleArchiverFamille = async () => {
    if (!motifArchivage) return;
    setArchivageLoading(true);
    try {
      const result = await api.familles.archiverFamille(selectedFamille.id, {
        motif: motifArchivage,
        annee_scolaire_id: anneeScolaireId,
        radier_eleves: radierElevesArchivage  // ← UTILISER L'ÉTAT
      });
      if (result.success) {
        showNotification('success', `Famille "${selectedFamille.nom}" archivée avec succès`);
        setShowConfirmArchiver(false);
        setMotifArchivage('');
        setRadierElevesArchivage(true);
        setViewMode('liste');
        setSelectedFamille(null);
        await loadInitialData();
      } else {
        showNotification('error', result.message || 'Erreur archivage');
      }
    } catch (error) {
      showNotification('error', 'Erreur lors de l\'archivage');
    } finally {
      setArchivageLoading(false);
    }
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);


  // ========== 🆕 CHARGER LES DÉTAILS D'UNE FAMILLE ==========
  // Rend `true` quand les détails de la famille ont bien été relus : le lien
  // direct n'ouvre une fenêtre de correction que sur des données à jour. Les
  // autres appels ignorent ce retour.
  const loadFamilleDetails = async (familleId) => {
    if (!anneeScolaireId) return false;

    let detailsCharges = false;
    setLoading(true);
    try {
      // 1. Charger les détails complets
      const detailsResult = await api.familles.getDetailsComplets(familleId, anneeScolaireId);

      if (detailsResult.success) {
        setSelectedFamille(detailsResult.data.famille);
        setElevesFamille(detailsResult.data.eleves);
        setStatsGlobales(detailsResult.data.stats_globales);
        setHistoriqueValeurs(detailsResult.data.historique_valeurs || []);
        detailsCharges = true;
      }

      // 2. Charger le rapport financier par mois
      const rapportResult = await api.familles.getPaiementsParMois(familleId, anneeScolaireId);

      if (rapportResult.success) {
        // 🆕 LOG COMPLET DE TOUS LES MOIS
        console.log('📊 ==== RAPPORT COMPLET PAR MOIS ====');
        console.log('Nombre de mois:', rapportResult.data.rapport_par_mois.length);

        rapportResult.data.rapport_par_mois.forEach((mois, index) => {
          console.log(`\n📅 Mois ${index + 1}: ${mois.mois_nom}`);
          console.log('  - Statut:', mois.statut);
          console.log('  - Total dû:', mois.total_du);
          console.log('  - Total payé:', mois.total_paye);
          console.log('  - Montant restant:', mois.total_du - mois.total_paye);
        });

        // 🎯 CHERCHER SPÉCIFIQUEMENT LES MOIS PAYÉS AU PRORATA
        const moisProrata = rapportResult.data.rapport_par_mois.filter(m =>
          m.statut === 'Payé au prorata' ||
          (m.total_paye > 0 && m.total_paye < m.total_du)
        );

        console.log('\n🔵 MOIS PAYÉS AU PRORATA (détecté):', moisProrata);

        setRapportParMois(rapportResult.data.rapport_par_mois);
      }


      if (rapportResult.success) {
        setRapportParMois(rapportResult.data.rapport_par_mois);
        setMouvementsComposition(rapportResult.data.mouvements_composition || []);
        setOperationsInterSites(rapportResult.data.operations_intersites || []);
        setMembresARegulariser(rapportResult.data.membres_a_regulariser || []);
      }

    } catch (error) {
      console.error('Erreur chargement détails famille:', error);
      showNotification('error', 'Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
    return detailsCharges;
  };




  const loadAnnexeFamille = async (categorie, familleIdParam = selectedFamille?.id) => {
    if (!familleIdParam || !anneeScolaireId || categorie === 'scolarite') return;

    setLoadingAnnexes(true);
    try {
      const [result, ponctuelsResult] = await Promise.all([
        api.familles.getAnnexes(familleIdParam, categorie, anneeScolaireId),
        api.familles.getAnnexesPonctuels(familleIdParam, categorie, anneeScolaireId)
      ]);

      if (result.success) {
        setAnnexesFamille((prev) => ({
          ...prev,
          [categorie]: result.data
        }));
      } else {
        showNotification('error', result.message || 'Erreur lors du chargement des frais annexes');
      }

      if (ponctuelsResult?.success) {
        setAnnexesPonctuelsFamille((prev) => ({
          ...prev,
          [categorie]: Array.isArray(ponctuelsResult.data) ? ponctuelsResult.data : []
        }));
      }
    } catch (error) {
      console.error('Erreur chargement frais annexes famille:', error);
      showNotification('error', error.message || 'Erreur lors du chargement des frais annexes');
    } finally {
      setLoadingAnnexes(false);
    }
  };

  useEffect(() => {
    if (viewMode !== 'detail') return;
    if (!selectedFamille?.id || activeCategoriePaiement === 'scolarite') return;
    loadAnnexeFamille(activeCategoriePaiement, selectedFamille.id);
  }, [activeCategoriePaiement, selectedFamille?.id, anneeScolaireId, viewMode]);
  // ========== CHARGER L'INFO INTER-SITE D'UNE FAMILLE ==========
  const loadInterSiteInfo = async (familleId) => {
    try {
      const result = await api.familles.getInterSiteInfo(familleId);
      if (result.success) {
        setInterSiteInfo(result.data);

        // Si la famille est inter-site, charger la vue consolidée
        if (result.data.is_intersite && anneeScolaireId) {
          const vueResult = await api.familles.getVueConsolidee(
            result.data.famille_uuid,
            anneeScolaireId
          );
          if (vueResult.success) {
            setVueConsolidee(vueResult.data);
          }
        }
      }
    } catch (error) {
      console.error('Erreur chargement info inter-site:', error);
    }
  };


  // ========== ACTIVER UNE FAMILLE EN INTER-SITE ==========
  const handleActiverInterSite = async () => {
    if (!selectedFamille) return;

    setActivationLoading(true);
    try {
      const result = await api.familles.activerInterSite(selectedFamille.id);

      if (result.success) {
        showNotification('success', `Famille "${selectedFamille.nom}" activée en inter-site !`);
        await loadInterSiteInfo(selectedFamille.id);
      } else {
        showNotification('error', result.error || 'Erreur activation inter-site');
      }
    } catch (error) {
      console.error('Erreur activation inter-site:', error);
      showNotification('error', 'Erreur lors de l\'activation inter-site');
    } finally {
      setActivationLoading(false);
    }
  };



  const handleRelierAuHub = async () => {
    if (!selectedFamille) return;
    setRelierHubLoading(true);
    try {
      const result = await api.familles.relierAuHub(selectedFamille.id);
      if (result.success) {
        showNotification('success', `Famille "${result.famille_nom}" re-liée au Hub ! (${result.nb_membres} membres)`);
        await loadInterSiteInfo(selectedFamille.id);
      } else {
        showNotification('error', result.error || 'Erreur re-liaison Hub');
      }
    } catch (error) {
      showNotification('error', 'Erreur lors de la re-liaison au Hub');
    } finally {
      setRelierHubLoading(false);
    }
  }




  const handleRattacherDepuisDistant = async () => {
    if (!selectedFamille || !interSiteInfo?.intersite_famille_uuid) return;

    try {
      showNotification('loading', 'Rattachement en cours...');

      // Récupérer les élèves actuels de la famille locale
      const eleveIds = elevesFamille
        ?.filter(e => e.statut_eleve === 'actif')
        .map(e => e.id) || [];

      if (eleveIds.length === 0) {
        showNotification('error', 'Aucun élève actif dans cette famille');
        return;
      }

      const result = await api.familles.rattacherInterSite(
        interSiteInfo.intersite_famille_uuid,
        eleveIds
      );

      if (result.success) {
        showNotification('success', `${eleveIds.length} élève(s) rattaché(s) au Hub Inter-Site !`);
        await loadInterSiteInfo(selectedFamille.id);
      } else {
        showNotification('error', result.error || 'Erreur rattachement');
      }
    } catch (error) {
      showNotification('error', 'Erreur lors du rattachement');
    }
  };


  const handleDesactiverInterSite = async () => {
    if (!motifDesactivationIS || !interSiteInfo?.famille_uuid) return;
    setDesactivationLoading(true);
    try {
      const result = await api.familles.desactiverInterSite(interSiteInfo.famille_uuid, {
        motif: motifDesactivationIS
      });
      if (result.success) {
        // La sortie du groupe défait des liens sur plusieurs écoles : elle passe
        // par le Centre de Support et n'est donc plus immédiate. Annoncer une
        // désactivation faite alors qu'elle est seulement demandée serait faux.
        let message = result.validation_required
          ? result.message
            || 'Demande envoyée au Centre de Support. La sortie du groupe sera effectuée après validation.'
          : `Famille "${result.famille_nom}" désactivée de l'inter-site.`;

        if (!result.validation_required && result.sites_traites) {
          const archivees = result.sites_traites.filter(s => s.action === 'archivee');
          const conservees = result.sites_traites.filter(s => s.action === 'conservee');
          if (archivees.length > 0) message += ` ${archivees.length} famille(s) distante(s) archivée(s).`;
          if (conservees.length > 0) message += ` ${conservees.length} famille(s) distante(s) conservée(s).`;
        }

        showNotification(result.validation_required ? 'info' : 'success', message, { long: true });
        setShowConfirmDesactiverIS(false);
        setMotifDesactivationIS('');
        setInterSiteInfo(null);
        setVueConsolidee(null);
        await loadFamilleDetails(selectedFamille.id);
        await loadInterSiteInfo(selectedFamille.id);
      } else {
        showNotification('error', result.error || 'Erreur désactivation');
      }
    } catch (error) {
      showNotification('error', 'Erreur lors de la désactivation inter-site');
    } finally {
      setDesactivationLoading(false);
    }
  };



  // ========== 🆕 CHARGER LES STATS POUR UN MOIS ==========
  const loadStatsMois = async (moisId) => {
    if (!selectedFamille || !anneeScolaireId || !moisId) return;

    try {
      // Charger les stats du mois
      const statsResult = await api.familles.getStatsMois(
        selectedFamille.id,
        moisId,
        anneeScolaireId
      );

      // 🆕 Stocker les élèves avec leurs montants du mois sélectionné
      if (statsResult.success && statsResult.data.eleves) {
        setElevesMoisSelectionne(statsResult.data.eleves);
      }

      // Charger les totaux
      const totauxResult = await api.familles.getTotaux(
        selectedFamille.id,
        moisId,
        anneeScolaireId
      );

      if (totauxResult.success) {
        setTotaux(totauxResult.data);
      }

    } catch (error) {
      console.error('Erreur chargement stats mois:', error);
    }
  };


  // Configuration des mois par trimestre (basée sur les vraies données)
  const getMoisParTrimestre = () => {
    const grouped = { '1': [], '2': [], '3': [] };

    moisScolaires.forEach(mois => {
      const trimestre = mois.trimestre.toString();
      if (grouped[trimestre]) {
        grouped[trimestre].push({
          id: mois.id,
          nom: mois.nom,
          ordre: mois.ordre,
          trimestre: mois.trimestre,
          date_debut: mois.date_debut,
          date_fin: mois.date_fin
        });
      }
    });

    // Trier par ordre
    Object.keys(grouped).forEach(trimestre => {
      grouped[trimestre].sort((a, b) => a.ordre - b.ordre);
    });

    return grouped;
  };

  const moisParTrimestre = getMoisParTrimestre();

  const normaliserTexte = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  const trouverMoisScolaireCourant = () => {
    if (!moisScolaires?.length) return null;

    const maintenant = new Date();
    const moisAvecDates = moisScolaires
      .map((mois) => ({
        ...mois,
        dateDebutObj: mois.date_debut ? new Date(mois.date_debut) : null,
        dateFinObj: mois.date_fin ? new Date(mois.date_fin) : null
      }))
      .filter((mois) =>
        mois.dateDebutObj instanceof Date &&
        mois.dateFinObj instanceof Date &&
        !Number.isNaN(mois.dateDebutObj.getTime()) &&
        !Number.isNaN(mois.dateFinObj.getTime())
      )
      .sort((a, b) => a.dateDebutObj - b.dateDebutObj);

    if (moisAvecDates.length > 0) {
      const moisExact = moisAvecDates.find((mois) => maintenant >= mois.dateDebutObj && maintenant <= mois.dateFinObj);
      if (moisExact) return moisExact;

      const moisAvenir = moisAvecDates.find((mois) => maintenant < mois.dateDebutObj);
      return moisAvenir || moisAvecDates[moisAvecDates.length - 1];
    }

    const nomMoisCourant = normaliserTexte(
      maintenant.toLocaleDateString('fr-FR', { month: 'long' })
    );

    return moisScolaires.find((mois) => normaliserTexte(mois.nom).includes(nomMoisCourant))
      || moisScolaires.find((mois) => nomMoisCourant.includes(normaliserTexte(mois.nom)))
      || moisScolaires[0];
  };

  // ========== EFFET POUR METTRE À JOUR LES MOIS DISPONIBLES ==========
  useEffect(() => {
    const mois = moisParTrimestre[selectedTrimestre] || [];

    // Démarre un petit état de chargement local
    setLoadingTrimestre(true);
    setMoisDisponibles(mois);

    const moisSelectionneDansTrimestre = mois.some(m => m.id.toString() === selectedMois?.toString());

    // Met à jour le mois uniquement si aucun mois valide n'est déjà sélectionné
    if (mois.length > 0 && !moisSelectionneDansTrimestre) {
      setSelectedMois(mois[0].id.toString());
    }

    // Petit délai pour lisser le rendu
    const timer = setTimeout(() => {
      setLoadingTrimestre(false);
    }, 150);

    return () => clearTimeout(timer);
  }, [selectedTrimestre, selectedMois, moisScolaires]);

  useEffect(() => {
    if (!selectedFamille || !moisScolaires?.length || periodeSelectionManuelleRef.current) return;

    const moisCourant = trouverMoisScolaireCourant();
    if (!moisCourant?.id) return;

    setSelectedTrimestre(String(moisCourant.trimestre || '1'));
    setSelectedMois(moisCourant.id.toString());
    setMoisSearchTerm('');
  }, [selectedFamille?.id, moisScolaires]);


  // ========== EFFET POUR CHARGER LES STATS QUAND LE MOIS CHANGE ==========
  useEffect(() => {
    if (selectedMois && selectedFamille) {
      loadStatsMois(selectedMois);
    }
  }, [selectedMois, selectedFamille]);



  // ========== FERMER LES DROPDOWNS QUAND ON CLIQUE EN DEHORS ==========
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est en dehors de tous les dropdowns
      const isOutsideClick = !event.target.closest('.relative');

      if (isOutsideClick) {
        setIsModePaiementDropdownOpen(false);
        setIsMoisDropdownOpen(false);
        setShowActionsMenu(false);
        setIsMotifDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);



  // ========== EFFET POUR CHARGER LES STATS QUAND LE MOIS CHANGE ==========
  useEffect(() => {
    if (selectedMois && selectedFamille) {
      loadStatsMois(selectedMois);
    }
  }, [selectedMois, selectedFamille]);


  useEffect(() => {
    if (typePaiement === 'solde_annuel' && rapportParMois.length > 0) {
      const moisASelectionner = rapportParMois
        .filter(mois => !estMoisNonPayable(mois))
        .map(mois => mois.mois_id);

      setMoisSelectionnes(moisASelectionner);
    }
  }, [typePaiement, rapportParMois]);


  useEffect(() => {
    if (anneeScolaireId) {
      loadFamilles(1);
    }
  }, [searchTerm, filterInterSite, filterNbEnfants]);


  // ========== FONCTIONS UTILITAIRES ==========
  // Aiguillage toast court / toast long : les messages metier longs (> 130 caracteres)
  // ou explicitement marques { long: true } passent par LongToast, le reste par Toast.
  const showNotification = (type, message, options = {}) => {
    const text = String(message || '');

    if (shouldUseLongToast(text, type, options)) {
      setToast({ visible: false, message: '', type: 'success' });
      setLongToast({
        show: true,
        title: getLongToastTitle(type, options.title),
        message: text,
        type
      });
      return;
    }

    setLongToast({ show: false, title: '', message: '', type: 'error' });
    setToast({ visible: true, message: text, type });
  };

  const closeLongToast = () => setLongToast({ show: false, title: '', message: '', type: 'error' });

  //Retirer le filtrage frontend puisque c'est le backend qui filtre maintenant.
  const famillesFiltrees = famillesData;



  const formatMontant = (montant) => {
    // 🆕 Toujours arrondir avant d'afficher (pas de décimales pour FCFA)
    const montantArrondi = Math.round(montant || 0);
    return new Intl.NumberFormat('fr-FR').format(montantArrondi) + ' FCFA';
  };

  const formatDateTime = (dateValue) => {
    if (!dateValue) return 'Non renseigne';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return 'Non renseigne';
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isFamilleARegulariser = (famille) => famille?.statut === 'a_regulariser';

  const getJoursRestantsFamille = (famille) => {
    if (!famille?.regularisation_deadline_at) return null;
    const deadline = new Date(famille.regularisation_deadline_at);
    if (Number.isNaN(deadline.getTime())) return null;
    const diff = deadline.getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  // Une échéance passée n'est pas « aujourd'hui » : à CSAP1, la famille 251
  // affichait ce libellé 19 jours après la sienne, parce que l'archivage
  // échouait. Elle est archivée au prochain affichage des familles.
  const echeanceFamilleDepassee = (famille) => {
    if (!famille?.regularisation_deadline_at) return false;
    const deadline = new Date(famille.regularisation_deadline_at);
    return !Number.isNaN(deadline.getTime()) && deadline.getTime() <= Date.now();
  };

  const getRegularisationFamilleLabel = (famille) => {
    const joursRestants = getJoursRestantsFamille(famille);
    if (joursRestants === null) return 'A regulariser';
    if (echeanceFamilleDepassee(famille)) return 'Délai dépassé';
    if (joursRestants === 0) return 'Archivage aujourd hui';
    return `J-${joursRestants}`;
  };

  const InfoTooltip = ({ children, className = '', position = 'top', width = 'w-72' }) => {
    const [visible, setVisible] = useState(false);
    const triggerRef = useRef(null);

    const widthPxByClass = {
      'w-64': 256,
      'w-72': 288,
      'w-80': 320,
      'w-96': 384
    };
    const widthPx = widthPxByClass[width] || 288;

    const getPanelStyle = () => {
      if (!triggerRef.current || typeof window === 'undefined') {
        return { left: 0, top: 0 };
      }

      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gap = 10;
      const padding = 12;
      const estimatedHeight = 150;
      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      const safePosition = position || 'top';

      let left = rect.left + (rect.width / 2) - (widthPx / 2);
      let top = rect.top - gap;
      let transform = 'translateY(-100%)';

      if (safePosition === 'bottom') {
        top = rect.bottom + gap;
        transform = 'none';
      }

      if (safePosition === 'right') {
        left = rect.right + gap;
        top = rect.top + (rect.height / 2);
        transform = 'translateY(-50%)';
        if (left + widthPx > viewportWidth - padding) {
          left = rect.left - widthPx - gap;
        }
      }

      if (safePosition === 'left') {
        left = rect.left - widthPx - gap;
        top = rect.top + (rect.height / 2);
        transform = 'translateY(-50%)';
        if (left < padding) {
          left = rect.right + gap;
        }
      }

      if (safePosition === 'top' && rect.top < estimatedHeight + padding) {
        top = rect.bottom + gap;
        transform = 'none';
      }

      if (safePosition === 'bottom' && rect.bottom + estimatedHeight > viewportHeight - padding) {
        top = rect.top - gap;
        transform = 'translateY(-100%)';
      }

      return {
        left: clamp(left, padding, viewportWidth - widthPx - padding),
        top: clamp(top, padding, viewportHeight - padding),
        transform
      };
    };

    const tooltip = visible && typeof document !== 'undefined'
      ? createPortal(
        <span
          className={`pointer-events-none fixed z-[9999] ${width} rounded-lg border border-[#246990]/20 bg-white p-3 text-left text-[11px] leading-relaxed text-gray-700 shadow-xl`}
          style={getPanelStyle()}
        >
          {children}
        </span>,
        document.body
      )
      : null;

    return (
      <>
        <span
          ref={triggerRef}
          className={`inline-flex items-center ${className}`}
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
          onFocus={() => setVisible(true)}
          onBlur={() => setVisible(false)}
          tabIndex={0}
        >
          <HelpCircle className="w-3.5 h-3.5 text-[#246990] cursor-help" />
        </span>
        {tooltip}
      </>
    );
  };

  const montantRestant = (du, paye) => Math.max(0, Math.round(Number(du || 0) - Number(paye || 0)));

  const getMontantMensuelEffectif = (eleve) => (
    eleve?.frais_mensuel_effectif ?? eleve?.frais_mensuel_avec_reduction ?? 0
  );

  const getAjustementAccord = (eleve) => Number(
    eleve?.ajustement_accord_mensuel ?? eleve?.accord_financier_detail?.ajustement_eleve ?? 0
  );

  const accordFinancierActif = statsGlobales?.accord_financier_actif || null;
  const accordEstRecompositionRetrait = accordFinancierActif?.mode_repartition === 'recomposition_retrait_eleve';
  const recompositionAccord = accordFinancierActif?.recomposition || null;
  const montantMensuelEffectif = statsGlobales?.montant_mensuel_effectif
    ?? statsGlobales?.total_mensuel_effectif
    ?? statsGlobales?.total_mensuel_avec_reduction
    ?? 0;
  const totalAnnuelEffectif = totaux?.total_annuel?.du
    ?? statsGlobales?.total_annuel_effectif
    ?? (montantMensuelEffectif * (statsGlobales?.nb_mois_scolarite || 0));
  const totalAnnuelData = totaux?.total_annuel || {};
  const familleAvecRetrait = Boolean(totalAnnuelData.a_des_retraits || accordEstRecompositionRetrait);
  const totalAnnuelDuReel = totalAnnuelData.du ?? totalAnnuelEffectif;
  const totalAnnuelPayeReel = totalAnnuelData.paye ?? statsGlobales?.total_paye ?? 0;

  // Décompose le total annuel pour la note explicative : combien de mois sont au
  // montant ordinaire, et lesquels s'en écartent — avec la raison. La gestionnaire
  // ne se demande pas comment le total est assemblé, elle se demande pourquoi il ne
  // vaut pas un mois multiplié par le nombre de mois. C'est à cela qu'on répond.
  const syntheseAnnuelle = (() => {
    if (!rapportParMois || rapportParMois.length === 0) return null;

    // Le montant le plus fréquent fait référence : c'est le mois « ordinaire ».
    const frequences = new Map();
    rapportParMois.forEach((mois) => {
      const montant = Math.round(Number(mois.total_du || 0));
      frequences.set(montant, (frequences.get(montant) || 0) + 1);
    });
    const [montantStandard, nbStandard] = [...frequences.entries()]
      .sort((a, b) => b[1] - a[1] || b[0] - a[0])[0];

    const ecarts = rapportParMois
      .filter((mois) => Math.round(Number(mois.total_du || 0)) !== montantStandard)
      .map((mois) => {
        const raisons = [];
        if (mois.nb_avant_integration > 0) {
          raisons.push(
            `${formatMontant(mois.montant_avant_integration)} réglés en gestion individuelle avant l'entrée dans la famille`
          );
        }
        if (mois.montant_non_recouvre > 0) {
          const nbRadies = Number(mois.nb_eleves_radies || 0);
          raisons.push(
            `${formatMontant(mois.montant_non_recouvre)} non recouvrés${nbRadies > 0
              ? ` (${nbRadies} élève${nbRadies > 1 ? 's' : ''} radié${nbRadies > 1 ? 's' : ''})`
              : ''}`
          );
        }
        return {
          nom: mois.mois_nom,
          montant: Math.round(Number(mois.total_du || 0)),
          raisons
        };
      });

    return { montantStandard, nbStandard, ecarts, nbMois: rapportParMois.length };
  })();
  const totalAnnuelResteReel = totalAnnuelData.reste ?? Math.max(0, totalAnnuelDuReel - totalAnnuelPayeReel);
  const baseMensuelleApresReductions = statsGlobales?.total_mensuel_avec_reduction ?? montantMensuelEffectif;
  const ajustementAccordMensuel = Math.abs(Number(accordFinancierActif?.ajustement_mensuel || 0));
  const moisSelectionneRapport = selectedMois
    ? rapportParMois.find((mois) => String(mois.mois_id) === String(selectedMois))
    : null;
  const selectedMoisNom = moisSelectionneRapport?.mois_nom
    || moisDisponibles.find(m => m.id.toString() === selectedMois)?.nom;

  const grouperMoisAccordParEleve = (items = []) => {
    const groupes = new Map();
    items.forEach((item) => {
      const key = item.eleve_id || item.eleve || 'inconnu';
      if (!groupes.has(key)) {
        groupes.set(key, {
          eleve_id: item.eleve_id,
          eleve: item.eleve || 'Eleve',
          nouveau_montant: item.nouveau_montant,
          ancien_montant: item.ancien_montant,
          raison: item.raison,
          mois: []
        });
      }
      const groupe = groupes.get(key);
      groupe.mois.push({
        id: item.mois_id,
        nom: item.mois,
        nouveau_montant: item.nouveau_montant,
        ancien_montant: item.ancien_montant,
        montant_paye: item.montant_paye,
        raison: item.raison
      });
    });

    return Array.from(groupes.values());
  };

  const moisImpactesParEleve = grouperMoisAccordParEleve(accordFinancierActif?.mois_impactes || []);
  const moisIgnoresParEleve = grouperMoisAccordParEleve(accordFinancierActif?.mois_ignores || []);

  const getRegularisationLabel = (regularisation) => {
    if (!regularisation) return '';
    const jours = Number(regularisation.jours_restants ?? 0);
    return jours <= 0 ? 'Archivage imminent' : `Archivage auto J-${jours}`;
  };

  const toggleMois = (moisId) => {
    // Vérifier si le mois est encore payable
    const moisInfo = rapportParMois.find(m => m.mois_id === moisId);
    if (estMoisNonPayable(moisInfo)) return;

    setMoisSelectionnes(prev =>
      prev.includes(moisId)
        ? prev.filter(id => id !== moisId)
        : [...prev, moisId]
    );
  };




  const getMoisStatutBadge = (moisId) => {
    const moisInfo = rapportParMois.find(m => m.mois_id === moisId);
    if (!moisInfo) return null;

    switch (moisInfo.statut) {
      case 'Payé':
        return {
          class: 'bg-green-100 text-green-800 border-green-300',
          text: '✓ Payé',
          disabled: true
        };
      case 'Payé au prorata':
        return {
          class: 'bg-blue-100 text-blue-800 border-blue-300',
          text: '✓ Payé au prorata',
          disabled: true
        };
      case 'Soldé':
        return {
          class: 'bg-teal-100 text-teal-800 border-teal-300',
          text: '✓ Soldé',
          disabled: true
        };
      case 'Partiel':
        return {
          class: 'bg-orange-100 text-orange-800 border-orange-300',
          text: `Partiel (${formatMontant(moisInfo.total_paye)})`,
          disabled: false
        };


      case 'Non scolarisé':
        return {
          class: 'bg-gray-200 text-gray-700 border-gray-400',
          text: 'Non scolarisé',
          disabled: true
        };
      case 'Partiel (Non scolarisé)':
        return {
          class: 'bg-gray-200 text-gray-700 border-gray-400',
          text: 'Partiel (Non scolarisé)',
          disabled: true
        };

      case 'Non payé':
        return {
          class: 'bg-red-50 text-red-600 border-red-200',
          text: 'Non payé',
          disabled: false
        };
      default:
        return null;
    }
  };



  const calculerRepartition = () => {
    if (!elevesFamille || !montantPaiement) return [];

    // 🆕 FILTRER LES ÉLÈVES RADIÉS
    const elevesActifs = elevesFamille.filter(e => e.statut_eleve !== 'radie');

    if (elevesActifs.length === 0) return [];

    const montant = Math.round(parseFloat(montantPaiement));

    // 🆕 CALCULER LE TOTAL SEULEMENT SUR LES ÉLÈVES ACTIFS
    const totalReste = elevesActifs.reduce((sum, e) => sum + Math.round(e.totaux.reste_a_payer), 0);

    if (totalReste === 0) {
      return elevesActifs.map(e => ({ ...e, montant_attribue: 0 }));
    }

    // 🆕 TRIER SEULEMENT LES ÉLÈVES ACTIFS
    const elevesTries = [...elevesActifs].sort((a, b) => {
      return Math.round(b.totaux.reste_a_payer) - Math.round(a.totaux.reste_a_payer);
    });

    let montantRestant = montant;
    const repartitions = [];

    console.log('🎨 Frontend - Aperçu répartition:', {
      montant_total: montant,
      nb_eleves_actifs: elevesTries.length, // 🆕 Préciser "actifs"
      total_reste: totalReste
    });

    elevesTries.forEach((eleve, index) => {
      let montantAttribue;

      if (index === elevesTries.length - 1) {
        montantAttribue = montantRestant;
        console.log(`  🎯 Dernier élève ${eleve.prenom}: reçoit le reste = ${montantRestant} FCFA`);
      } else {
        const resteAPayer = Math.round(eleve.totaux.reste_a_payer);
        const proportion = resteAPayer / totalReste;
        const montantProportionnel = Math.round(montant * proportion);

        montantAttribue = Math.min(montantProportionnel, resteAPayer, montantRestant);

        console.log(`  📝 ${eleve.prenom}:`, {
          reste_a_payer: resteAPayer,
          proportion: proportion.toFixed(4),
          montant_proportionnel: montantProportionnel,
          montant_attribue: montantAttribue
        });
      }

      montantAttribue = Math.max(0, Math.round(montantAttribue));
      montantRestant -= montantAttribue;

      repartitions.push({
        ...eleve,
        montant_attribue: montantAttribue
      });
    });

    console.log('✅ Frontend - Répartition terminée, reste:', montantRestant);

    return repartitions;
  };



  const handlePaiement = async () => {
    // ========== VALIDATION DES CHAMPS OBLIGATOIRES ==========
    if (!selectedFamille || !modePaiement) {
      showNotification('error', 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    // ========== VALIDATION : Au moins un mois sélectionné ==========
    if (isFamilleARegulariser(selectedFamille)) {
      showNotification('error', 'Cette famille est a regulariser. Ajoutez un membre ou attendez son archivage avant de reprendre les paiements famille.');
      return;
    }

    if (moisSelectionnes.length === 0) {
      showNotification('error', 'Veuillez sélectionner au moins un mois à payer dans la section "Mois concernés"');
      return;
    }

    // ========== CALCUL DU MONTANT SELON LE TYPE DE PAIEMENT ==========
    let montant;
    let moisAConcerner = [...moisSelectionnes]; // 🔑 Utiliser UNIQUEMENT les checkboxes

    if (typePaiement === 'total_annuel') {
      // 1️⃣ PAIEMENT ANNUEL : Payer tout le reste à payer pour l'année
      montant = statsGlobales?.reste_a_payer || 0;

      console.log('💰 Paiement total annuel:', {
        montant: montant,
        mois_concernes: moisAConcerner.length,
        mois_ids: moisAConcerner
      });

    } else if (typePaiement === 'mois_selectionnes') {
      // 2️⃣ PAIEMENT TOTAL DES MOIS SÉLECTIONNÉS
      // Calculer le total des mois cochés
      let totalDesMoisSelectionnes = 0;

      for (const moisId of moisAConcerner) {
        const moisInfo = rapportParMois.find(m => m.mois_id === moisId);
        if (moisInfo) {
          totalDesMoisSelectionnes += (moisInfo.total_du - moisInfo.total_paye);
        }
      }

      montant = totalDesMoisSelectionnes;

      console.log('💰 Paiement total des mois sélectionnés:', {
        montant: montant,
        mois_concernes: moisAConcerner.length,
        mois_ids: moisAConcerner
      });

    } else {
      // 3️⃣ PAIEMENT PARTIEL : Montant saisi par l'utilisateur
      montant = parseFloat(montantPaiement) || 0;

      console.log('💰 Paiement partiel:', {
        montant: montant,
        mois_concernes: moisAConcerner.length,
        mois_ids: moisAConcerner
      });
    }





    // ========== VALIDATION DU MONTANT ==========
    if (!montant || montant <= 0) {
      showNotification('error', 'Montant invalide : le montant doit être supérieur à 0');
      return;
    }

    // Vérifier que le montant ne dépasse pas le reste à payer
    const maxMontant = statsGlobales?.reste_a_payer || 0;
    if (montant > maxMontant) {
      showNotification('error', `Le montant (${formatMontant(montant)}) dépasse le reste à payer (${formatMontant(maxMontant)})`);
      return;
    }

    // ========== CONFIRMATION POUR PAIEMENT TOTAL ANNUEL ==========
    if (typePaiement === 'total_annuel') {
      showNotification('success', `Paiement annuel de ${formatMontant(montant)} en cours...`);
    }


    // ========== ENREGISTREMENT DU PAIEMENT ==========
    setLoadingPaiement(true);
    try {
      const paiementData = {
        montant: montant,
        mode_paiement_id: parseInt(modePaiement),
        type_paiement: typePaiement, // 'total_annuel', 'total_mensuel' ou 'partiel'
        mois_ids: moisAConcerner,
        annee_scolaire_id: anneeScolaireId,
        observations: observations || ''
      };

      console.log('📤 Envoi du paiement:', paiementData); // Debug

      const result = await api.familles.effectuerPaiement(
        selectedFamille.id,
        paiementData
      );

      if (result.success) {
        // ========== SUCCÈS ==========
        showNotification('success', `Paiement de ${formatMontant(montant)} effectué avec succès !`);

        // Afficher un résumé du paiement
        if (result.data && result.data.repartitions) {
          console.log('✅ Répartition du paiement:', result.data.repartitions);
        }

        // Recharger les données de la famille
        await loadFamilleDetails(selectedFamille.id);

        // Recharger les stats du mois si un mois est sélectionné
        if (selectedMois) {
          await loadStatsMois(selectedMois);
        }

        // Réinitialiser le formulaire
        resetPaiementForm();

      } else {
        // ========== ERREUR ==========
        showNotification('error', result.message || result.error || 'Erreur lors du paiement');
      }

    } catch (error) {
      console.error('❌ Erreur paiement:', error);
      showNotification('error', error.message || 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setLoadingPaiement(false);
    }
  };




  const getRapportAnnexeActif = () => annexesFamille[activeCategoriePaiement] || null;

  const getTotalAnnexeSelectionne = (rapportAnnexe) => {
    if (!rapportAnnexe) return 0;
    return (rapportAnnexe.rapport_par_mois || [])
      .filter((mois) => moisSelectionnes.includes(mois.mois_id))
      .reduce((sum, mois) => sum + Number(mois.reste_a_payer || 0), 0);
  };

  const getElevesPonctuelsDisponibles = (annexeData) => {
    const source = [
      ...(annexeData?.eleves_concernes || []),
      ...(annexeData?.eleves_non_concernes || [])
    ];
    const map = new Map();

    source.forEach((eleve) => {
      const id = Number(eleve.id || eleve.eleve_id);
      if (id) map.set(id, { ...eleve, id });
    });

    (elevesFamille || []).forEach((eleve) => {
      const id = Number(eleve.id || eleve.eleve_id);
      if (!id || map.has(id)) return;
      const statut = String(eleve.statut_eleve || eleve.statut || '').toLowerCase();
      if (statut && !['actif', 'active'].includes(statut)) return;
      map.set(id, { ...eleve, id });
    });

    return Array.from(map.values()).sort((a, b) => `${a.nom || ''} ${a.prenom || ''}`.localeCompare(`${b.nom || ''} ${b.prenom || ''}`));
  };

  const toggleElevePonctuel = (eleveId) => {
    const id = Number(eleveId);
    setElevesPonctuelsSelectionnes((prev) => prev.includes(id)
      ? prev.filter((currentId) => currentId !== id)
      : [...prev, id]
    );
  };

  const handlePaiementPonctuelFamilleChange = (field, value) => {
    setPaiementPonctuelFamille((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const resetPaiementPonctuelFamille = () => {
    setPaiementPonctuelFamille({
      date_service: new Date().toISOString().split('T')[0],
      type_paiement: 'journee',
      sens_transport: 'aller',
      montant: '',
      observations: ''
    });
    setElevesPonctuelsSelectionnes([]);
  };

  const getTotalPonctuelFamille = () => {
    const montantUnitaire = Math.round(Number(String(paiementPonctuelFamille.montant || '').replace(/[^0-9]/g, '')));
    return montantUnitaire * elevesPonctuelsSelectionnes.length;
  };

  const handlePaiementPonctuelFamille = async () => {
    if (!selectedFamille || activeCategoriePaiement === 'scolarite') return;

    const montantUnitaire = Math.round(Number(String(paiementPonctuelFamille.montant || '').replace(/[^0-9]/g, '')));
    if (!modePaiement) {
      showNotification('error', 'Veuillez selectionner un mode de paiement');
      return;
    }
    if (!paiementPonctuelFamille.date_service) {
      showNotification('error', 'Selectionnez la date du service');
      return;
    }
    if (elevesPonctuelsSelectionnes.length === 0) {
      showNotification('error', 'Selectionnez au moins un eleve concerne par le ticket');
      return;
    }
    if (!montantUnitaire || montantUnitaire <= 0) {
      showNotification('error', 'Saisissez un montant unitaire valide');
      return;
    }

    setLoadingPaiement(true);
    try {
      const result = await api.familles.effectuerPaiementAnnexePonctuel(selectedFamille.id, {
        categorie: activeCategoriePaiement,
        annee_scolaire_id: anneeScolaireId,
        eleve_ids: elevesPonctuelsSelectionnes,
        date_service: paiementPonctuelFamille.date_service,
        type_paiement: activeCategoriePaiement === 'cantine' ? 'journalier' : paiementPonctuelFamille.type_paiement,
        sens_transport: activeCategoriePaiement === 'transport' ? paiementPonctuelFamille.sens_transport : null,
        montant_unitaire: montantUnitaire,
        montant: montantUnitaire,
        mode_paiement_id: Number(modePaiement),
        observations: paiementPonctuelFamille.observations || ''
      });

      if (result.success) {
        showNotification('success', result.message || 'Paiement ponctuel enregistre avec succes');
        await loadAnnexeFamille(activeCategoriePaiement, selectedFamille.id);
        resetPaiementPonctuelFamille();
      } else {
        showNotification('error', result.message || 'Erreur lors du paiement ponctuel');
      }
    } catch (error) {
      console.error('Erreur paiement ponctuel famille:', error);
      showNotification('error', error.message || 'Erreur lors du paiement ponctuel');
    } finally {
      setLoadingPaiement(false);
    }
  };

  const handleTelechargerRecuAnnexePonctuelFamille = async (paiement) => {
    try {
      showNotification('loading', 'Generation du recu ponctuel en cours...');
      const pdfBlob = await api.finance.genererRecuAnnexePonctuel(paiement.paiement_id || paiement.id);
      if (!pdfBlob || pdfBlob.size === 0) throw new Error('Le fichier recu est vide');
      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Recu-annexe-${activeCategoriePaiement}-${paiement.date_service || paiement.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      showNotification('success', 'Recu ponctuel telecharge avec succes');
    } catch (error) {
      showNotification('error', error.message || 'Erreur lors du telechargement du recu ponctuel');
    }
  };
  const handlePaiementAnnexe = async () => {
    if (!selectedFamille || activeCategoriePaiement === 'scolarite') return;

    const rapportAnnexe = getRapportAnnexeActif();
    if (!rapportAnnexe?.stats_globales?.has_configured_fees) {
      showNotification('error', `Aucun frais de ${categoriePaiementActive.label.toLowerCase()} n'est configure pour cette famille.`);
      return;
    }

    if (!modePaiement) {
      showNotification('error', 'Veuillez selectionner un mode de paiement');
      return;
    }

    if (moisSelectionnes.length === 0) {
      showNotification('error', 'Selectionnez au moins un mois a payer');
      return;
    }

    const totalSelectionne = getTotalAnnexeSelectionne(rapportAnnexe);
    const montant = typePaiement === 'partiel'
      ? Math.round(Number(montantPaiement || 0))
      : totalSelectionne;

    if (!montant || montant <= 0) {
      showNotification('error', 'Montant invalide');
      return;
    }

    if (montant > totalSelectionne) {
      showNotification('error', `Le montant (${formatMontant(montant)}) depasse le reste a payer (${formatMontant(totalSelectionne)})`);
      return;
    }

    setLoadingPaiement(true);
    try {
      const result = await api.familles.effectuerPaiementAnnexe(selectedFamille.id, {
        categorie: activeCategoriePaiement,
        montant,
        mode_paiement_id: Number(modePaiement),
        type_paiement: typePaiement === 'partiel' ? 'partiel' : 'mois_selectionnes',
        mois_ids: moisSelectionnes,
        annee_scolaire_id: anneeScolaireId,
        observations: observations || ''
      });

      if (result.success) {
        showNotification('success', result.message || `Paiement ${categoriePaiementActive.label.toLowerCase()} enregistre avec succes`);
        await loadAnnexeFamille(activeCategoriePaiement, selectedFamille.id);
        resetPaiementForm();
        setTypePaiement('mois_selectionnes');
      } else {
        showNotification('error', result.message || 'Erreur lors du paiement annexe');
      }
    } catch (error) {
      console.error('Erreur paiement annexe famille:', error);
      showNotification('error', error.message || 'Erreur lors du paiement annexe');
    } finally {
      setLoadingPaiement(false);
    }
  };
  // ============================================================================
  // FONCTION : Retirer un élève de la famille
  // ============================================================================
  const handleRetirerEleve = async (eleveId) => {
    setRetraitLoading(true);
    try {
      const result = await api.familles.retirerEleve(selectedFamille.id, eleveId, {
        annee_scolaire_id: anneeScolaireId,
        motif_retrait: motifRetraitEleve.trim()
      });

      if (result.success) {
        setElevesMoisSelectionne((prev) => prev.map((eleve) => (
          Number(eleve.eleve_id) === Number(eleveId)
            ? {
              ...eleve,
              a_demande_retrait_en_cours: true,
              demande_retrait_en_cours: result.data?.demande || { id: result.data?.demande_id || null }
            }
            : eleve
        )));

        showNotification('success', result.message || 'Demande de retrait envoyee au Centre de Support');

        // Recharger les données de la famille
        await loadFamilleDetails(selectedFamille.id);

        // Recharger les stats du mois si un mois est sélectionné
        if (selectedMois) {
          await loadStatsMois(selectedMois);
        }
      } else {
        showNotification('error', result.message || 'Erreur lors du retrait de l\'élève');
      }
    } catch (error) {
      console.error('Erreur retrait élève:', error);
      showNotification('error', error.message || 'Erreur lors du retrait de l\'élève');
    } finally {
      setRetraitLoading(false);
    }
  };


  // ============================================================================
  // FONCTION : Télécharger le reçu de paiement d'un mois
  // ============================================================================
  const handleTelechargerRecu = async (mois) => {
    try {
      showNotification('loading', 'Génération du reçu en cours...');

      console.log('📄 Demande de génération reçu pour:', mois);

      // Appeler l'API pour générer le PDF
      const pdfBlob = await api.familles.genererRecuMois(
        selectedFamille.id,
        mois.mois_id,
        anneeScolaireId
      );

      console.log('📄 Blob reçu:', pdfBlob.size, 'bytes');

      // Vérifier que c'est bien un PDF
      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Le fichier reçu est vide');
      }

      const filename = `Recu-${selectedFamille.nom}-${mois.mois_nom}.pdf`;

      console.log('📄 Création du lien de téléchargement:', filename);

      // Créer un blob explicitement en PDF
      const pdfBlobFixed = new Blob([pdfBlob], { type: 'application/pdf' });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(pdfBlobFixed);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Nettoyer après un délai
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      showNotification('success', `Reçu pour ${mois.mois_nom} téléchargé avec succès !`);

    } catch (error) {
      console.error('Erreur téléchargement reçu:', error);
      showNotification('error', `Erreur: ${error.message}`);
    }
  };



  const handleTelechargerLivret = async () => {
    try {
      showNotification('loading', 'Génération du livret en cours...');

      const pdfBlob = await api.familles.genererLivret(selectedFamille.id, anneeScolaireId);

      if (!pdfBlob || pdfBlob.size === 0) {
        throw new Error('Le fichier reçu est vide');
      }

      const filename = `Livret-${selectedFamille.nom}.pdf`;
      const pdfBlobFixed = new Blob([pdfBlob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(pdfBlobFixed);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);

      showNotification('success', 'Livret téléchargé avec succès !');
    } catch (error) {
      console.error('Erreur téléchargement livret:', error);
      showNotification('error', `Erreur: ${error.message}`);
    }
  };



  const resetPaiementForm = () => {
    setMontantPaiement('');
    setTypePaiement('total_annuel');
    setMoisSelectionnes([]);
    setModePaiement('');
    setObservations('');
  };



  // ========== 🆕 FONCTION POUR OUVRIR UNE FAMILLE ==========
  // Rend le retour de loadFamilleDetails (détails relus ou non), que seul le
  // lien direct exploite.
  const handleOpenFamille = async (famille) => {
    setActiveCategoriePaiement('scolarite');
    setAnnexesFamille({});
    setAnnexesPonctuelsFamille({});
    setElevesPonctuelsSelectionnes([]);
    periodeSelectionManuelleRef.current = false;
    setSelectedMois('');
    setMoisSearchTerm('');
    setSelectedFamille(famille);
    setViewMode('detail');
    const detailsCharges = await loadFamilleDetails(famille.id);
    await loadInterSiteInfo(famille.id);
    return detailsCharges;
  };


  // ========== OUVERTURE PAR LIEN DIRECT (panneau des alertes critiques) ==========
  // Le lien ouvre une famille, puis « Modifier les informations » (`modifier`)
  // ou « Modifier la réduction » d'un de ses membres (`reduction`). Seuls les
  // gestes existants sont déclenchés, comme si l'agent avait cliqué : aucun
  // calcul ici. Rien ne s'ouvre si la famille ou l'élève n'est plus là sur
  // l'année affichée ; un message dit pourquoi.
  const lienDirectEnCoursRef = useRef(null);
  // Ce que l'écran affiche au dernier rendu. Après les attentes réseau, le lien
  // vérifie que l'agent est toujours sur la famille demandée (il a pu revenir à
  // la liste entre-temps) avant d'ouvrir une fenêtre par-dessus.
  const vueRef = useRef(viewMode);
  const familleAfficheeRef = useRef(null);
  vueRef.current = viewMode;
  familleAfficheeRef.current = selectedFamille?.id ?? null;

  const ouvrirFamilleParLien = async ({ famille: familleParam, eleve: eleveParam, ouvrir, signature }) => {
    const terminer = () => {
      if (onLienDirectTraite) onLienDirectTraite(signature);
    };
    const echouer = (message) => {
      showNotification('error', message, { long: true, title: 'Correction impossible' });
      terminer();
    };
    // Un refus du serveur (licence, droit) est rendu tel quel, suivi de la
    // conclusion commune à tous les échecs du lien.
    const conclure = (motif) => {
      const texte = String(motif || '').trim();
      if (!texte) return '';
      return `${texte}${/[.!?]$/.test(texte) ? '' : '.'} Rien n'a été ouvert.`;
    };

    const familleId = Number.parseInt(familleParam, 10);
    if (!Number.isInteger(familleId) || familleId <= 0) {
      echouer("Le lien de correction ne désigne aucune famille valide. Rien n'a été ouvert.");
      return;
    }

    // L'écran repart d'un état propre : aucune fenêtre de la famille précédente
    // ne doit rester ouverte par-dessus celle qu'on vient chercher.
    setShowArchives(false);
    setShowActionsMenu(false);
    setShowModalModif(false);
    setEleveReductionAModifier(null);
    showNotification('loading', 'Ouverture de la famille…');

    // Vérification préalable, sans toucher à l'écran : la famille existe-t-elle,
    // a-t-elle des membres actifs cette année, l'élève visé en fait-il partie ?
    let details = null;
    let motifEchec = '';
    try {
      const resultat = await api.familles.getDetailsComplets(familleId, anneeScolaireId);
      if (resultat?.success && resultat.data?.famille) {
        details = resultat.data;
      } else if (resultat?.success === false) {
        // Refus métier (licence fermée, droit manquant) : le serveur dit pourquoi.
        motifEchec = conclure(resultat.message || resultat.error);
      }
    } catch (error) {
      // Seul un 404 dit que la famille n'existe plus ; une erreur du serveur,
      // une coupure réseau ou une session expirée n'en disent rien.
      if (error?.status === 404) {
        motifEchec = "Famille introuvable : elle a peut-être été archivée ou supprimée. Rien n'a été ouvert.";
      } else if (error?.status === 401) {
        motifEchec = "Votre session a expiré : reconnectez-vous, puis rouvrez le lien. Rien n'a été ouvert.";
      } else {
        motifEchec = "La fiche de cette famille n'a pas pu être lue (erreur du serveur ou du réseau). "
          + "Réessayez dans un instant. Rien n'a été ouvert.";
      }
    }

    if (!details) {
      echouer(motifEchec
        || "La fiche de cette famille n'a pas pu être lue. Réessayez dans un instant. Rien n'a été ouvert.");
      return;
    }

    const nomFamille = details.famille.nom || 'Cette famille';
    const membres = Array.isArray(details.eleves) ? details.eleves : [];
    const membresActifs = membres.filter((membre) => membre.statut_eleve === 'actif');

    if (membresActifs.length === 0) {
      echouer(`La famille ${nomFamille} n'a aucun élève actif sur l'année scolaire affichée. `
        + "Si elle relève d'une autre année, reconnectez-vous sur cette année pour la corriger. Rien n'a été ouvert.");
      return;
    }

    let membreVise = null;
    if (ouvrir === 'reduction' && eleveParam) {
      membreVise = membresActifs.find((membre) => String(membre.id) === String(eleveParam)) || null;
      if (!membreVise) {
        const ancien = membres.find((membre) => String(membre.id) === String(eleveParam));
        echouer(ancien
          ? `${ancien.prenom} ${ancien.nom} n'est plus un membre actif de la famille ${nomFamille} : sa réduction ne se modifie plus. Rien n'a été ouvert.`
          : `Cet élève n'appartient pas à la famille ${nomFamille} sur l'année scolaire affichée. Rien n'a été ouvert.`);
        return;
      }
    }

    // Mêmes remises à zéro que « Retour à la liste des familles » : le lien
    // peut arriver alors qu'une autre famille est affichée, avec son formulaire
    // de paiement en ligne. Rien de cette famille ne doit rester sur la suivante.
    setToast({ visible: false, message: '', type: 'success' });
    setElevesFamille([]);
    setStatsGlobales(null);
    setRapportParMois([]);
    resetPaiementForm();
    setInterSiteInfo(null);
    setVueConsolidee(null);
    const detailsCharges = await handleOpenFamille(details.famille);

    // Pendant les attentes, l'agent a pu revenir à la liste ou ouvrir une autre
    // famille : la fenêtre ne s'ouvre que sur la famille demandée, encore affichée.
    const toujoursAffichee = vueRef.current === 'detail'
      && String(familleAfficheeRef.current) === String(details.famille.id);
    if (!toujoursAffichee) {
      terminer();
      return;
    }

    // Une fenêtre de correction ne s'ouvre que sur des données relues : sinon
    // elle partirait de chiffres vides ou périmés.
    if (!detailsCharges) {
      echouer(`La famille ${nomFamille} est affichée, mais ses données n'ont pas pu être rechargées`
        + `${ouvrir === 'modifier' || membreVise ? " : la fenêtre de correction n'a pas été ouverte" : ''}. `
        + 'Réessayez dans un instant.');
      return;
    }

    if (ouvrir === 'modifier') {
      setShowModalModif(true);
    } else if (membreVise) {
      setEleveReductionAModifier({
        id: membreVise.id,
        prenom: membreVise.prenom,
        nom: membreVise.nom,
        matricule: membreVise.matricule
      });
    } else if (ouvrir === 'reduction') {
      showNotification('info', "Le lien ne précise pas l'élève : choisissez-le dans la liste des membres.");
    }

    terminer();
  };

  // Le lien est traité une fois l'année connue. Le repère évite de le traiter
  // deux fois (double rendu, mode strict) ; il retombe quand le lien a quitté
  // l'adresse, pour qu'un second clic sur le même lien soit suivi.
  useEffect(() => {
    if (!lienDirect) {
      lienDirectEnCoursRef.current = null;
      return;
    }
    if (!lienDirect.famille || !anneeScolaireId) return;
    if (lienDirectEnCoursRef.current === lienDirect.signature) return;
    lienDirectEnCoursRef.current = lienDirect.signature;
    ouvrirFamilleParLien(lienDirect);
  }, [lienDirect?.signature, anneeScolaireId]);



  // ========== VUE ARCHIVES ==========
  if (showArchives) {
    return (
      <ArchiveFamilles
        anneeScolaireId={anneeScolaireId}
        onClose={() => { setShowArchives(false); loadInitialData(); }}
      />
    );
  }


  // ========== VUE LISTE  ET IMPORT STATS ENFANTS PAR FAMILLES ==========
  if (viewMode === 'liste') {
    return (
      <div className="w-full min-h-screen bg-gray-50 p-6">
        <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-6">

          {onClose && (
            <Button
              variant="ghost"
              className="mb-3 text-[#246990] hover:bg-[#246990]/10 w-fit flex items-center"
              onClick={onClose}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour à la gestion des paiements
            </Button>
          )}

          <div className="p-4 bg-[#eef7fb] rounded-lg border border-[#246990]/20 flex items-center mb-5">
            <div className="p-2 bg-[#246990] rounded-full mr-4">
              <Users className="text-white" size={20} />
            </div>
            <div className="flex-1">
              <h1 className="font-medium text-[#246990] text-xl">Familles Nombreuses</h1>
              <p className="text-sm text-gray-600">Gestion des paiements groupés</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#246990]">
                {totalFamilles}
              </div>
              <div className="text-sm text-gray-500">Familles enregistrées</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher par nom de famille..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
              />
            </div>

            {(searchTerm || filterNbEnfants) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterNbEnfants('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Bouton Lier à famille inter-site (depuis la liste) */}
            <button
              onClick={() => setShowModalCreerFamille(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#246990] text-white rounded-lg hover:bg-[#1f5a7a] transition-colors font-medium text-sm whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              Creer une famille
            </button>

            {interSitesOuvert && (
            <button
              onClick={() => setShowModalRattacher(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm whitespace-nowrap"
            >
              <Network className="w-4 h-4" />
              Liaison inter-site
            </button>
            )}


            <button
              onClick={() => setShowArchives(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium text-sm whitespace-nowrap"
            >
              <Archive className="w-4 h-4" />
              Archives
            </button>

            {interSitesOuvert && (
            <div className="relative group">
              <button
                onClick={() => setShowGuideInterSite(true)}
                className="flex items-center justify-center w-10 h-10 bg-[#246990] text-white rounded-lg hover:bg-[#1f5a7a] transition-colors"
              >
                <HelpCircle className="w-5 h-5" />
              </button>


              {/* Tooltip fixe */}
              <div className="absolute right-0 top-full mt-2 w-64 bg-white text-gray-700 text-xs rounded-lg px-3 py-2.5 shadow-lg border border-gray-200 pointer-events-none z-20">
                <p className="font-semibold text-[#246990] mb-1">Guide familles inter-sites</p>
                <p className="text-[10px] text-gray-500 leading-relaxed">Veuillez lire et comprendre le guide avant toute action sur les familles inter-sites.</p>
                <div className="absolute right-4 -top-1.5 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
              </div>

            </div>
            )}

          </div>
        </div>

        <StatistiquesEnfants
          statsEnfants={statsEnfants}
          filterNbEnfants={filterNbEnfants}
          setFilterNbEnfants={setFilterNbEnfants}
        />


        {/* Filtre Inter-Site. Sans le module, toutes les familles sont locales :
            le filtre proposerait un tri entre un tout et un vide. */}
        {interSitesOuvert && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-3">
            <Users className="w-4 h-4 text-[#246990]" />
            <span className="text-sm font-semibold text-gray-700">Type de famille :</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilterInterSite(filterInterSite === 'intersite' ? '' : 'intersite')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterInterSite === 'intersite'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                  }`}
              >
                <span className="flex items-center gap-1">
                  <Network size={10} />
                  Inter-Site ({countsType.intersite})
                </span>
              </button>
              <button
                onClick={() => setFilterInterSite(filterInterSite === 'locale' ? '' : 'locale')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filterInterSite === 'locale'
                    ? 'bg-gray-600 text-white shadow-sm'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                  }`}
              >
                Locale ({countsType.locale})
              </button>
              {filterInterSite && (
                <button
                  onClick={() => setFilterInterSite('')}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
        )}



        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner color="#246990" size={48} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {famillesFiltrees.map((famille) => {
                const nbEnfantsSite = famille.nombre_enfants_reel ?? famille.nombre_enfants ?? 0;
                return (
                  <div
                    key={famille.id}
                    className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleOpenFamille(famille)}
                  >
                    <div className="p-4 bg-[#eef7fb] border-b border-[#246990]/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-[#246990] text-white flex items-center justify-center mr-3">
                            <Users size={20} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-[#246990] text-lg">
                              {famille.nom}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {nbEnfantsSite} enfant{nbEnfantsSite > 1 ? 's' : ''}{famille.intersite_famille_uuid ? ' (sur ce site)' : ''}
                            </p>
                          </div>
                        </div>
                        <ChevronRight className="text-[#246990]" size={20} />
                      </div>
                    </div>

                    <div className="p-4 space-y-3">




                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2 flex-wrap">
                          {famille.intersite_famille_uuid ? (
                            <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${famille.was_intersite_reference
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-teal-100 text-teal-700'
                              }`}>
                              <Network size={10} />
                              {famille.was_intersite_reference ? 'Inter-Site (réf)' : 'Inter-Site'}
                            </span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                              Locale
                            </span>
                          )}

                          {isFamilleARegulariser(famille) && (
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
                              <AlertCircle size={10} />
                              A regulariser · {getRegularisationFamilleLabel(famille)}
                            </span>
                          )}

                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {famillesFiltrees.length === 0 && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune famille trouvée
                </h3>
                <p className="text-sm text-gray-600">
                  Aucune famille ne correspond à vos critères de recherche
                </p>
              </div>
            )}
          </>
        )}


        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between bg-white rounded-lg border border-gray-200 shadow-sm p-4 mt-6">
            <div className="text-xs text-gray-500">
              Affichage {((currentPage - 1) * FAMILLES_PER_PAGE) + 1} - {Math.min(currentPage * FAMILLES_PER_PAGE, totalFamilles)} sur {totalFamilles} famille{totalFamilles > 1 ? 's' : ''}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => loadFamilles(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                «
              </button>
              <button
                onClick={() => loadFamilles(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Précédent
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, idx, arr) => (
                  <React.Fragment key={page}>
                    {idx > 0 && arr[idx - 1] !== page - 1 && (
                      <span className="px-1 text-gray-400 text-xs">...</span>
                    )}
                    <button
                      onClick={() => loadFamilles(page)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${currentPage === page
                          ? 'bg-[#246990] text-white shadow-sm'
                          : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                        }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }

              <button
                onClick={() => loadFamilles(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Suivant
              </button>
              <button
                onClick={() => loadFamilles(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                »
              </button>
            </div>
          </div>
        )}

        {/* MODAL RATTACHER — accessible depuis la vue liste */}
        {showModalRattacher && (
          <ModalRattacherFamilleInterSite
            anneeScolaireId={anneeScolaireId}
            onClose={() => setShowModalRattacher(false)}
            onSuccess={async (data) => {
              showNotification('success', `${data.nb_rattaches} élève(s) rattaché(s) avec succès !`);
              setShowModalRattacher(false);
              await loadInitialData();
            }}
          />
        )}

        {showModalCreerFamille && (
          <ModalCreerFamilleDepuisEleves
            anneeScolaireId={anneeScolaireId}
            onClose={() => setShowModalCreerFamille(false)}
            onSuccess={async (data) => {
              if (data?.validation_required) {
                // Message long assume : il annonce un circuit, pas un resultat.
                // Sans `long: true` il passait de justesse sous le seuil des 130
                // caracteres et s'affichait dans un bandeau etroit - accents en
                // moins, ce qui trahissait sa redaction hative.
                showNotification(
                  'success',
                  `La demande de création de « ${data.nom} » a été envoyée au Centre de Support. `
                  + `${data.nombre_eleves} élève(s) y seront rattachés après validation. `
                  + `Rien n'est modifié tant que la demande n'est pas approuvée.`,
                  { long: true, title: 'Demande envoyée' }
                );
                setShowModalCreerFamille(false);
                await loadFamilles(1);
                await loadInitialData();
                return;
              }
              showNotification('success', `Famille « ${data.nom} » créée avec ${data.nombre_eleves} élève(s).`);
              setShowModalCreerFamille(false);
              await loadFamilles(1);
              await loadInitialData();
            }}
          />
        )}

        <LongToast
          show={longToast.show}
          title={longToast.title}
          message={longToast.message}
          type={longToast.type}
          onClose={closeLongToast}
        />

        {toast.visible && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, visible: false })}
          />
        )}

        <GuideInterSiteFamilles
          isOpen={showGuideInterSite}
          onClose={() => setShowGuideInterSite(false)}
        />

      </div>
    );
  }


  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <button
        onClick={() => {
          setViewMode('liste');
          setSelectedFamille(null);
          setElevesFamille([]);
          setStatsGlobales(null);
          setRapportParMois([]);
          resetPaiementForm();
          setInterSiteInfo(null);
          setVueConsolidee(null);
        }}
        className="mb-4 flex items-center text-[#246990] hover:text-[#1f5a7a] transition-colors"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour à la liste des familles
      </button>

      {loading && !selectedFamille ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner color="#246990" size={48} />
        </div>
      ) : (
        <>





          {/* En-tête famille — V2 Compact */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
            {/* Ligne principale */}
            <div className="p-4 bg-[#eef7fb] border-b border-[#246990]/20">
              <div className="flex items-center justify-between">
                {/* Gauche : Icône + Nom + Badge */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2.5 bg-[#246990] rounded-full flex-shrink-0">
                    <Users className="text-white" size={20} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="font-bold text-[#246990] text-xl truncate">
                        {selectedFamille?.nom}
                      </h1>


                      {/* Badge Inter-Site compact + Tooltip */}
                      {interSiteInfo?.is_intersite && (
                        <div className="relative group flex-shrink-0">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold cursor-help ${interSiteInfo.is_reference
                              ? 'bg-[#246990] text-white'
                              : 'bg-teal-100 text-teal-700 border border-teal-200'
                            }`}>
                            <Network size={10} />
                            {interSiteInfo.is_reference ? 'Réf. Inter-Site' : `Inter-Site (${interSiteInfo.site_reference})`}
                          </span>

                          {/* Tooltip */}
                          <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl p-3 hidden group-hover:block z-50">
                            <div className="text-xs text-left space-y-2">
                              <p className="font-bold text-[#246990] flex items-center gap-1.5">
                                <Network size={12} />
                                {interSiteInfo.is_reference ? 'Site de référence Inter-Site' : 'Famille liée en Inter-Site'}
                              </p>

                              {interSiteInfo.is_reference ? (
                                <div className="space-y-1.5 text-gray-600">
                                  <p>
                                    Cette famille a été <strong>activée en inter-site</strong> depuis ce site.
                                    En tant que <strong>site de référence</strong>, vous gérez les réductions
                                    et elles sont propagées automatiquement vers les autres sites du groupe.
                                  </p>
                                  <div className="p-2 bg-blue-50 rounded border border-blue-100 space-y-1">
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Membres total :</span>
                                      <span className="font-semibold text-[#246990]">{interSiteInfo.nb_membres_total}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Sites connectés :</span>
                                      <span className="font-semibold text-[#246990]">{interSiteInfo.nb_sites}</span>
                                    </div>
                                  </div>
                                  <p className="text-[10px] text-gray-400 italic">
                                    Vous pouvez désactiver l'inter-site depuis le menu Actions.
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-1.5 text-gray-600">
                                  <p>
                                    Cette famille est <strong>liée à une famille inter-site</strong> dont le site
                                    de référence est <strong>{interSiteInfo.site_reference}</strong>. Les réductions
                                    sont définies par le site de référence et appliquées automatiquement ici.
                                  </p>
                                  <div className="p-2 bg-teal-50 rounded border border-teal-100 space-y-1">
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Membres total :</span>
                                      <span className="font-semibold text-teal-700">{interSiteInfo.nb_membres_total}</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-gray-500">Site de référence :</span>
                                      <span className="font-semibold text-teal-700">{interSiteInfo.site_reference}</span>
                                    </div>
                                  </div>
                                  <p className="text-[10px] text-gray-400 italic">
                                    Seul le site de référence peut modifier les réductions ou désactiver l'inter-site.
                                  </p>
                                </div>
                              )}
                            </div>
                            {/* Flèche */}
                            <div className="absolute left-6 -top-2 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                          </div>
                        </div>
                      )}

                      {interSiteInfo?.regularisation && (
                        <div className="relative group flex-shrink-0">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold cursor-help bg-amber-100 text-amber-800 border border-amber-200">
                            <Calendar size={10} />
                            Sortie Inter-Site · {getRegularisationLabel(interSiteInfo.regularisation)}
                          </span>

                          <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-amber-200 rounded-lg shadow-xl p-3 hidden group-hover:block z-50">
                            <div className="text-xs text-left space-y-2">
                              <p className="font-bold text-amber-800 flex items-center gap-1.5">
                                <Calendar size={12} />
                                Famille à régulariser
                              </p>
                              <p className="text-gray-600">
                                Cette famille est sortie du réseau inter-site. Elle reste locale pour laisser le temps de corriger ou rattacher un autre élève.
                              </p>
                              <div className="p-2 bg-amber-50 rounded border border-amber-100 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Élèves actifs :</span>
                                  <span className="font-semibold text-amber-800">{interSiteInfo.regularisation.nb_eleves_actifs}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Échéance :</span>
                                  <span className="font-semibold text-amber-800">{getRegularisationLabel(interSiteInfo.regularisation)}</span>
                                </div>
                              </div>
                              <p className="text-[10px] text-gray-400 italic">
                                À l'échéance, la famille sera archivée automatiquement sans radier l'élève.
                              </p>
                            </div>
                            <div className="absolute left-6 -top-2 w-3 h-3 bg-white border-l border-t border-amber-200 transform rotate-45"></div>
                          </div>
                        </div>
                      )}

                      {isFamilleARegulariser(selectedFamille) && (
                        <div className="relative group flex-shrink-0">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold cursor-help bg-amber-100 text-amber-800 border border-amber-200">
                            <AlertCircle size={10} />
                            A regulariser · {getRegularisationFamilleLabel(selectedFamille)}
                          </span>
                          <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-amber-200 rounded-lg shadow-xl p-3 hidden group-hover:block z-50">
                            <div className="text-xs text-left space-y-2">
                              <p className="font-bold text-amber-800 flex items-center gap-1.5">
                                <AlertCircle size={12} />
                                Famille sous le minimum
                              </p>
                              <p className="text-gray-600">
                                Cette famille ne compte plus assez d'eleves actifs pour rester une famille nombreuse. Ajoutez un membre avant l'echeance ou elle sera archivee automatiquement sans radier les eleves.
                              </p>
                              <div className="p-2 bg-amber-50 rounded border border-amber-100 space-y-1">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Minimum attendu :</span>
                                  <span className="font-semibold text-amber-800">{selectedFamille.regularisation_min_enfants || 2}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Echeance :</span>
                                  <span className="font-semibold text-amber-800">{getRegularisationFamilleLabel(selectedFamille)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="absolute left-6 -top-2 w-3 h-3 bg-white border-l border-t border-amber-200 transform rotate-45"></div>
                          </div>
                        </div>
                      )}

                      {interSiteInfo?.is_intersite && (
                        <span className="text-[10px] text-gray-500 flex-shrink-0">
                          {interSiteInfo.nb_membres_total} membres · {interSiteInfo.nb_sites} sites
                        </span>
                      )}
                    </div>

                  </div>
                </div>

                {/* Droite : Boutons principaux + Menu Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">

                  {(interSiteInfo?.is_intersite || interSiteInfo?.was_intersite_reference || interSiteInfo?.was_intersite_distant) && (
                    <div className="relative">
                      <Button
                        size="sm"
                        onClick={() => setShowGuideInterSiteDetail(true)}
                        className="bg-[#246990] hover:bg-[#1f5a7a] text-white font-semibold flex items-center gap-1.5"
                      >
                        <HelpCircle size={14} />
                        <span className="hidden sm:inline">Infos</span>
                      </Button>
                      {/* Tooltip fixe */}
                      <div className="absolute right-0 top-full mt-2 w-64 bg-white text-gray-700 text-xs rounded-lg px-3 py-2.5 shadow-lg border border-gray-200 z-20">
                        <p className="font-semibold text-[#246990] mb-1">Cette famille est inter-site</p>
                        <p className="text-[10px] text-gray-500 leading-relaxed">Cliquez pour voir les spécificités de cette famille et comprendre votre rôle et les actions disponibles.</p>
                        <div className="absolute right-4 -top-1.5 w-3 h-3 bg-white border-l border-t border-gray-200 transform rotate-45"></div>
                      </div>
                    </div>
                  )}

                  {/* Bouton principal 1 : Livret de caisse */}
                  <Button
                    size="sm"
                    onClick={handleTelechargerLivret}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center gap-1.5"
                  >
                    <Download size={14} />
                    <span className="hidden sm:inline">Livret de caisse</span>
                    <span className="sm:hidden">Livret</span>
                  </Button>

                  {/* Bouton principal 2 : Ajouter un élève */}
                  <Button
                    size="sm"
                    onClick={() => setShowModalAjouterEleve(true)}
                    className="bg-[#319190] hover:bg-[#2a7d7c] text-white font-semibold flex items-center gap-1.5"
                  >
                    <Plus size={14} />
                    <span className="hidden sm:inline">Ajouter un élève</span>
                    <span className="sm:hidden">Ajouter</span>
                  </Button>

                  {/* Menu Actions (dropdown) */}
                  <div className="relative">
                    <Button
                      size="sm"
                      onClick={() => setShowActionsMenu(!showActionsMenu)}
                      className="bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 font-semibold flex items-center gap-1.5"
                    >
                      <MoreHorizontal size={16} />
                      <span className="hidden sm:inline">Actions</span>
                      <ChevronDown size={12} className={`transition-transform ${showActionsMenu ? 'rotate-180' : ''}`} />
                    </Button>

                    {/* Dropdown */}
                    {showActionsMenu && (
                      <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-30 py-1 overflow-hidden">

                        {/* Modifier les informations */}
                        <button
                          onClick={() => { setShowActionsMenu(false); setShowModalModif(true); }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                        >
                          <Edit size={15} className="text-[#246990]" />
                          <div>
                            <div className="font-medium">Modifier les informations</div>
                            <div className="text-[10px] text-gray-400">Nom, réductions, description</div>
                          </div>
                        </button>

                        {/* Mois non scolarisés */}
                        <button
                          onClick={() => { setShowActionsMenu(false); setShowModalMoisNonScolarises(true); }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                        >
                          <Calendar size={15} className="text-[#c66d59]" />
                          <div>
                            <div className="font-medium">Mois non scolarisés</div>
                            <div className="text-[10px] text-gray-400">Gérer les mois d'absence</div>
                          </div>
                        </button>

                        {/* Séparateur */}
                        <div className="h-px bg-gray-100 my-1"></div>




                        {/* Section Inter-Site du menu d'actions. Activer, desactiver,
                            reprendre la reference : autant de gestes qui n'existent
                            pas dans une ecole seule. */}
                        {interSitesOuvert && (
                        <>
                        {/* Section Inter-Site dans le dropdown */}
                        {!interSiteInfo?.is_intersite ? (
                          <>
                            {interSiteInfo?.was_intersite_reference ? (
                              // ÉTAIT site de référence → Bouton "Re-lier au Hub"
                              <button
                                onClick={() => { setShowActionsMenu(false); setShowConfirmRelierHub(true); }}
                                disabled={relierHubLoading}
                                className="w-full px-4 py-2.5 text-left text-sm text-teal-700 hover:bg-teal-50 flex items-center gap-3 transition-colors disabled:opacity-50"
                              >
                                <Network size={15} className="text-teal-600" />
                                <div>
                                  <div className="font-medium">Re-lier au Hub Inter-Site</div>
                                  <div className="text-[10px] text-teal-500">Cette famille était déjà inter-site - Réactiver la liaison</div>
                                </div>
                              </button>

                            ) : interSiteInfo?.was_intersite_distant ? (
                              // ÉTAIT site distant
                              interSiteInfo?.hub_is_active ? (
                                // Le Hub est redevenu actif → proposer de rattacher
                                <button
                                  onClick={async () => { setShowActionsMenu(false); await handleRattacherDepuisDistant(); }}
                                  className="w-full px-4 py-2.5 text-left text-sm text-green-700 hover:bg-green-50 flex items-center gap-3 transition-colors"
                                >
                                  <Network size={15} className="text-green-600" />
                                  <div>
                                    <div className="font-medium">Rattacher au Hub Inter-Site</div>
                                    <div className="text-[10px] text-green-500">Le site de référence a re-lié la famille - Rattacher vos élèves</div>
                                  </div>
                                </button>
                              ) : (
                                // Le Hub n'est pas encore actif → message d'attente
                                <div className="px-4 py-2.5 flex items-center gap-3">
                                  <Network size={15} className="text-gray-400" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-500">Ancienne famille inter-site</div>
                                    <div className="text-[10px] text-gray-400">En attente de re-liaison par le site de référence</div>
                                  </div>
                                </div>
                              )
                            ) : (
                              // JAMAIS été inter-site → Bouton "Activer Inter-Site"
                              <button
                                onClick={async () => {
                                  setShowActionsMenu(false);
                                  setApercuActivationIS(null);
                                  setShowConfirmInterSite(true);
                                  const detail = await api.familles.getDetailsComplets(selectedFamille.id, anneeScolaireId);
                                  const famille = detail?.data?.famille || detail?.famille || null;
                                  const eleves = detail?.data?.eleves || detail?.eleves || [];
                                  if (famille) {
                                    setApercuActivationIS({
                                      reduction_mensuel: Number(famille.reduction_mensuel || 0),
                                      reduction_inscription: Number(famille.reduction_inscription || 0),
                                      nb_suivent: eleves.filter((e) => (
                                        Number(e.utilise_reduction_famille) === 1
                                        && Number(e.reduction_custom_mensuel || 0) === 0
                                      )).length,
                                      nb_eleves: eleves.length
                                    });
                                  }
                                }}
                                disabled={activationLoading}
                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-3 transition-colors disabled:opacity-50"
                              >
                                <Network size={15} className="text-[#246990]" />
                                <div>
                                  <div className="font-medium">Activer Inter-Site</div>
                                  <div className="text-[10px] text-gray-400">Rendre visible aux autres sites</div>
                                </div>
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Désactiver (uniquement site de référence) */}
                            {interSiteInfo?.is_reference && (
                              <button
                                onClick={() => { setShowActionsMenu(false); setShowConfirmDesactiverIS(true); }}
                                className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                              >
                                <X size={15} className="text-red-500" />
                                <div>
                                  <div className="font-medium">Désactiver Inter-Site</div>
                                  <div className="text-[10px] text-red-400">Retirer du réseau inter-sites</div>
                                </div>
                              </button>
                            )}
                          </>
                        )}
                        </>
                        )}




                        {/* Séparateur */}
                        <div className="h-px bg-gray-100 my-1"></div>

                        {/* Archiver (zone danger) */}
                        <button
                          onClick={() => { setShowActionsMenu(false); setShowConfirmArchiver(true); }}
                          className="w-full px-4 py-2.5 text-left text-sm text-amber-700 hover:bg-amber-50 flex items-center gap-3 transition-colors"
                        >
                          <Archive size={15} className="text-amber-500" />
                          <div>
                            <div className="font-medium">Archiver la famille</div>
                            <div className="text-[10px] text-amber-400">Désactiver et conserver l'historique</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>






          {interSiteInfo?.is_intersite && (
            <div className="mb-6 rounded-lg border border-[#246990]/20 bg-[#f8fbfd] p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#246990]/10 text-[#246990] flex items-center justify-center flex-shrink-0">
                  <Network size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-[#246990]">Lecture inter-site de cette famille</h3>
                    <span className="rounded-full bg-white border border-[#246990]/20 px-2 py-0.5 text-[10px] font-semibold text-[#246990]">
                      {interSiteInfo.is_reference ? 'Site de reference' : `Site distant - reference ${interSiteInfo.site_reference}`}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                    <div className="rounded-lg bg-white border border-slate-200 p-3">
                      <p className="font-semibold text-slate-800">Paiements locaux</p>
                      <p className="mt-1 text-slate-600">
                        Chaque site encaisse ses propres eleves. Les membres distants sont affiches en lecture seule.
                      </p>
                    </div>
                    <div className="rounded-lg bg-white border border-teal-200 p-3">
                      <p className="font-semibold text-teal-800">Reduction de reference</p>
                      <p className="mt-1 text-teal-700">
                        Le site de reference pilote la reduction famille. Les reductions personnalisees locales la remplacent pour l'eleve concerne.
                      </p>
                    </div>
                    <div className="rounded-lg bg-white border border-amber-200 p-3">
                      <p className="font-semibold text-amber-800">Accords financiers</p>
                      <p className="mt-1 text-amber-700">
                        Un accord sur ce dossier concerne uniquement les eleves de ce site. Il ne recompose pas les sites distants.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sélecteur de période */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6 overflow-hidden">
            <div
              className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => setShowTracabilite(!showTracabilite)}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#246990]" />
                <h3 className="font-semibold text-gray-900 text-sm">Tracabilite de la famille</h3>
              </div>
              <ChevronDown className={`w-4 h-4 text-[#246990] transition-transform ${showTracabilite ? 'rotate-180' : ''}`} />
            </div>
            {showTracabilite && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 text-sm">
              <div className="rounded-lg bg-[#eef7fb] border border-[#246990]/10 p-3">
                <span className="text-xs uppercase tracking-wide text-[#246990] font-semibold">Creee le</span>
                <div className="font-semibold text-gray-900 mt-1">
                  {formatDateTime(selectedFamille?.date_creation || selectedFamille?.created_at)}
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                <span className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Creee par</span>
                <div className="font-semibold text-gray-900 mt-1">
                  {selectedFamille?.created_by_nom || 'Non renseigne'}
                </div>
              </div>
              <div className="rounded-lg bg-[#edf8f6] border border-[#319190]/20 p-3 md:col-span-2">
                <span className="text-xs uppercase tracking-wide text-[#319190] font-semibold">Motif</span>
                <div className="font-semibold text-gray-900 mt-1">
                  {selectedFamille?.motif_creation || selectedFamille?.description || 'Non renseigne'}
                </div>
              </div>
            </div>
            )}
            {showTracabilite && elevesFamille?.some((eleve) => eleve.origine_famille_avant_integration === 'eleve_seul') && (
              <div className="mx-4 mb-4 rounded-lg border border-[#246990]/15 bg-[#f8fbfd] px-3 py-2 text-xs text-gray-600">
                Cette famille contient des eleves qui etaient suivis comme eleves seuls avant leur integration. Leur historique comptable individuel reste consultable dans Paiements scolarite.
              </div>
            )}
          </div>

          {/* Paiement par categorie */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6 p-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {categoriesPaiementFamille.map((categorie) => {
                const IconCategorie = categorie.icon;
                const isActive = activeCategoriePaiement === categorie.id;

                return (
                  <button
                    key={categorie.id}
                    type="button"
                    onClick={() => {
                      if (activeCategoriePaiement !== categorie.id) {
                        setActiveCategoriePaiement(categorie.id);
                        resetPaiementForm();
                        if (categorie.id !== 'scolarite') {
                          setTypePaiement('mois_selectionnes');
                        }
                      }
                    }}
                    className={`text-left rounded-lg border px-4 py-3 transition-all ${isActive
                        ? 'border-[#246990] bg-[#eef7fb] text-[#246990] shadow-sm'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-[#246990]/40 hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg ${isActive ? 'bg-[#246990] text-white' : 'bg-gray-100 text-gray-500'}`}>
                        <IconCategorie className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">{categorie.label}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">{categorie.description}</span>
                        {!categorie.ready && (
                          <span className="mt-2 inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                            Preparation dediee
                          </span>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {activeCategoriePaiement === 'scolarite' ? (
            <>
          <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm mb-6">
            {/* 🆕 Titre avec tooltip */}
            <div className="mb-3 pb-3 border-b border-gray-200 relative group">
              <h3 className="font-medium text-gray-900 flex items-center">

                Visualisation des statistiques
                <AlertCircle className="w-4 h-4 ml-2 text-blue-500 cursor-help" />
              </h3>

              {/* Tooltip élégant */}
              <div className="absolute left-0 top-full mt-2 w-96 bg-white text-gray-800 text-xs rounded-lg shadow-xl p-3 hidden group-hover:block z-50 border border-blue-200">
                <div className="flex items-start gap-2">

                  <div>
                    <p className="font-semibold text-blue-700 mb-1">Sélecteur de consultation uniquement</p>
                    <p className="text-gray-600 mb-2">
                      Ce sélecteur permet de <span className="font-semibold">visualiser les statistiques</span> d'un trimestre ou d'un mois spécifique.
                    </p>
                    <p className="text-gray-600 mb-2">
                      A l'ouverture d'une famille, Klasys se place automatiquement sur le mois scolaire en cours et sur son trimestre. Vous pouvez ensuite changer la periode manuellement.
                    </p>
                    <p className="text-gray-600">
                      Pour effectuer un paiement, utilisez les <span className="font-semibold text-[#246990]">cases à cocher des mois</span> dans la section
                      <span className="font-semibold"> "Mois concernés par le paiement"</span> plus bas.
                      Vous pouvez sélectionner plusieurs mois à payer en une seule fois.
                    </p>
                  </div>
                </div>
                {/* Petite flèche */}
                <div className="absolute -top-2 left-8 w-4 h-4 bg-white border-l border-t border-blue-200 transform rotate-45"></div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Trimestre
                </label>

                <div className="bg-gray-100 rounded-lg p-1 flex items-center gap-1">

                  {[1, 2, 3].map((trimestre) => (
                    <button
                      key={trimestre}
                      onClick={() => {
                        if (selectedTrimestre !== trimestre.toString()) {
                          periodeSelectionManuelleRef.current = true;
                          setSelectedTrimestre(trimestre.toString());
                        }
                      }}
                      className={`flex-1 px-4 py-2 rounded-md font-semibold transition-all text-sm ${selectedTrimestre === trimestre.toString()
                          ? 'bg-white text-[#246990] shadow-md'
                          : 'text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                      Trimestre {trimestre}
                    </button>
                  ))}

                </div>
              </div>


              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mois <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />

                  {/* Champ de recherche du mois.
                      Liste ouverte : le champ affiche ce que l'utilisateur tape.
                      Liste fermée : il réaffiche le mois choisi. Le champ ne perd donc
                      jamais sa valeur, même si on ouvre la liste puis qu'on clique
                      ailleurs sans rien choisir.
                      À l'ouverture on repart d'une recherche vide, sinon le nom du mois
                      déjà sélectionné servirait de filtre et la liste n'afficherait que
                      lui. */}
                  <input
                    type="text"
                    placeholder="Rechercher un mois..."
                    value={isMoisDropdownOpen
                      ? (moisSearchTerm || '')
                      : (moisDisponibles.find(m => m.id.toString() === String(selectedMois))?.nom || '')}
                    onChange={(e) => {
                      periodeSelectionManuelleRef.current = true;
                      setMoisSearchTerm(e.target.value);
                      setIsMoisDropdownOpen(true);
                    }}
                    onFocus={() => {
                      setMoisSearchTerm('');
                      setIsMoisDropdownOpen(true);
                    }}
                    disabled={loading || moisDisponibles.length === 0}
                    className="w-full pl-10 pr-10 py-2.5 text-sm font-medium border-2 border-gray-200 rounded-lg 
                        bg-white hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10 
                        outline-none transition-all
                        disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  />

                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* La condition portait aussi `&& !selectedMois`. Comme un mois est
                      sélectionné automatiquement au chargement — le mois courant, ou le
                      premier du trimestre — elle était toujours fausse : la liste ne
                      pouvait jamais s'ouvrir. */}
                  {isMoisDropdownOpen && (
                    <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {(() => {
                        const filteredMois = moisDisponibles.filter(mois =>
                          mois.nom.toLowerCase().includes((moisSearchTerm || '').toLowerCase())
                        );

                        if (filteredMois.length === 0) {
                          return (
                            <div className="p-4 text-center text-gray-500 text-sm">
                              Aucun mois trouvé
                            </div>
                          );
                        }

                        return filteredMois.map((mois) => (
                          <div
                            key={mois.id}
                            onClick={() => {
                              periodeSelectionManuelleRef.current = true;
                              setSelectedMois(mois.id.toString());
                              setMoisSearchTerm('');
                              setIsMoisDropdownOpen(false);
                            }}
                            className="px-4 py-2.5 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                          >
                            <div className="font-medium text-gray-900 text-sm">
                              {mois.nom}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>


            </div>
          </div>


          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              {/* Stats financières en haut */}


              <div className="grid grid-cols-3 gap-4">
                {selectedMois && totaux ? (
                  <>
                    {/* Carte 1 : Total dû */}
                    <div className="bg-[#246990] text-white p-4 rounded-lg shadow-sm hover:bg-[#246990]/90 transition">
                      <div className="text-sm opacity-90 mb-1 flex items-center gap-1">
                        Total dû ({moisDisponibles.find(m => m.id.toString() === selectedMois)?.nom})

                        {/* ✅ Tooltip SEULEMENT si ce mois a des radiés non payés */}
                        {totaux?.total_mensuel?.a_des_radies_non_payes && (
                          <div className="relative group">
                            <AlertCircle className="w-3 h-3 cursor-help opacity-80" />

                            <div className="absolute left-0 top-full mt-2 w-56 bg-white border-2 border-blue-200 rounded-lg shadow-xl p-2 hidden group-hover:block z-50 text-gray-700">
                              <div className="text-[10px] text-left">
                                <p className="font-semibold text-blue-700 mb-1">
                                  ℹ️ Composition modifiée
                                </p>
                                <div className="space-y-1">
                                  <div className="flex justify-between text-gray-500">
                                    <span>Avant radiation :</span>
                                    <span className="line-through">{formatMontant(totaux.total_mensuel?.du)}</span>
                                  </div>
                                  <div className="flex justify-between font-semibold text-gray-800">
                                    <span>Élèves actifs :</span>
                                    <span>{formatMontant(totaux.total_mensuel?.du_actifs)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="absolute left-4 bottom-full w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-white"></div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ✅ Affichage SEULEMENT si ce mois a des radiés non payés */}
                      {totaux?.total_mensuel?.a_des_radies_non_payes ? (
                        <div className="space-y-1">
                          <div className="text-sm opacity-60 line-through">
                            {formatMontant(totaux.total_mensuel?.du)}
                          </div>
                          <div className="text-2xl font-bold">
                            {formatMontant(totaux.total_mensuel?.du_actifs)}
                          </div>
                        </div>
                      ) : (
                        <div className="text-2xl font-bold">
                          {formatMontant(totaux.total_mensuel?.du)}
                        </div>
                      )}
                    </div>

                    {/* Carte 2 : Déjà payé */}
                    <div className="bg-[#319190] text-white p-4 rounded-lg shadow-sm hover:bg-[#319190]/90 transition">
                      <div className="text-sm opacity-90 mb-1">
                        Déjà payé ({moisDisponibles.find(m => m.id.toString() === selectedMois)?.nom})
                      </div>
                      <div className="text-2xl font-bold">
                        {formatMontant(totaux.total_mensuel?.paye)}
                      </div>
                    </div>

                    {/* Carte 3 : Reste à payer */}
                    <div className="bg-[#c66d59] text-white p-4 rounded-lg shadow-sm hover:bg-[#ae7467]/90 transition">
                      <div className="text-sm opacity-90 mb-1 flex items-center gap-1">
                        Reste à payer ({moisDisponibles.find(m => m.id.toString() === selectedMois)?.nom})

                        {/* ✅ Tooltip SEULEMENT si ce mois a des radiés non payés */}
                        {totaux?.total_mensuel?.a_des_radies_non_payes && (
                          <div className="relative group">
                            <AlertCircle className="w-3 h-3 cursor-help opacity-80" />

                            <div className="absolute right-0 top-full mt-2 w-56 bg-white border-2 border-orange-200 rounded-lg shadow-xl p-2 hidden group-hover:block z-50 text-gray-700">
                              <div className="text-[10px] text-left">
                                <p className="font-semibold text-orange-700 mb-1">
                                  ℹ️ Montant élèves actifs
                                </p>
                                <div className="space-y-1">
                                  <div className="flex justify-between text-gray-500">
                                    <span>Avant radiation :</span>
                                    <span className="line-through">{formatMontant(totaux.total_mensuel?.reste)}</span>
                                  </div>
                                  <div className="flex justify-between font-semibold text-gray-800">
                                    <span>Reste actuel :</span>
                                    <span>{formatMontant(montantRestant(totaux.total_mensuel?.du_actifs, totaux.total_mensuel?.paye))}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="absolute right-4 bottom-full w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-white"></div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ✅ Affichage SEULEMENT si ce mois a des radiés non payés */}
                      {totaux?.total_mensuel?.a_des_radies_non_payes ? (
                        <div className="space-y-1">
                          <div className="text-sm opacity-60 line-through">
                            {formatMontant(totaux.total_mensuel?.reste)}
                          </div>
                          <div className="text-2xl font-bold">
                            {formatMontant(montantRestant(totaux.total_mensuel?.du_actifs, totaux.total_mensuel?.paye))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-2xl font-bold">
                          {formatMontant(totaux.total_mensuel?.reste)}
                        </div>
                      )}
                    </div>
                  </>
                ) : statsGlobales ? (
                  <>
                    {/* Total dû (Vue globale) */}
                    <div className="bg-[#246990] text-white p-4 rounded-lg shadow-sm hover:bg-[#246990]/90 transition">
                      <div className="text-sm opacity-90 mb-1">Total dû</div>
                      <div className="text-xl font-bold">
                        {formatMontant(statsGlobales.total_du)}
                      </div>
                    </div>

                    {/* Déjà payé (Vue globale) */}
                    <div className="bg-[#319190] text-white p-4 rounded-lg shadow-sm hover:bg-[#319190]/90 transition">
                      <div className="text-sm opacity-90 mb-1">Déjà payé</div>
                      <div className="text-xl font-bold">
                        {formatMontant(statsGlobales.total_paye)}
                      </div>
                    </div>

                    {/* Reste à payer (Vue globale) */}
                    <div className="bg-[#c66d59] text-white p-4 rounded-lg shadow-sm hover:bg-[#ae7467]/90 transition">
                      <div className="text-sm opacity-90 mb-1">Reste à payer</div>
                      <div className="text-xl font-bold">
                        {formatMontant(statsGlobales.reste_a_payer)}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="col-span-3 text-center text-gray-500">
                    Chargement des statistiques...
                  </div>
                )}
              </div>



              {/* 1️⃣ LISTE DES ÉLÈVES - Suivi des paiements */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div
                  className="p-4 bg-[#eef7fb] border-b border-[#246990]/20 flex items-center justify-between cursor-pointer hover:bg-[#e5f3fb] transition-colors"
                  onClick={() => setShowListeEleves(!showListeEleves)}
                >
                  <h2 className="font-medium text-[#246990] text-lg flex items-center">
                    <GraduationCap className="w-5 h-5 mr-2" />
                    Suivi des paiements par élève ({elevesFamille?.length || 0})
                    {selectedMois && (
                      <span className="ml-2 text-sm font-normal text-gray-600">
                        - {moisDisponibles.find(m => m.id.toString() === selectedMois)?.nom}
                      </span>
                    )}
                  </h2>
                  <ChevronDown
                    className={`w-5 h-5 text-[#246990] transition-transform ${showListeEleves ? 'rotate-180' : ''
                      }`}
                  />
                </div>



                {showListeEleves && (
                  <div className="p-4">
                    {!selectedMois ? (
                      <div className="text-center py-8">
                        <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-gray-500">
                          Sélectionnez un mois pour voir le suivi des paiements
                        </p>
                      </div>
                    ) : elevesMoisSelectionne && elevesMoisSelectionne.length > 0 ? (
                      <div className="space-y-3">


                        {elevesMoisSelectionne.map((eleve) => {
                          const pourcentage = eleve.montant_attendu > 0
                            ? Math.min(100, ((eleve.montant_paye / eleve.montant_attendu) * 100) || 0)
                            : 0;

                          const estNonScolarise = eleve.statut_frequentation === 'non_scolarise';
                          const estRadie = eleve.statut_eleve === 'radie';
                          const demandeRetraitEnCours = Boolean(eleve.a_demande_retrait_en_cours || eleve.demande_retrait_en_cours);

                          const dateIntegration = eleve.date_integration_famille ? new Date(eleve.date_integration_famille) : null;
                          const dateIntegrationValide = dateIntegration && !Number.isNaN(dateIntegration.getTime())
                            ? dateIntegration
                            : null;
                          const maintenant = new Date();
                          const joursDepuisIntegration = dateIntegrationValide
                            ? Math.floor((maintenant - dateIntegrationValide) / (1000 * 60 * 60 * 24))
                            : null;
                          const joursIntegrationAffiches = joursDepuisIntegration !== null
                            ? Math.max(0, joursDepuisIntegration)
                            : null;
                          const estRecent = joursIntegrationAffiches !== null && joursIntegrationAffiches <= 7;
                          const moisCourant = moisDisponibles.find(m => m.id.toString() === selectedMois);
                          const dateIntegrationLabel = dateIntegrationValide
                            ? dateIntegrationValide.toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })
                            : null;
                          const dureeIntegrationLabel = joursIntegrationAffiches === null
                            ? null
                            : joursIntegrationAffiches === 0
                              ? "aujourd'hui"
                              : `${joursIntegrationAffiches} jour${joursIntegrationAffiches > 1 ? 's' : ''}`;
                          // Le serveur qualifie lui-même la ligne. Une version précédente
                          // comparait ici le début du mois à la date d'intégration : le
                          // critère était faux, puisqu'un élève peut régler un mois d'avance
                          // avant de rejoindre la famille, et la mention ne s'affichait alors
                          // jamais. La frontière est le mois d'effet, que seul le serveur
                          // connaît - il l'a figée au moment de l'ajout.
                          const afficherMontantHistorique = eleve.contexte_famille === 'individuel_avant_integration';
                          const origineEleveSeul = eleve.origine_famille_avant_integration === 'eleve_seul';
                          const utiliseReductionFamille = eleve.utilise_reduction_famille === true || eleve.utilise_reduction_famille === 1 || eleve.utilise_reduction_famille === '1';

                          return (
                            <div
                              key={eleve.eleve_id}
                              className={`p-4 rounded-lg border transition-all ${estRadie
                                  ? 'bg-red-50 border-red-300'  // ✅ FOND ROUGE SI RADIÉ
                                  : estNonScolarise
                                    ? 'bg-gray-100 border-gray-300'
                                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300'
                                }`}
                            >
                              <div className="grid grid-cols-1 gap-4 mb-3 lg:grid-cols-[minmax(0,1fr)_260px]">
                                <div className="min-w-0 space-y-3">
                                  <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(160px,1fr)_112px_minmax(300px,auto)] md:items-center">
                                    <div className="min-w-0">
                                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                                        <span className={`font-semibold text-base truncate ${estRadie ? 'text-red-900 line-through' : 'text-gray-900'}`}>
                                          {eleve.prenom} {eleve.nom}
                                        </span>
                                        {estRadie && (
                                          <span className="inline-flex items-center rounded border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                                            <UserMinus className="mr-1 h-3 w-3" />
                                            Radié
                                          </span>
                                        )}
                                        {!estRadie && estNonScolarise && (
                                          <span className="inline-flex items-center rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
                                            <Calendar className="mr-1 h-3 w-3" />
                                            Non scolarisé
                                          </span>
                                        )}
                                      </div>
                                      <div className="mt-1 text-xs text-gray-500">
                                        Mois : {moisCourant?.nom || '-'}
                                      </div>
                                    </div>

                                    <div className="flex md:justify-center">
                                      <span className={BADGE_CLASSE_CLASSES}>
                                        {eleve.classe_nom || '-'}
                                      </span>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-2 md:justify-end">
                                      {!estRadie && demandeRetraitEnCours && (
                                        <button
                                          type="button"
                                          disabled
                                          className="inline-flex min-w-[136px] items-center justify-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-medium rounded cursor-not-allowed"
                                          title={eleve.demande_retrait_en_cours?.id ? `Demande #${eleve.demande_retrait_en_cours.id} en attente` : 'Demande de retrait en attente'}
                                        >
                                          <AlertCircle className="w-3 h-3" />
                                          Demande en cours
                                        </button>
                                      )}

                                      {!estRadie && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEleveReductionAModifier({
                                              id: eleve.eleve_id,
                                              prenom: eleve.prenom,
                                              nom: eleve.nom,
                                              matricule: eleve.matricule
                                            });
                                          }}
                                          className="inline-flex min-w-[140px] items-center justify-center gap-1 px-2.5 py-1 bg-[#eef7fb] text-[#246990] border border-[#246990]/20 text-xs font-medium rounded hover:bg-[#e0f0f8] transition-colors"
                                        >
                                          <Calculator className="w-3 h-3" />
                                          Modifier la réduction
                                        </button>
                                      )}

                                      {!estRadie && !demandeRetraitEnCours && (
                                        <div className="relative group">
                                          <button
                                            disabled={retraitLoading}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (retraitLoading) return;
                                              setEleveARetirer({
                                                id: eleve.eleve_id,
                                                nom: eleve.nom,
                                                prenom: eleve.prenom
                                              });
                                              setShowConfirmDialog(true);
                                            }}
                                            className="inline-flex min-w-[110px] items-center justify-center gap-1 px-2.5 py-1 bg-red-50 text-red-600 border border-red-100 text-xs font-medium rounded hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            <X className="w-3 h-3" />
                                            Retirer
                                            <HelpCircle className="w-3 h-3 text-red-400" />
                                          </button>

                                          <div className="absolute left-1/2 bottom-full mb-2 w-80 -translate-x-1/2 bg-white border-2 border-red-200 rounded-lg shadow-xl p-3 hidden group-hover:block z-50">
                                            <div className="text-xs text-left">
                                              <p className="font-semibold text-red-700 mb-2 flex items-center gap-1">
                                                <AlertCircle className="w-4 h-4" />
                                                Différence importante
                                              </p>
                                              <div className="space-y-3">
                                                <div>
                                                  <p className="font-semibold text-blue-700 mb-1 flex items-center gap-1">
                                                    <span className="w-2 h-2 bg-blue-700 rounded-full"></span>
                                                    Retirer de la famille :
                                                  </p>
                                                  <p className="text-gray-700 ml-3">
                                                    Ce bouton retire l'élève de la famille nombreuse. <span className="font-semibold">L'élève reste inscrit dans l'école</span> et redevient un élève individuel.
                                                  </p>
                                                </div>
                                                <div>
                                                  <p className="font-semibold text-blue-700 mb-1 flex items-center gap-1">
                                                    <span className="w-2 h-2 bg-blue-700 rounded-full"></span>
                                                    Radier de l'école :
                                                  </p>
                                                  <p className="text-gray-700 ml-3">
                                                    Pour supprimer définitivement un élève de l'établissement, allez dans <span className="font-semibold">Élèves → Liste des élèves</span> puis utilisez l'option <span className="text-red-600 font-semibold">Radier</span>.
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="absolute left-1/2 top-full -translate-x-1/2 w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-red-200"></div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {(origineEleveSeul || eleve.integre_famille_par_nom || dateIntegrationLabel) && (
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                                      <div className="relative inline-flex group">
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-1.5 rounded border border-[#cfe3ec] bg-[#eaf3f7] px-2.5 py-2 text-[#246990]"
                                        >
                                          <User2 className="h-3.5 w-3.5 flex-shrink-0" />
                                          <span className="font-medium">
                                            Origine : {origineEleveSeul ? 'élève seul' : 'famille'}
                                          </span>
                                          <HelpCircle className="h-3.5 w-3.5 text-[#246990]/70" />
                                        </button>

                                        <div className="absolute left-0 top-full z-50 mt-2 hidden w-72 rounded-lg border border-[#cfe3ec] bg-white p-3 text-left text-xs text-gray-700 shadow-xl group-hover:block">
                                          <div className="mb-2 font-semibold text-[#246990]">Traçabilité</div>
                                          <div className="space-y-1.5">
                                            <div className="flex justify-between gap-3">
                                              <span className="text-gray-500">Origine</span>
                                              <span className="font-medium text-gray-900">
                                                {origineEleveSeul ? 'Élève seul' : 'Famille nombreuse'}
                                              </span>
                                            </div>
                                            {eleve.integre_famille_par_nom && (
                                              <div className="flex justify-between gap-3">
                                                <span className="text-gray-500">Intégré par</span>
                                                <span className="font-medium text-gray-900">{eleve.integre_famille_par_nom}</span>
                                              </div>
                                            )}
                                            {dateIntegrationLabel && (
                                              <div className="flex justify-between gap-3">
                                                <span className="text-gray-500">Depuis</span>
                                                <span className="font-medium text-gray-900">{dateIntegrationLabel}</span>
                                              </div>
                                            )}
                                            {!estRadie && dureeIntegrationLabel && (
                                              <div className="flex justify-between gap-3">
                                                <span className="text-gray-500">Durée</span>
                                                <span className="font-medium text-blue-700">{dureeIntegrationLabel}</span>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Un mois dont le paiement a commencé n'est jamais réaligné sur le
                                      tarif de la famille : sans cette mention, rien n'explique qu'un
                                      élève intégré en cours d'année affiche un montant plus faible
                                      sur ses mois déjà réglés que sur les suivants. */}
                                  {afficherMontantHistorique && (
                                    <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                      <History className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                                      <div className="min-w-0 text-[11px] leading-snug text-gray-600">
                                        <span className="font-semibold text-gray-800">Montant avant intégration</span>
                                        {' — '}réglé en gestion individuelle
                                        {dateIntegrationLabel ? `, avant son entrée dans la famille le ${dateIntegrationLabel}` : ''}.
                                        {' '}Ce mois n'entre pas dans les totaux de la famille.
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {!estNonScolarise ? (
                                  <div className="rounded border border-gray-200 bg-white p-3">
                                    <div className="flex items-center justify-between gap-3">
                                      <span className="text-xs text-gray-500">Payé</span>
                                      <span className={`text-base font-bold ${estRadie ? 'text-red-600' : 'text-[#319190]'}`}>
                                        {formatMontant(eleve.montant_paye)}
                                      </span>
                                    </div>
                                    <div className="mt-1 flex items-center justify-between gap-3">
                                      <span className="text-xs text-gray-500">Attendu</span>
                                      <span className={`text-sm font-medium ${estRadie ? 'text-red-700' : 'text-gray-700'}`}>
                                        {formatMontant(eleve.montant_attendu)}
                                      </span>
                                    </div>
                                    <div className="my-2 border-t border-gray-100" />
                                    <div className="flex items-center justify-between gap-3">
                                      {eleve.statut === 'Payé' ? (
                                        <>
                                          <span className="text-xs text-gray-500">Statut</span>
                                          <span className={`flex items-center text-xs font-semibold px-2 py-1 rounded ${estRadie ? 'text-red-700 bg-red-100' : 'text-[#319190] bg-[#e6f4f3]'}`}>
                                            <Check className="w-3 h-3 mr-1" />
                                            Payé {estRadie && '(avant radiation)'}
                                          </span>
                                        </>
                                      ) : eleve.statut === 'Payé au prorata' ? (
                                        <>
                                          <span className="text-xs text-gray-500">Statut</span>
                                          <span className="flex items-center text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded">
                                            <Calculator className="w-3 h-3 mr-1" />
                                            Payé au prorata
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className="text-xs text-gray-500">Reste</span>
                                          <span className={`text-base font-bold ${estRadie ? 'text-red-700' : 'text-[#ae7467]'}`}>
                                            {formatMontant(montantRestant(eleve.montant_attendu, eleve.montant_paye))}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="rounded border border-gray-200 bg-white p-3">
                                    <span className="text-sm text-gray-600 inline-flex items-center">
                                      <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                                      Non scolarisé ce mois
                                    </span>
                                    <div className="mt-1 text-xs text-gray-500">
                                      Exclu des statistiques
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* ✅ MESSAGE EXPLICATIF SI RADIÉ */}
                              {estRadie && (
                                <div className="mb-3 p-3 bg-red-100 border border-red-300 rounded text-xs text-red-800 flex items-start">
                                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-red-600" />
                                  <div>
                                    <p className="font-semibold mb-1">Élève radié de l'établissement</p>
                                    <p>
                                      Les montants affichés correspondent aux frais <strong>au moment du paiement</strong>.
                                      Cet élève ne fait plus partie des élèves actifs et n'est pas comptabilisé dans les statistiques actuelles.
                                    </p>
                                  </div>
                                </div>
                              )}

                              {/* MESSAGE NON SCOLARISÉ */}
                              {!estRadie && estNonScolarise && (
                                <div className="mb-3 p-2 bg-gray-200 border border-gray-300 rounded text-xs text-gray-700 flex items-start">
                                  <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                                  <span>
                                    Cet élève n'est pas scolarisé ce mois. Le montant dû pour ce mois est de <strong>0 FCFA</strong> et
                                    n'est pas comptabilisé dans les statistiques d'impayés.
                                  </span>
                                </div>
                              )}

                              {/* MESSAGE NOUVEAU */}
                              {!estRadie && !estNonScolarise && estRecent && utiliseReductionFamille && (
                                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-start">
                                  <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                                  <span>
                                    Cet élève bénéficie maintenant des réductions famille nombreuse ({formatMontant(selectedFamille?.reduction_mensuel || 0)}/mois).
                                    Les mois déjà payés avant son intégration ne sont pas modifiés.
                                  </span>
                                </div>
                              )}

                              {/* BARRE DE PROGRESSION (seulement si actif et scolarisé) */}
                              {!estRadie && !estNonScolarise && (
                                <div className="mt-3">
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-gray-600 font-medium">
                                      Progression de paiement (ce mois)
                                    </span>
                                    <span className="text-xs font-bold text-blue-600">
                                      {pourcentage.toFixed(0)}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                      className={`h-2.5 rounded-full transition-all ${pourcentage === 100 ? 'bg-[#319190]' :
                                          pourcentage > 50 ? 'bg-[#246990]' : 'bg-[#ae7467]'
                                        }`}
                                      style={{ width: `${pourcentage}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        Aucune donnée disponible pour ce mois
                      </div>
                    )}
                  </div>
                )}


                {/* ============ MEMBRES INTER-SITES (LECTURE SEULE) ============ */}
                {interSiteInfo?.is_intersite && vueConsolidee?.membres_distants?.length > 0 && (
                  <div className="bg-white rounded-lg border border-teal-200 shadow-sm">
                    <div className="p-4 bg-teal-50 border-b border-teal-200 flex items-center justify-between">
                      <h2 className="font-medium text-teal-700 text-lg flex items-center gap-2">
                        <Network className="w-5 h-5" />
                        Membres inter-sites ({vueConsolidee.membres_distants.length})
                        <span className="text-xs font-normal text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">
                          Lecture seule
                        </span>
                      </h2>

                      {/* Stats consolidées */}
                      {vueConsolidee.stats_consolidees && (
                        <div className="flex items-center gap-4 text-xs">
                          <span className="text-teal-700">
                            Total famille : <strong>{formatMontant(vueConsolidee.stats_consolidees.total_du)}</strong>
                          </span>
                          <span className="text-green-700">
                            Payé : <strong>{formatMontant(vueConsolidee.stats_consolidees.total_paye)}</strong>
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-4 space-y-3">
                      {vueConsolidee.membres_distants.map((membre) => (
                        <div
                          key={`${membre.site_subdomain}-${membre.eleve_id}`}
                          className="p-4 rounded-lg border border-teal-100 bg-teal-50/30"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Badge site */}
                              <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold">
                                {membre.site_subdomain.substring(0, 2).toUpperCase()}
                              </div>

                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-gray-900">
                                    {membre.eleve_prenom} {membre.eleve_nom}
                                  </span>
                                  <span className={BADGE_CLASSE_CLASSES}>
                                    {membre.classe_nom || '-'}
                                  </span>
                                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-bold flex items-center gap-1">
                                    <Globe size={10} />
                                    {membre.site_subdomain}
                                  </span>
                                  <span className="px-2 py-0.5 bg-white text-[#246990] border border-[#246990]/20 rounded text-xs font-semibold">
                                    Paiement local
                                  </span>
                                  {membre.annee_scolaire_nom && (
                                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">
                                      {membre.annee_scolaire_nom}
                                    </span>
                                  )}
                                </div>
                                {membre.eleve_matricule && (
                                  <span className="text-xs text-gray-500">Matricule : {membre.eleve_matricule}</span>
                                )}
                              </div>
                            </div>

                            {/* Paiements du membre distant */}
                            <div className="text-right">
                              {membre.erreur ? (
                                <span className="text-xs text-orange-600 italic">{membre.erreur}</span>
                              ) : (
                                <>
                                  <div className="flex items-center justify-end gap-2 mb-1">
                                    <span className="text-xs text-gray-500">Payé :</span>
                                    <span className="text-sm font-bold text-teal-700">
                                      {formatMontant(membre.total_paye)}
                                    </span>
                                    <span className="text-xs text-gray-400">/</span>
                                    <span className="text-sm text-gray-600">
                                      {formatMontant(membre.total_du)}
                                    </span>
                                  </div>
                                  {membre.reste_a_payer > 0 && (
                                    <div className="text-xs text-gray-500">
                                      Reste : <span className="font-semibold text-orange-600">{formatMontant(membre.reste_a_payer)}</span>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Note explicative */}
                      <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-teal-700">
                          Ces élèves sont gérés sur leur site respectif. Les paiements sont effectués localement sur chaque site.
                          Cette vue consolide l'état de la famille, mais ne permet pas encore un encaissement unique inter-site.
                        </span>
                      </div>
                    </div>
                  </div>
                )}



              </div>


              {/* 2️⃣ TABLEAU DES RÉDUCTIONS */}
              {accordFinancierActif && (
                <div className="bg-white rounded-lg border border-[#f0c36b] shadow-sm overflow-visible">
                  <div className="p-4 bg-[#fff8e8] border-b border-[#f0c36b]/70 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#fff1c7] text-[#9b5c00] flex items-center justify-center">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="font-semibold text-[#9b5c00]">
                          {accordEstRecompositionRetrait ? 'Accord recompose apres retrait' : 'Accord financier actif'}
                        </h2>
                        <p className="text-xs text-[#7c5b1a] max-w-3xl">
                          {accordEstRecompositionRetrait
                            ? 'La famille conserve uniquement les parts des membres restants. Les mois deja payes ou partiels restent dans leur historique.'
                            : 'Couche financiere separee : les reductions restent conservees, seuls les mois non payes a partir du mois d\'effet sont recalcules.'}
                        </p>

                        {/* Pourquoi cette carte est là. Sans cette phrase, on se demande
                            légitimement pourquoi une famille l'affiche et une autre non. */}
                        <p className="mt-1.5 text-[11px] leading-snug text-[#8a6a2a] max-w-3xl">
                          Cette carte n'apparaît que lorsqu'un <span className="font-semibold">montant mensuel global</span> a été
                          négocié pour la famille, différent de la somme des montants individuels après réductions.
                          Sans négociation, il n'y a pas d'accord — et pas de carte : le montant de la famille est
                          simplement l'addition de ceux de ses membres.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="px-3 py-1 rounded-full bg-white border border-[#f0c36b] text-[#9b5c00] text-xs font-semibold">
                        Accord #{accordFinancierActif.id}
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowAccordFinancier(!showAccordFinancier)}
                        className="h-8 w-8 rounded-lg hover:bg-[#fff1c7] text-[#9b5c00] flex items-center justify-center transition-colors"
                        title={showAccordFinancier ? "Replier l'accord" : "Deplier l'accord"}
                      >
                        <ChevronDown className={`w-5 h-5 transition-transform ${showAccordFinancier ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  </div>
                  {showAccordFinancier && (
                  <>

                  <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1">
                        {accordEstRecompositionRetrait ? 'Base membres restants' : 'Avant accord'}
                        <InfoTooltip className="ml-1" position="bottom">
                          {accordEstRecompositionRetrait
                            ? 'Somme des montants des membres qui restent dans la famille, avant conservation de leurs parts accordees.'
                            : 'Montant mensuel total obtenu apres les reductions individuelles/famille deja existantes. Cette base reste l\'historique des reductions.'}
                        </InfoTooltip>
                      </div>
                      <div className="text-xl font-bold text-gray-900">
                        {formatMontant(accordFinancierActif.montant_normal_avant_accord)}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {accordEstRecompositionRetrait ? 'Apres retrait, avant accord conserve' : 'Base apres reductions individuelles'}
                      </div>
                    </div>

                    <div className="rounded-lg border border-[#246990]/30 bg-[#eef7fb] p-3">
                      <div className="text-[11px] uppercase tracking-wide text-[#246990] font-semibold mb-1">
                        {accordEstRecompositionRetrait ? 'Montant conserve' : 'Montant valide'}
                        <InfoTooltip className="ml-1" position="bottom">
                          {accordEstRecompositionRetrait
                            ? 'Somme des parts accordees conservees pour les membres restants. Ce n\'est pas un nouvel accord arbitraire.'
                            : 'Montant accepte par validation. Il devient la nouvelle base de paiement uniquement pour les mois recalcules.'}
                        </InfoTooltip>
                      </div>
                      <div className="text-xl font-bold text-[#246990]">
                        {formatMontant(accordFinancierActif.montant_accorde)}
                      </div>
                      <div className="text-xs text-[#246990]/80 mt-1">
                        {accordEstRecompositionRetrait ? 'Ajustement conserve : - ' : 'Ajustement : - '}
                        {formatMontant(Math.abs(accordFinancierActif.ajustement_mensuel || 0))}
                      </div>
                    </div>

                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <div className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold mb-1">
                        Mois d'effet
                        <InfoTooltip className="ml-1" position="bottom">
                          Les mois deja payes ou partiellement payes avant ce point ne sont pas modifies. Ils restent sur l'ancien montant.
                        </InfoTooltip>
                      </div>
                      <div className="text-xl font-bold text-amber-800">
                        {accordFinancierActif.mois_effet_nom || '-'}
                      </div>
                      <div className="text-xs text-amber-700 mt-1">
                        {accordFinancierActif.nombre_mois_impactes || 0} ligne(s) recalculée(s), {accordFinancierActif.nombre_mois_ignores || 0} conservée(s)
                      </div>
                    </div>

                    <div className="rounded-lg border border-[#319190]/30 bg-[#effaf8] p-3 text-xs text-gray-700 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Demande</span>
                        <span className="font-semibold text-gray-900 text-right">
                          {accordFinancierActif.validation?.demandeur_nom || accordFinancierActif.demandeur_nom || 'Non renseigne'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Demandee le</span>
                        <span className="font-medium text-gray-800 text-right">
                          {formatDateTime(accordFinancierActif.validation?.date_demande)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Approuvee par</span>
                        <span className="font-semibold text-gray-900 text-right">
                          {accordFinancierActif.validation?.validateur_nom || accordFinancierActif.validateur_nom || 'Non renseigne'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Approuvee le</span>
                        <span className="font-medium text-gray-800 text-right">
                          {formatDateTime(accordFinancierActif.validation?.date_decision || accordFinancierActif.date_debut)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {accordFinancierActif.motif && (
                    <div className="mx-4 mb-4 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
                      <span className="font-semibold">Motif :</span> {accordFinancierActif.motif}
                    </div>
                  )}

                  {accordEstRecompositionRetrait && recompositionAccord && (
                    <div className="mx-4 mb-4 rounded-lg border border-amber-200 bg-white p-3 text-xs text-gray-700">
                      <div className="font-semibold text-[#9b5c00] mb-2">Lecture du retrait</div>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                        <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
                          <div className="text-[10px] uppercase text-gray-500 font-semibold">Avant retrait</div>
                          <div className="font-bold text-gray-900">{formatMontant(recompositionAccord.ancien_total_famille)}</div>
                        </div>
                        <div className="rounded-md border border-red-100 bg-red-50 p-2">
                          <div className="text-[10px] uppercase text-red-600 font-semibold">Part retiree</div>
                          <div className="font-bold text-red-700">- {formatMontant(recompositionAccord.part_retiree)}</div>
                          <div className="text-[10px] text-red-600 mt-0.5">{recompositionAccord.eleve_retire || 'Eleve sorti'}</div>
                        </div>
                        <div className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-2">
                          <div className="text-[10px] uppercase text-[#246990] font-semibold">Apres retrait</div>
                          <div className="font-bold text-[#246990]">{formatMontant(recompositionAccord.nouveau_total_famille)}</div>
                        </div>
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-2">
                          <div className="text-[10px] uppercase text-amber-700 font-semibold">Effet</div>
                          <div className="font-bold text-amber-800">{recompositionAccord.mois_effet?.nom || accordFinancierActif.mois_effet_nom || '-'}</div>
                        </div>
                      </div>
                      <div className="mt-2 rounded-md border border-[#246990]/10 bg-[#f4fbfd] p-2 text-[#174b67]">
                        Calcul applique : {formatMontant(recompositionAccord.ancien_total_famille)} - {formatMontant(recompositionAccord.part_retiree)} = {formatMontant(recompositionAccord.nouveau_total_famille)}.
                        Les mois deja payes ou partiels gardent leur ancien montant. Les mois non payes a partir du mois d'effet utilisent le montant recomposé.
                      </div>
                    </div>
                  )}

                  <div className="mx-4 mb-4 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-xs text-[#174b67]">
                    <div className="font-semibold mb-1">Lecture de l'accord</div>
                    <p>
                      {accordEstRecompositionRetrait
                        ? `Les reductions affichees restent les reductions officielles des membres restants. La recomposition conserve leurs parts d'accord pour atteindre ${formatMontant(accordFinancierActif.montant_accorde)} sur les mois recalcules.`
                        : `Les reductions affichees dans le tableau restent les reductions officielles des eleves. L'accord ajoute ensuite une repartition validee pour atteindre ${formatMontant(accordFinancierActif.montant_accorde)} sur les mois recalcules.`}
                      {' '}Les lignes conservees ne sont pas touchees.
                    </p>
                  </div>

                  {(accordFinancierActif.mois_impactes?.length > 0 || accordFinancierActif.mois_ignores?.length > 0) && (
                    <div className="mx-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                      <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                        <div className="font-semibold text-green-800 mb-2 flex items-center gap-1">
                          Mois recalcules
                          <InfoTooltip position="right" width="w-80">
                            Ces mois n'avaient aucun paiement au moment de l'accord. Le montant attendu a ete remplace par la part accordee de chaque eleve.
                          </InfoTooltip>
                        </div>
                        {moisImpactesParEleve.length === 0 ? (
                          /* Un cadre vide laisse croire à une information manquante.
                             Zéro mois recalculé est pourtant une réponse : les montants
                             en place étaient déjà ceux que l'accord prévoit. */
                          <div className="rounded-md border border-dashed border-green-300 bg-white/70 p-3 text-green-800">
                            Aucun mois n'a eu besoin d'être recalculé : les montants déjà en place
                            correspondaient à ceux de cet accord.
                          </div>
                        ) : (
                        <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                          {moisImpactesParEleve.map((groupe) => (
                            <div key={`impacte-${groupe.eleve_id || groupe.eleve}`} className="rounded-md bg-white/70 border border-green-100 p-2">
                              <div className="flex items-center justify-between gap-2 text-green-900">
                                <span className="font-semibold">{groupe.eleve}</span>
                                <span className="font-semibold">{formatMontant(groupe.nouveau_montant)}</span>
                              </div>
                              <div className="mt-1 flex flex-wrap gap-1">
                                {groupe.mois.map((mois, index) => (
                                  <span key={`${groupe.eleve_id}-${mois.id}-${index}`} className="rounded bg-green-100 px-2 py-0.5 text-[10px] text-green-800">
                                    {mois.nom}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <div className="font-semibold text-gray-800 mb-2 flex items-center gap-1">
                          Mois conserves
                          <InfoTooltip position="left" width="w-80">
                            Ces mois ont deja ete payes ou partiellement payes. Ils gardent leur ancien montant pour eviter tout ecart comptable.
                          </InfoTooltip>
                        </div>
                        {moisIgnoresParEleve.length === 0 ? (
                          <div className="rounded-md border border-dashed border-gray-300 bg-white/70 p-3 text-gray-500">
                            Aucun mois paye ou partiel n'a ete conserve sur cet accord.
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                            {moisIgnoresParEleve.map((groupe) => (
                              <div key={`ignore-${groupe.eleve_id || groupe.eleve}`} className="rounded-md bg-white border border-gray-200 p-2">
                                <div className="font-semibold text-gray-800">{groupe.eleve}</div>
                                <div className="mt-1 space-y-1">
                                  {groupe.mois.map((mois, index) => (
                                    <div key={`${groupe.eleve_id}-${mois.id}-${index}`} className="flex justify-between gap-2 text-gray-700">
                                      <span>{mois.nom}</span>
                                      <span className="font-semibold">{mois.raison || groupe.raison}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  </>
                  )}
                </div>
              )}

              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div
                  className="p-4 bg-[#eef7fb] border-b border-[#246990]/20 flex items-center justify-between cursor-pointer hover:bg-[#e5f3fb] transition-colors"
                  onClick={() => setShowTableauReductions(!showTableauReductions)}
                >
                  <h2 className="font-medium text-[#246990] text-lg flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Détail des frais et réductions par élève
                  </h2>
                  <ChevronDown
                    className={`w-5 h-5 text-[#246990] transition-transform ${showTableauReductions ? 'rotate-180' : ''
                      }`}
                  />
                </div>

                {showTableauReductions && (
                  <>
                    {/* Taux réellement accordé à la famille. Placé juste au-dessus du
                        tableau : les trois montants qui le composent sont dans la
                        ligne de total, en bas. Rien à reconstituer.
                        La valeur stockée sur la famille n'apparaît que si un membre
                        la suit — sinon elle ne veut rien dire, et 56 % des familles
                        en portent une que personne n'utilise. */}
                    {statsGlobales?.total_mensuel_normal > 0 && (
                      <div className="mx-4 mt-4 rounded-lg border border-[#cfe3ec] bg-[#f4f9fc] p-3">
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                          <span className="text-xs font-semibold text-[#246990]">Réduction de la famille</span>
                          <span className="text-2xl font-bold text-[#246990]">
                            {statsGlobales.taux_reduction_mensuel} %
                          </span>
                          <span className="text-[11px] text-gray-600">
                            par mois : {formatMontant(statsGlobales.total_mensuel_normal)} de tarif officiel,
                            {' '}{formatMontant(statsGlobales.total_reduction_mensuel)} de réductions,
                            {' '}<span className="font-semibold text-gray-800">
                              {formatMontant(statsGlobales.total_mensuel_avec_reduction)}
                              {ajustementAccordMensuel > 0 ? ' après réductions' : ' à payer'}
                            </span>
                          </span>
                        </div>

                        {/* Le taux ci-dessus ne mesure que les réductions portées par
                            les élèves. Présenter son résultat comme le montant dû est
                            faux dès qu'un accord existe : la famille paie moins encore.
                            Les deux couches restent séparées — un accord n'est pas une
                            réduction — mais le montant réellement dû doit apparaître. */}
                        {ajustementAccordMensuel > 0 && (
                          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-[#cfe3ec] pt-1.5">
                            <span className="text-xs font-semibold text-[#9b5c00]">Accord de la famille</span>
                            <span className="text-[11px] text-gray-600">
                              - {formatMontant(ajustementAccordMensuel)} sur le montant après réductions,
                              {' '}<span className="font-semibold text-gray-800">
                                {formatMontant(Math.max(0, Number(statsGlobales.total_mensuel_avec_reduction || 0) - ajustementAccordMensuel))} à payer
                              </span>
                            </span>
                            <span className="text-[11px] text-gray-500">
                              soit {Number(statsGlobales.total_mensuel_normal) > 0
                                ? (((Number(statsGlobales.total_reduction_mensuel || 0) + ajustementAccordMensuel)
                                  / Number(statsGlobales.total_mensuel_normal)) * 100).toFixed(1)
                                : '0.0'} % de baisse totale
                            </span>
                          </div>
                        )}

                        {statsGlobales.total_frais_inscription_normal > 0 && (
                          <div className="mt-1 text-[11px] text-gray-600">
                            Frais de début d'année : <span className="font-semibold text-gray-800">{statsGlobales.taux_reduction_inscription} %</span>
                            {' : '}{formatMontant(statsGlobales.total_frais_inscription_normal)} ·
                            {' '}{formatMontant(statsGlobales.total_reduction_inscription)} ·
                            {' '}{formatMontant(statsGlobales.total_frais_inscription_avec_reduction)} à payer
                          </div>
                        )}

                        {statsGlobales.nb_membres_suivant_valeur_famille > 0 && (
                          <div className="mt-2 border-t border-[#cfe3ec] pt-2 text-[11px] leading-snug text-gray-600">
                            <span className="font-medium text-gray-800">
                              Valeur de famille : {formatMontant(statsGlobales.valeur_famille_mensuel)}/mois
                            </span>
                            {' — reprise par '}
                            <span className="font-semibold text-gray-800">
                              {statsGlobales.nb_membres_suivant_valeur_famille} membre{statsGlobales.nb_membres_suivant_valeur_famille > 1 ? 's' : ''}
                              {' sur '}{statsGlobales.nb_membres_actifs}
                            </span>
                            {' : '}
                            {(statsGlobales.membres_suivant_valeur_famille || []).map((membre) => membre.nom).join(', ')}
                            {statsGlobales.nb_membres_suivant_valeur_famille < statsGlobales.nb_membres_actifs
                              && '. Les autres ont leur propre réduction.'}
                          </div>
                        )}

                        {/* L'historique des valeurs, replié.
                            Il est rendu ici, sous la valeur, et non dans le rapport
                            financier : la question « depuis quand ? » se pose en
                            regardant le chiffre. Et il ne dépend pas du nombre de
                            membres qui suivent la valeur — sinon il disparaîtrait
                            quand personne ne la suit, c'est-à-dire exactement quand
                            un changement est le plus déroutant. */}
                        {historiqueValeurs.length > 0 && (
                          <div className="mt-2 border-t border-[#cfe3ec] pt-2">
                            <button
                              type="button"
                              onClick={() => setHistoriqueValeursOuvert((ouvert) => !ouvert)}
                              className="flex items-center gap-1.5 text-[11px] font-medium text-[#246990] hover:text-[#1f5a7a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/40 rounded"
                            >
                              {historiqueValeursOuvert
                                ? <ChevronDown className="h-3.5 w-3.5" />
                                : <ChevronRight className="h-3.5 w-3.5" />}
                              {historiqueValeurs.length} changement{historiqueValeurs.length > 1 ? 's' : ''} enregistré{historiqueValeurs.length > 1 ? 's' : ''}
                              {historiqueValeurs[0]?.date && (
                                <span className="font-normal text-gray-500">
                                  · dernier le {new Date(historiqueValeurs[0].date).toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </button>

                            {historiqueValeursOuvert && (
                              <div className="mt-2 space-y-1.5">
                                {historiqueValeurs.map((ligne) => (
                                  <div key={ligne.id} className="rounded-md border border-[#cfe3ec] bg-white px-2.5 py-1.5">
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                      <span className="text-[11px] font-semibold text-gray-900">
                                        {LIBELLES_HISTORIQUE_VALEUR[ligne.type] || ligne.type}
                                      </span>
                                      <span className="text-[10px] text-gray-500">
                                        {ligne.date
                                          ? new Date(ligne.date).toLocaleDateString('fr-FR', {
                                            day: '2-digit', month: 'long', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit'
                                          })
                                          : ''}
                                        {ligne.auteur ? ` · ${ligne.auteur}` : ''}
                                      </span>
                                    </div>
                                    {/* « avant → après » ne vaut que pour une valeur
                                        simple : 500 → 1200 se lit. Certains types
                                        stockent un objet, et l'afficher tel quel
                                        donnait du JSON brut à un gestionnaire. On
                                        interprète, ou on n'affiche rien. */}
                                    {(() => {
                                      const phrase = decrireChangementValeur(ligne);
                                      if (phrase) {
                                        return (
                                          <div className="mt-0.5 text-[11px] text-gray-700">{phrase}</div>
                                        );
                                      }
                                      if (estJson(ligne.avant) || estJson(ligne.apres)) return null;
                                      return (
                                        <div className="mt-0.5 text-[11px] text-gray-700">
                                          <span className="text-gray-500">{ligne.avant || '—'}</span>
                                          <span className="mx-1 text-gray-400">→</span>
                                          <span className="font-semibold text-gray-900">{ligne.apres || '—'}</span>
                                        </div>
                                      );
                                    })()}
                                    {ligne.motif && (
                                      <p className="mt-0.5 text-[10px] leading-snug text-gray-500">{ligne.motif}</p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Une réduction et un accord réduisent tous les deux ce que paie
                        la famille, mais ce ne sont pas la même chose et ils ne
                        s'affichent pas au même endroit. Sans cette explication, une
                        colonne Réduction vide en face d'un élève qui paie moins que
                        son tarif reste incompréhensible. */}
                    <div className="mx-4 mt-4 rounded-lg border border-[#246990]/20 bg-[#f4fbfd] overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowAideLecture(!showAideLecture)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-[#e9f5fa] transition-colors"
                      >
                        <span className="flex items-center gap-2 text-xs font-semibold text-[#174b67]">
                          <HelpCircle className="w-4 h-4" />
                          Comment lire ce tableau ?
                        </span>
                        <ChevronDown className={`w-4 h-4 text-[#246990] transition-transform ${showAideLecture ? 'rotate-180' : ''}`} />
                      </button>

                      {showAideLecture && (
                        <div className="border-t border-[#246990]/15 p-3 space-y-3 text-xs text-[#174b67]">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div className="rounded-md bg-white border border-green-200 p-2.5">
                              <div className="font-semibold text-green-800">Une réduction appartient à l'élève</div>
                              <p className="mt-1 text-gray-700">
                                Elle vit sur sa fiche, porte un motif, et le suit s'il change de famille.
                                C'est elle qui remplit la colonne <span className="font-semibold">Réduction</span> et
                                la colonne <span className="font-semibold">Ajustement</span>.
                              </p>
                            </div>
                            <div className="rounded-md bg-white border border-amber-200 p-2.5">
                              <div className="font-semibold text-amber-800">Un accord appartient à la famille</div>
                              <p className="mt-1 text-gray-700">
                                C'est un montant global négocié pour l'ensemble des enfants. Il ne modifie
                                aucune réduction : il s'ajoute par-dessus, réparti entre les membres, et
                                s'affiche sous <span className="font-semibold">À payer/mois</span>.
                              </p>
                            </div>
                          </div>

                          <p className="text-gray-700">
                            Autrement dit : <span className="font-semibold">un accord réduit sans être une réduction</span>.
                            C'est pourquoi la colonne Réduction peut rester vide alors que l'élève paie moins que
                            le tarif de sa classe.
                          </p>

                          <div className="rounded-md bg-white border border-[#246990]/15 overflow-hidden">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-[#eef7fb] text-[#174b67]">
                                  <th className="text-left px-2.5 py-1.5 font-semibold">Colonne Réduction</th>
                                  <th className="text-left px-2.5 py-1.5 font-semibold">Sous « À payer/mois »</th>
                                  <th className="text-left px-2.5 py-1.5 font-semibold">Ce que cela veut dire</th>
                                </tr>
                              </thead>
                              <tbody className="text-gray-700">
                                <tr className="border-t border-gray-100">
                                  <td className="px-2.5 py-1.5">vide</td>
                                  <td className="px-2.5 py-1.5">rien</td>
                                  <td className="px-2.5 py-1.5">L'élève paie le tarif officiel de sa classe.</td>
                                </tr>
                                <tr className="border-t border-gray-100">
                                  <td className="px-2.5 py-1.5">un badge</td>
                                  <td className="px-2.5 py-1.5">rien</td>
                                  <td className="px-2.5 py-1.5">Une réduction propre à l'élève, sans accord de famille.</td>
                                </tr>
                                <tr className="border-t border-gray-100 bg-amber-50/40">
                                  <td className="px-2.5 py-1.5">vide</td>
                                  <td className="px-2.5 py-1.5">Base réduite et Accord validé</td>
                                  <td className="px-2.5 py-1.5">
                                    Aucune réduction, mais un montant négocié pour la famille : c'est lui qui
                                    fait baisser ce que paie l'élève.
                                  </td>
                                </tr>
                                <tr className="border-t border-gray-100">
                                  <td className="px-2.5 py-1.5">un badge</td>
                                  <td className="px-2.5 py-1.5">Base réduite et Accord validé</td>
                                  <td className="px-2.5 py-1.5">
                                    Les deux : la réduction s'applique d'abord, l'accord ensuite sur le montant
                                    déjà réduit.
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {accordFinancierActif && (
                            <div className="rounded-md bg-white border border-amber-200 p-2.5">
                              <div className="font-semibold text-amber-800 mb-1.5">
                                Cette famille a un accord actif : les quatre valeurs à suivre
                              </div>
                              <ul className="space-y-1 text-gray-700">
                                <li><span className="font-semibold">Normal</span> : le frais officiel de la classe.</li>
                                <li><span className="font-semibold">Ajustement</span> : les réductions de l'élève, que l'accord ne modifie pas.</li>
                                <li><span className="font-semibold">Base réduite</span> : le montant après réductions, avant l'accord.</li>
                                <li><span className="font-semibold">Accord validé</span> : la part de l'ajustement négocié qui revient à cet élève, appliquée aux seuls mois recalculés.</li>
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="p-4 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b-2 border-gray-300 bg-gray-50">
                            <th className="text-left py-3 px-2 font-semibold text-gray-700" rowSpan="2">Élève</th>
                            <th className="text-center py-3 px-2 font-semibold text-gray-700" rowSpan="2">Type</th>
                            <th className="text-center py-3 px-2 font-semibold text-gray-700" rowSpan="2">Classe</th>
                            <th className="text-center py-3 px-2 font-semibold text-gray-700 whitespace-nowrap" rowSpan="2">
                              Réduction
                              <InfoTooltip className="ml-1" position="bottom" width="w-96">
                                <span className="block font-semibold mb-1">D'ou vient la reduction de chaque eleve</span>
                                <span className="block mb-1">
                                  <span className="font-semibold">Valeur famille</span> : l'eleve reprend la reduction
                                  de reference de la famille. Si cette valeur change, son montant suit.
                                </span>
                                <span className="block mb-1">
                                  <span className="font-semibold">Personnalisee</span> : une reduction propre a cet
                                  eleve, qui tient lieu de reduction pour lui. Si la famille a une valeur de reference,
                                  celle-ci ne s'applique pas a cet eleve ; si la famille n'en a pas, la personnalisee
                                  est simplement sa seule reduction.
                                </span>
                                <span className="block mb-1">
                                  <span className="font-semibold">Exceptionnelle</span> : une reduction supplementaire,
                                  la seule qui se cumule. Ce badge signifie qu'elle est la seule reduction de l'eleve.
                                </span>
                                <span className="block mb-1">
                                  <span className="font-semibold">Cumul de deux</span> : une exceptionnelle s'ajoute a
                                  la valeur de famille ou a la personnalisee.
                                </span>
                                <span className="block">
                                  Aucun badge : cet eleve n'a aucune reduction sur cette ligne. Le badge decrit le
                                  mensuel ; quand l'inscription suit une autre regle, elle est precisee dessous.
                                </span>
                              </InfoTooltip>
                            </th>
                            <th className="text-center py-2 px-2 font-semibold text-purple-700 border-l border-gray-300" colSpan="3">
                              {fraisDebutAnneeLabels.table}
                            </th>
                            <th className="text-center py-2 px-2 font-semibold text-blue-700 border-l border-gray-300" colSpan="3">
                              Frais scolaires mensuels
                            </th>
                          </tr>
                          <tr className="border-b-2 border-gray-300 bg-gray-50">
                            <th className="text-right py-2 px-2 font-semibold text-gray-600 border-l border-gray-300">
                              Normal
                              <InfoTooltip className="ml-1" position="bottom">
                                Frais officiel de debut d'annee defini sur la classe.
                              </InfoTooltip>
                            </th>
                            <th className="text-right py-2 px-2 font-semibold text-green-700">
                              Ajustement
                              <InfoTooltip className="ml-1" position="bottom">
                                Reductions appliquees a l'eleve. L'accord financier actif ne modifie pas cette colonne.
                              </InfoTooltip>
                            </th>
                            <th className="text-right py-2 px-2 font-semibold text-purple-700">
                              À payer
                            </th>
                            <th className="text-right py-2 px-2 font-semibold text-gray-600 border-l border-gray-300">
                              Normal
                              <InfoTooltip className="ml-1" position="bottom">
                                Frais mensuel officiel de la classe, avant toute reduction.
                              </InfoTooltip>
                            </th>
                            <th className="text-right py-2 px-2 font-semibold text-green-700">
                              Ajustement
                              <InfoTooltip className="ml-1" position="bottom">
                                Reductions mensuelles conservees sur le dossier eleve.
                              </InfoTooltip>
                            </th>
                            <th className="text-right py-2 px-2 font-semibold text-blue-700">
                              À payer/mois
                              <InfoTooltip className="ml-1" position="left" width="w-80">
                                Avec un accord actif, ce montant correspond a la ligne mensuelle recalculée pour les mois non payes. Les mois deja payes ou partiels gardent leur ancien montant.
                              </InfoTooltip>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {elevesFamille && elevesFamille.map((eleve, index) => (
                            <tr
                              key={eleve.id}
                              className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                            >
                              {/* ✅ MODIFIÉ : Badge RADIÉ */}
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium text-sm ${eleve.statut_eleve === 'radie' ? 'text-red-600 line-through' : ''
                                    }`}>
                                    {eleve.prenom} {eleve.nom}
                                  </span>

                                  {eleve.statut_eleve === 'radie' && (
                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">
                                      RADIÉ
                                    </span>
                                  )}
                                </div>
                              </td>

                              <td className="py-3 px-2 text-center">
                                {eleve.frais_debut_annee_type === 'reinscription' ? (
                                  <span className="inline-flex min-w-[92px] justify-center px-2 py-0.5 bg-[#e7f3f8] text-[#246990] rounded text-[10px] font-bold">
                                    RÉINSCRIPTION
                                  </span>
                                ) : (
                                  <span className="inline-flex min-w-[92px] justify-center px-2 py-0.5 bg-[#eef7fb] text-[#246990] rounded text-[10px] font-bold">
                                    INSCRIPTION
                                  </span>
                                )}
                              </td>

                              <td className="py-3 px-2 text-center">
                                <span className={BADGE_CLASSE_CLASSES}>
                                  {eleve.classe_nom}
                                </span>
                              </td>

                              {/* Colonne dediee : les badges s'alignent au lieu de flotter
                                  a la suite de noms de longueurs differentes. */}
                              <td className="py-3 px-2 text-center">
                                {BADGE_ORIGINE_REDUCTION[eleve.reduction_mode_mensuel] ? (
                                  <span
                                    className={`${BADGE_ORIGINE_CLASSES} ${BADGE_ORIGINE_REDUCTION[eleve.reduction_mode_mensuel].classe}`}
                                    title={detaillerSourcesReduction(eleve.reduction_detail?.mensuel).join(' + ') || undefined}
                                  >
                                    {BADGE_ORIGINE_REDUCTION[eleve.reduction_mode_mensuel].libelle}
                                  </span>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                                {/* Les regles sont par ligne : un eleve peut suivre la valeur
                                    de famille a l'inscription sans la suivre au mensuel. */}
                                {eleve.reduction_mode_inscription !== eleve.reduction_mode_mensuel && (
                                  <div className="mt-1 text-[10px] text-gray-500">
                                    Inscription :{' '}
                                    {BADGE_ORIGINE_REDUCTION[eleve.reduction_mode_inscription]?.libelle || 'aucune'}
                                  </div>
                                )}
                              </td>

                              <td className="py-3 px-2 text-right text-gray-600 border-l border-gray-200">
                                {formatMontant(eleve.frais_inscription_normal)}
                              </td>
                              <td className="py-3 px-2 text-right text-green-600 font-semibold">
                                - {formatMontant(eleve.reduction_inscription_totale)}
                                {detaillerSourcesReduction(eleve.reduction_detail?.inscription).length > 1 && (
                                  <div className="mt-0.5 text-[10px] font-normal text-gray-500">
                                    {detaillerSourcesReduction(eleve.reduction_detail.inscription).join(' + ')}
                                  </div>
                                )}
                                {eleve.reduction_detail?.inscription?.plafonnee && (
                                  <div className="mt-0.5 text-[10px] font-medium text-amber-700">
                                    {formatMontant(eleve.reduction_detail.inscription.demandee)} demandés,
                                    {' '}plafonnés aux frais
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-2 text-right text-purple-700 font-bold">
                                {formatMontant(eleve.frais_inscription_avec_reduction)}
                              </td>
                              <td className="py-3 px-2 text-right text-gray-600 border-l border-gray-200">
                                {formatMontant(eleve.frais_mensuel_normal)}
                              </td>
                              <td className={`py-3 px-2 text-right font-semibold ${eleve.reduction_mensuel_totale >= 0 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                {eleve.reduction_mensuel_totale >= 0 ? '- ' : '+ '}
                                {formatMontant(Math.abs(eleve.reduction_mensuel_totale))}
                                {detaillerSourcesReduction(eleve.reduction_detail?.mensuel).length > 1 && (
                                  <div className="mt-0.5 text-[10px] font-normal text-gray-500">
                                    {detaillerSourcesReduction(eleve.reduction_detail.mensuel).join(' + ')}
                                  </div>
                                )}
                                {eleve.reduction_detail?.mensuel?.plafonnee && (
                                  <div className="mt-0.5 text-[10px] font-medium text-amber-700">
                                    {formatMontant(eleve.reduction_detail.mensuel.demandee)} demandés,
                                    {' '}plafonnés aux frais
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-2 text-right text-blue-700 font-bold text-sm">
                                <div>{formatMontant(getMontantMensuelEffectif(eleve))}</div>
                                {getAjustementAccord(eleve) > 0 && (
                                  <div className="mt-1 space-y-0.5 text-[10px] font-medium">
                                    <div className="text-gray-500">
                                      Base reduite : {formatMontant(eleve.frais_mensuel_avec_reduction)}
                                    </div>
                                    <div className="text-[#9b5c00]">
                                      Accord valide : - {formatMontant(getAjustementAccord(eleve))}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}

                          {/* ✅ MODIFIÉ : Ligne TOTAL avec badge radiés */}
                          <tr className="bg-blue-50 border-t-2 border-blue-300 font-bold">
                            {/* 4 colonnes libellees avant les montants : Eleve, Type, Classe, Reduction */}
                            <td className="py-3 px-2" colSpan="4">
                              <div className="flex items-center gap-2">
                                <span className="text-blue-900 flex items-center">
                                  TOTAL FAMILLE
                                  <span className="ml-2 text-xs font-normal">
                                    ({statsGlobales?.nombre_eleves} élève{statsGlobales?.nombre_eleves > 1 ? 's' : ''} actif{statsGlobales?.nombre_eleves > 1 ? 's' : ''})
                                  </span>
                                </span>

                                {statsGlobales?.nb_eleves_radies > 0 && (
                                  <div className="relative group">
                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold cursor-help">
                                      {statsGlobales.nb_eleves_radies} RADIÉ{statsGlobales.nb_eleves_radies > 1 ? 'S' : ''}
                                    </span>

                                    <div className="absolute left-0 bottom-full mb-2 w-56 bg-white border-2 border-red-200 rounded-lg shadow-xl p-2 hidden group-hover:block z-50">
                                      <div className="text-[10px] text-left text-gray-700">
                                        <p className="font-semibold text-red-700 mb-1">
                                          ℹ️ Totaux élèves actifs uniquement
                                        </p>
                                        <p>
                                          {statsGlobales.nb_eleves_radies} élève{statsGlobales.nb_eleves_radies > 1 ? 's' : ''} radié{statsGlobales.nb_eleves_radies > 1 ? 's' : ''}
                                          (non inclus dans les totaux).
                                        </p>
                                      </div>
                                      <div className="absolute left-8 top-full w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-red-200"></div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2 text-right text-gray-700 border-l border-gray-300">
                              {formatMontant(statsGlobales?.total_frais_inscription_normal)}
                            </td>
                            <td className="py-3 px-2 text-right text-green-700">
                              - {formatMontant(statsGlobales?.total_reduction_inscription)}
                            </td>
                            <td className="py-3 px-2 text-right text-purple-700 text-sm">
                              {formatMontant(statsGlobales?.total_frais_inscription_avec_reduction)}
                            </td>
                            <td className="py-3 px-2 text-right text-gray-700 border-l border-gray-300">
                              {formatMontant(statsGlobales?.total_mensuel_normal)}
                            </td>
                            <td className={`py-3 px-2 text-right font-semibold ${statsGlobales?.total_reduction_mensuel >= 0 ? 'text-green-700' : 'text-red-700'
                              }`}>
                              {statsGlobales?.total_reduction_mensuel >= 0 ? '- ' : '+ '}
                              {formatMontant(Math.abs(statsGlobales?.total_reduction_mensuel || 0))}
                            </td>
                            <td className="py-3 px-2 text-right text-blue-900 text-base">
                              <div>{formatMontant(montantMensuelEffectif)}</div>
                              {accordFinancierActif && (
                                <div className="mt-1 space-y-0.5 text-[10px] font-medium">
                                  <div className="text-gray-500">
                                    Base apres reductions : {formatMontant(statsGlobales?.total_mensuel_avec_reduction)}
                                  </div>
                                  <div className="text-[#9b5c00]">
                                    Accord valide : - {formatMontant(accordFinancierActif.ajustement_mensuel)}
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>


              {/* 3️⃣ RÉSUMÉ FINANCIER */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div
                  className="p-4 bg-[#eef7fb] border-b border-[#246990]/20 flex items-center justify-between cursor-pointer hover:bg-[#e5f3fb] transition-colors"
                  onClick={() => setShowResume(!showResume)}
                >
                  <h2 className="font-medium text-[#246990] text-lg flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Résumé {selectedFamille?.nom}
                  </h2>
                  <ChevronDown
                    className={`w-5 h-5 text-[#246990] transition-transform ${showResume ? 'rotate-180' : ''
                      }`}
                  />
                </div>

                {showResume && (
                  <div className="p-4">
                    {familleAvecRetrait && (
                      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                        <div className="font-semibold mb-1">Lecture du résumé après retrait</div>
                        <div className="text-xs leading-relaxed">
                          Cette famille a changé de composition. Les mois déjà payés ou partiellement payés restent dans leur historique comptable.
                          Les nouveaux montants ne s'appliquent qu'aux mois non payés à partir du mois d'effet.
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4 mb-4">


                      {/* Carte 1 : Reduction actuelle */}
                      <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-green-600 font-medium flex items-center gap-1">
                            Réductions des membres restants
                            {familleAvecRetrait && (
                              <InfoTooltip position="right" width="w-80">
                                Cette valeur concerne uniquement les élèves encore rattachés à la famille. Les mois déjà payés avant le retrait gardent leur historique.
                              </InfoTooltip>
                            )}
                          </div>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-semibold">
                            PROJECTION
                          </span>
                        </div>

                        {/* Affichage avec montant barré si radiation */}
                        {statsGlobales?.nb_eleves_radies > 0 ? (
                          <>
                            <div className="text-lg text-gray-400 line-through">
                              - {formatMontant(statsGlobales?.total_reduction_mensuel_tous)}
                            </div>
                            <div className="text-2xl font-bold text-green-600">
                              - {formatMontant(statsGlobales?.total_reduction_mensuel)}
                            </div>
                          </>
                        ) : (
                          <div className="text-2xl font-bold text-green-600">
                            - {formatMontant(statsGlobales?.total_reduction_mensuel)}
                          </div>
                        )}

                        <div className="text-xs text-gray-500 mt-1">
                          par mois, sur {statsGlobales?.nombre_eleves} membre{statsGlobales?.nombre_eleves > 1 ? 's' : ''} actif{statsGlobales?.nombre_eleves > 1 ? 's' : ''}
                        </div>

                        <div className="mt-2 pt-2 border-t border-green-200">
                          {statsGlobales?.nb_eleves_radies > 0 ? (
                            <>
                              <div className="text-sm text-gray-400 line-through">
                                - {formatMontant(statsGlobales?.total_reduction_annuel_tous)}
                              </div>
                              <div className="text-sm font-semibold text-green-700">
                                - {formatMontant(statsGlobales?.total_reduction_annuel)}
                              </div>
                            </>
                          ) : (
                            <div className="text-sm font-semibold text-green-700">
                              - {formatMontant(statsGlobales?.total_reduction_annuel)}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {familleAvecRetrait
                              ? `projection sur ${statsGlobales?.nb_mois_scolarite} mois, hors mois historiques`
                              : `sur l'année (${statsGlobales?.nb_mois_scolarite} mois)`}
                          </div>
                        </div>

                        {/* Sans cette distinction, une famille sous accord affiche
                            « - 0 FCFA » de réductions alors qu'elle paie bel et bien
                            moins : le chiffre se lit comme « aucun avantage », ce qui
                            est faux. L'accord n'est pas une réduction, mais il réduit. */}
                        {accordFinancierActif && ajustementAccordMensuel > 0 && (
                          <div className="mt-2 pt-2 border-t border-green-200 space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500">Réductions des élèves :</span>
                              <span className="font-semibold text-green-700">
                                - {formatMontant(statsGlobales?.total_reduction_mensuel)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500">Accord de la famille :</span>
                              <span className="font-semibold text-[#9b5c00]">
                                - {formatMontant(ajustementAccordMensuel)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs pt-1 border-t border-green-100">
                              <span className="font-semibold text-gray-700">Baisse totale par mois :</span>
                              <span className="font-bold text-gray-900">
                                - {formatMontant(Number(statsGlobales?.total_reduction_mensuel || 0) + ajustementAccordMensuel)}
                              </span>
                            </div>
                            <p className="text-[11px] leading-snug text-gray-500 pt-1">
                              Le montant en gros ci-dessus ne compte que les réductions portées par les élèves.
                              L'accord est négocié pour la famille : il réduit sans être une réduction.
                            </p>
                          </div>
                        )}

                        {/* Le tarif brut et la base annuelle vivaient dans la carte
                            « Compte annuel réel », où ils contredisaient le total affiché :
                            ce sont des projections « un mois × N », pas des montants réels.
                            Leur place est ici, sous le badge PROJECTION, à côté de la
                            réduction dont ils découlent. */}
                        {statsGlobales?.total_annuel_normal > 0 && (
                          <div className="mt-2 pt-2 border-t border-green-200 space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500">Tarif sans réduction :</span>
                              <span className="text-gray-500 line-through">
                                {formatMontant(statsGlobales.total_annuel_normal)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-600">Base projetée :</span>
                              <span className="font-semibold text-gray-800">
                                {formatMontant(
                                  (statsGlobales.total_annuel_normal || 0)
                                  - (statsGlobales.total_reduction_annuel || 0)
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>



                      {/* ✅ Carte 2 : Montant mensuel AVEC TOOLTIP */}
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">


                        {/* Carte 2 : Montant mensuel */}
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
                              {familleAvecRetrait ? 'Montant des mois recalculés' : 'Montant mensuel courant'}

                              {statsGlobales?.nb_eleves_radies > 0 && (
                                <div className="relative group">
                                  <AlertCircle className="w-3 h-3 text-blue-500 cursor-help" />

                                  <div className="absolute left-0 bottom-full mb-2 w-56 bg-white border-2 border-blue-200 rounded-lg shadow-xl p-2 hidden group-hover:block z-50">
                                    <div className="text-[10px] text-left text-gray-700">
                                      <p className="font-semibold text-blue-700 mb-1">
                                        ℹ️ Composition modifiée
                                      </p>
                                      <p>
                                        {statsGlobales.nb_eleves_radies} élève{statsGlobales.nb_eleves_radies > 1 ? 's' : ''} radié{statsGlobales.nb_eleves_radies > 1 ? 's' : ''}.
                                        Le montant concerne les {statsGlobales.nombre_eleves} élève{statsGlobales.nombre_eleves > 1 ? 's' : ''} actuellement actif{statsGlobales.nombre_eleves > 1 ? 's' : ''}.
                                      </p>
                                    </div>
                                    <div className="absolute left-4 top-full w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-blue-200"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">
                              {familleAvecRetrait ? 'MOIS FUTURS' : 'À PAYER'}
                            </span>
                          </div>

                          {/* Affichage avec montant barré si radiation */}
                          {statsGlobales?.nb_eleves_radies > 0 ? (
                            <>
                              <div className="text-lg text-gray-400 line-through">
                                {formatMontant(statsGlobales?.total_mensuel_initial_tous)}
                              </div>
                              <div className="text-2xl font-bold text-blue-700">
                                {formatMontant(montantMensuelEffectif)}
                              </div>
                            </>
                          ) : (
                            <div className="text-2xl font-bold text-blue-700">
                              {formatMontant(montantMensuelEffectif)}
                            </div>
                          )}

                          <div className="text-xs text-gray-500 mt-1">
                            pour {statsGlobales?.nombre_eleves} membre{statsGlobales?.nombre_eleves > 1 ? 's' : ''} restant{statsGlobales?.nombre_eleves > 1 ? 's' : ''}
                            {familleAvecRetrait && accordFinancierActif?.mois_effet_nom
                              ? `, à partir de ${accordFinancierActif.mois_effet_nom}`
                              : ''}
                          </div>

                          <div className="mt-2 pt-2 border-t border-blue-200">
                            <div className="flex justify-between text-xs">
                              <span className="text-gray-500">Tarif normal membres restants :</span>
                              <span className="text-gray-600 line-through">
                                {formatMontant(statsGlobales?.total_mensuel_normal)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs mt-1">
                              <span className="text-green-600">Réductions officielles :</span>
                              <span className="text-green-600 font-semibold">
                                {formatMontant(statsGlobales?.total_reduction_mensuel)}
                              </span>
                            </div>
                            <div className="flex justify-between text-xs mt-1">
                              <span className="text-gray-600">Base après réductions :</span>
                              <span className="text-gray-800 font-semibold">
                                {formatMontant(baseMensuelleApresReductions)}
                              </span>
                            </div>
                            {accordFinancierActif && (
                              <div className="flex justify-between text-xs mt-1">
                                <span className="text-[#9b5c00]">
                                  {familleAvecRetrait ? 'Recomposition après retrait :' : 'Accord validé :'}
                                </span>
                                <span className="text-[#9b5c00] font-semibold">
                                  - {formatMontant(ajustementAccordMensuel)}
                                </span>
                              </div>
                            )}
                            {familleAvecRetrait && (
                              <div className="mt-2 rounded border border-blue-200 bg-white/70 p-2 text-[10px] leading-relaxed text-blue-800">
                                Les mois déjà payés ou partiels gardent l'ancien montant. Ce montant sert uniquement aux mois non payés recalculés après le retrait.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Carte 3 : Compte annuel reel */}
                      <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs text-indigo-600 font-medium flex items-center gap-1">
                            Compte annuel réel

                            {/* Tooltip si radiation */}
                            {statsGlobales?.nb_eleves_radies > 0 && (
                              <div className="relative group">
                                <AlertCircle className="w-3 h-3 text-indigo-500 cursor-help" />

                                <div className="absolute left-0 bottom-full mb-2 w-64 bg-white border-2 border-indigo-200 rounded-lg shadow-xl p-3 hidden group-hover:block z-50">
                                  <div className="text-xs text-left text-gray-700">
                                    <p className="font-semibold text-indigo-700 mb-2">
                                      ℹ️ Composition modifiée
                                    </p>
                                    <p className="mb-2">
                                      Le montant initial ({formatMontant(statsGlobales.total_annuel_initial)}) incluait
                                      {statsGlobales.nombre_eleves + statsGlobales.nb_eleves_radies} élève{(statsGlobales.nombre_eleves + statsGlobales.nb_eleves_radies) > 1 ? 's' : ''}.
                                    </p>
                                    <div className="bg-red-50 border border-red-200 rounded p-2 text-[10px]">
                                      <p className="font-semibold text-red-700 mb-1">
                                        Radiation : -{formatMontant(statsGlobales.total_annuel_initial - statsGlobales.total_annuel_avec_reduction)}
                                      </p>
                                      <p className="text-gray-600">
                                        Le montant actuel concerne {statsGlobales.nombre_eleves} élève{statsGlobales.nombre_eleves > 1 ? 's' : ''} actif{statsGlobales.nombre_eleves > 1 ? 's' : ''}.
                                      </p>
                                    </div>
                                  </div>
                                  <div className="absolute left-4 top-full w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-indigo-200"></div>
                                </div>
                              </div>
                            )}
                          </div>
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-semibold">
                            {statsGlobales?.nb_mois_scolarite} MOIS
                          </span>
                        </div>

                        {/* Affichage avec montant barré si radiation */}
                        {statsGlobales?.nb_eleves_radies > 0 ? (
                          <div className="space-y-1">
                            <div className="text-lg text-gray-400 line-through">
                              {formatMontant(statsGlobales?.total_annuel_initial)}
                            </div>
                            <div className="text-2xl font-bold text-indigo-700">
                              {formatMontant(totalAnnuelDuReel)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-2xl font-bold text-indigo-700">
                            {formatMontant(totalAnnuelDuReel)}
                          </div>
                        )}

                        <div className="text-xs text-gray-500 mt-1">
                          total dû de l'année
                        </div>

                        {/* Note explicative : dit d'ou vient le total, avec les chiffres
                            de cette famille. Le texte precedent decrivait le mecanisme
                            interne - « mois historiques conserves », « mois recalcules » -
                            sans jamais repondre a la question posee : pourquoi ce total ne
                            vaut-il pas le montant d'un mois multiplie par le nombre de mois. */}
                        {syntheseAnnuelle && (
                          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-[10px] leading-relaxed text-blue-700 flex items-start">
                            <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0 mt-0.5" />
                            {syntheseAnnuelle.ecarts.length === 0 ? (
                              <span>
                                <span className="font-semibold">
                                  {syntheseAnnuelle.nbMois} mois à {formatMontant(syntheseAnnuelle.montantStandard)}.
                                </span>
                              </span>
                            ) : (
                              <span>
                                <span className="font-semibold">
                                  Somme des {syntheseAnnuelle.nbMois} mois, et non le montant d'un mois × {syntheseAnnuelle.nbMois}.
                                </span>
                                <br />
                                {syntheseAnnuelle.nbStandard} mois à {formatMontant(syntheseAnnuelle.montantStandard)}
                                {syntheseAnnuelle.ecarts.map((ecart) => (
                                  <React.Fragment key={ecart.nom}>
                                    {' · '}
                                    <span className="font-semibold">{ecart.nom}</span> à {formatMontant(ecart.montant)}
                                    {ecart.raisons.length > 0 && <> : {ecart.raisons.join(', ')}</>}
                                  </React.Fragment>
                                ))}
                                .
                              </span>
                            )}
                          </div>
                        )}

                        {/* Lecture du compte annuel. Le cas ordinaire n'affiche plus rien
                            ici : le tarif brut et les réductions sont des projections
                            « un mois × N », elles ont rejoint la carte PROJECTION. Les
                            laisser sous un total réel donnait trois nombres qui ne
                            s'additionnaient pas. Ne restent que les décompositions
                            adossées à un événement réel. */}
                        {(familleAvecRetrait || statsGlobales?.nb_eleves_radies > 0) && (
                          <div className="mt-2 pt-2 border-t border-indigo-200">
                          {familleAvecRetrait ? (
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Mois historiques conservés :</span>
                                <span className="font-semibold text-gray-800">
                                  {formatMontant(totalAnnuelData.montant_historique_conserve || 0)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Mois futurs recalculés :</span>
                                <span className="font-semibold text-gray-800">
                                  {formatMontant(totalAnnuelData.montant_recalcule || 0)}
                                </span>
                              </div>
                              <div className="h-px bg-indigo-200 my-1" />
                              <div className="flex justify-between">
                                <span className="text-indigo-700 font-semibold">Total dû réel :</span>
                                <span className="font-bold text-indigo-700">{formatMontant(totalAnnuelDuReel)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-green-700">Déjà encaissé :</span>
                                <span className="font-semibold text-green-700">{formatMontant(totalAnnuelPayeReel)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-red-700">Reste à encaisser :</span>
                                <span className="font-bold text-red-700">{formatMontant(totalAnnuelResteReel)}</span>
                              </div>
                              <div className="mt-2 rounded border border-indigo-200 bg-white/70 p-2 text-[10px] leading-relaxed text-indigo-800">
                                Les mois payés ou partiels restent au montant historique. Les mois non payés après le retrait utilisent le nouveau montant mensuel.
                              </div>
                            </div>
                          ) : (
                            <>
                              {/* Comparaison de deux projections annuelles, pas de montants
                                  réels : le libellé doit le dire, sinon on retombe sur des
                                  chiffres qui semblent devoir s'accorder avec le total. */}
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500 mb-1">
                                Projection annuelle
                              </div>

                              {/* AVANT radiation */}
                              <div className="mb-3">
                                <div className="text-[10px] text-gray-500 mb-1 font-semibold flex items-center gap-1">
                                  <span>Avant radiation ({statsGlobales.nombre_eleves + statsGlobales.nb_eleves_radies} élève{(statsGlobales.nombre_eleves + statsGlobales.nb_eleves_radies) > 1 ? 's' : ''}) :</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-500">Sans réduction :</span>
                                  <span className="text-gray-600 line-through">
                                    {formatMontant(statsGlobales?.total_annuel_normal_tous)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs mt-1">
                                  <span className="text-green-500">Économie famille :</span>
                                  <span className="text-green-600 font-semibold line-through">
                                    {formatMontant(statsGlobales?.total_reduction_annuel_tous)}
                                  </span>
                                </div>
                              </div>

                              {/* Séparateur */}
                              <div className="h-px bg-indigo-300 my-2"></div>

                              {/* APRÈS radiation */}
                              <div>
                                <div className="text-[10px] text-indigo-600 mb-1 font-semibold flex items-center gap-1">
                                  <span>Après radiation ({statsGlobales.nombre_eleves} élève{statsGlobales.nombre_eleves > 1 ? 's' : ''} actif{statsGlobales.nombre_eleves > 1 ? 's' : ''}) :</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-700">Sans réduction :</span>
                                  <span className="text-gray-800 font-semibold">
                                    {formatMontant(statsGlobales?.total_annuel_normal)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs mt-1">
                                  <span className="text-green-700">Économie famille :</span>
                                  <span className="text-green-700 font-bold">
                                    {formatMontant(statsGlobales?.total_reduction_annuel)}
                                  </span>
                                </div>
                              </div>
                            </>
                          )}
                          </div>
                        )}
                      </div>


                    </div>

                    {/* Frais d'inscription */}
                    {/* Frais d'inscription */}
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <div className="flex items-center mb-2">
                        <div className="text-xs font-semibold text-gray-700">
                          {fraisDebutAnneeLabels.resume}
                        </div>
                      </div>
                      {familleAvecRetrait && (
                        <div className="mb-2 rounded border border-purple-200 bg-white/70 p-2 text-[10px] leading-relaxed text-purple-800">
                          Ces frais correspondent au début d'année. Ils ne sont pas recalculés par le retrait ; l'accord/recomposition concerne les frais mensuels futurs.
                        </div>
                      )}

                      {statsGlobales?.nb_eleves_radies > 0 ? (
                        <div className="grid grid-cols-3 gap-3 text-xs mb-2">
                          <div className="text-center">
                            <div className="text-gray-500 mb-1">Normal</div>
                            <div className="text-sm text-gray-400 line-through">
                              {formatMontant(statsGlobales?.total_frais_inscription_initial)}
                            </div>
                            <div className="font-bold text-gray-700">
                              {formatMontant(statsGlobales?.total_frais_inscription_normal)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-green-600 mb-1">Réduction</div>
                            <div className="text-sm text-gray-400 line-through">
                              - {formatMontant(statsGlobales?.total_reduction_inscription_tous)}
                            </div>
                            <div className="font-bold text-green-700">
                              - {formatMontant(statsGlobales?.total_reduction_inscription)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-blue-600 mb-1">À payer</div>
                            <div className="text-sm text-gray-400 line-through">
                              {formatMontant(statsGlobales?.total_frais_inscription_initial)}
                            </div>
                            <div className="font-bold text-blue-700 text-sm">
                              {formatMontant(statsGlobales?.total_frais_inscription_avec_reduction)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-3 gap-3 text-xs">
                          <div className="text-center">
                            <div className="text-gray-500 mb-1">Normal</div>
                            <div className="font-bold text-gray-700">
                              {formatMontant(statsGlobales?.total_frais_inscription_normal)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-green-600 mb-1">Réduction</div>
                            <div className="font-bold text-green-700">
                              - {formatMontant(statsGlobales?.total_reduction_inscription)}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-blue-600 mb-1">À payer</div>
                            <div className="font-bold text-blue-700 text-sm">
                              {formatMontant(statsGlobales?.total_frais_inscription_avec_reduction)}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>


              {/* 4️⃣ RAPPORT FINANCIER PAR MOIS - VERSION COMPLÈTE */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div
                  className="p-4 bg-[#eef7fb] border-b border-[#246990]/20 flex items-center justify-between cursor-pointer hover:bg-[#e5f3fb] transition-colors"
                  onClick={() => setShowRapportFinancier(!showRapportFinancier)}
                >
                  <h2 className="font-medium text-[#246990] text-lg flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Rapport financier par mois
                  </h2>
                  <ChevronDown
                    className={`w-5 h-5 text-[#246990] transition-transform ${showRapportFinancier ? 'rotate-180' : ''
                      }`}
                  />
                </div>

                {showRapportFinancier && (
                  <div className="p-4 space-y-4">

                    {/* Rappel sans bouton de fermeture.
                        Une case « OK, c'est fait » serait une promesse, pas une
                        vérification : on pourrait la cocher sans rien avoir fait,
                        et le problème disparaîtrait de l'écran en restant dans les
                        comptes. Ici la condition est constatée à chaque chargement
                        — l'encadré s'efface de lui-même quand les mois sont
                        marqués, et pas avant. */}
                    {membresARegulariser.length > 0 && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold text-amber-900">
                              {membresARegulariser.length === 1
                                ? 'Un élève doit des mois que les autres ont réglés'
                                : `${membresARegulariser.length} élèves doivent des mois que les autres ont réglés`}
                            </div>
                            <div className="mt-1.5 space-y-1.5">
                              {membresARegulariser.map((membre) => (
                                <div key={membre.eleve_id} className="text-[11px] leading-snug text-amber-900">
                                  <span className="font-semibold">{membre.eleve}</span>
                                  {' doit '}
                                  <span className="font-semibold">{formatMontant(membre.montant_total)}</span>
                                  {' sur '}
                                  {membre.mois.map((m) => m.mois_nom).join(', ')}
                                  {' — mois que le reste de la famille a soldés.'}
                                </div>
                              ))}
                            </div>
                            <p className="mt-2 text-[11px] leading-snug text-amber-800">
                              S'il n'était pas encore à l'école sur ces mois, marquez-les
                              « non scolarisés » : son montant y passera à zéro et le total de
                              la famille redeviendra celui d'avant son arrivée. S'il doit
                              réellement ces mois, il n'y a rien à faire — ce rappel disparaîtra
                              dès qu'ils seront réglés.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}


                    {/* Opérations inter-sites.
                        Elles s'écrivaient déjà dans l'historique de la famille, mais la
                        requête de traçabilité ne lisait que trois types d'événements :
                        elles étaient donc en base sans qu'aucun écran ne les montre.
                        Une décision prise sur un autre site est précisément ce qu'un
                        comptable ne peut pas deviner en regardant le sien. */}
                    {operationsInterSites.length > 0 && (
                      <div className="rounded-lg border border-[#cfe3ec] bg-[#f4f9fc] p-3">
                        <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-[#246990]">
                          <Network className="h-4 w-4" />
                          Opérations inter-sites
                        </div>
                        <p className="mb-2 text-[11px] leading-snug text-gray-600">
                          Décisions venues du groupe ou prises ici, qui engagent plusieurs écoles.
                        </p>
                        <div className="space-y-2">
                          {operationsInterSites.map((operation) => (
                            <div key={operation.id} className="rounded-md border border-[#cfe3ec] bg-white p-2.5">
                              <div className="flex flex-wrap items-baseline justify-between gap-2">
                                <span className="text-xs font-semibold text-gray-900">
                                  {LIBELLES_OPERATION_INTERSITE[operation.type] || operation.type}
                                </span>
                                <span className="text-[11px] text-gray-500">
                                  {operation.date
                                    ? new Date(operation.date).toLocaleDateString('fr-FR', {
                                      day: '2-digit', month: 'long', year: 'numeric',
                                      hour: '2-digit', minute: '2-digit'
                                    })
                                    : ''}
                                </span>
                              </div>
                              {operation.motif && (
                                <p className="mt-1 text-[11px] leading-snug text-gray-600">{operation.motif}</p>
                              )}
                              {operation.type === 'reduction_propagee_intersite' && operation.avant && operation.apres && (
                                <div className="mt-1.5 flex flex-wrap gap-3 text-[11px]">
                                  <span className="text-gray-500">
                                    Mensuelle : <span className="font-semibold text-gray-800">
                                      {formatMontant(operation.avant.reduction_mensuel)}
                                    </span>
                                    {' → '}
                                    <span className="font-semibold text-[#246990]">
                                      {formatMontant(operation.apres.reduction_mensuel)}
                                    </span>
                                  </span>
                                  {Number(operation.apres.eleves_non_alignes || 0) > 0 && (
                                    <span className="text-amber-700">
                                      {operation.apres.eleves_non_alignes} élève(s) non aligné(s)
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mouvements de composition de l'année.
                        Placé une seule fois en tête : une entrée ou une sortie est un
                        fait de l'année, pas de chaque mois. Sans lui, un comptable qui
                        découvre le dossier ne sait pas qu'un élève est passé par la
                        famille, ni pourquoi certains montants ne comptent pas. */}
                    {mouvementsComposition.length > 0 && (
                      <div className="rounded-lg border border-[#cfe3ec] bg-[#f4f9fc] p-3">
                        <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold text-[#246990]">
                          <Users className="h-4 w-4" />
                          Mouvements de composition de l'année
                        </div>

                        {/* Sans cette phrase, « effet Novembre » à côté de « validée le
                            9 août » se lit comme une contradiction : on cherche un lien
                            entre deux choses qui n'en ont pas. Le mois d'effet est une
                            position dans l'année scolaire, pas une date. */}
                        <p className="mb-2 text-[11px] leading-snug text-gray-600">
                          <span className="font-semibold text-gray-800">Le mois d'effet n'est pas une date de calendrier.</span>
                          {' '}C'est le premier mois de l'année scolaire que l'élève n'avait pas encore commencé à
                          régler au moment de la décision. Un élève ayant déjà payé octobre aura donc pour mois
                          d'effet novembre, quelle que soit la date à laquelle la décision a été prise.
                        </p>
                        <div className="space-y-2">
                          {mouvementsComposition.map((mouvement) => {
                            // Deux étapes possibles par élève, dans l'ordre chronologique.
                            // Le serveur renvoie désormais la chronologie complète : un
                            // élève retiré puis réintégré puis radié porte ses quatre
                            // étapes, dans l'ordre. On n'en masque aucune.
                            const LIBELLES_ETAPE = {
                              entree: 'Entrée',
                              sortie: 'Sortie de la famille',
                              radiation: 'Radiation',
                              reintegration: 'Réintégration'
                            };
                            const etapes = (mouvement.etapes || []).map((etape, index) => ({
                              cle: `${etape.type}-${index}`,
                              libelle: LIBELLES_ETAPE[etape.type] || etape.type,
                              ...etape
                            }));
                            const derniereEtape = etapes[etapes.length - 1];
                            const estRadie = derniereEtape?.type === 'radiation';
                            const periode = mouvement.encore_membre ? 'depuis son entrée' : 'pendant sa présence';

                            return (
                              <div
                                key={mouvement.eleve_id}
                                className="rounded-md border border-[#dceaf2] bg-white px-3 py-2"
                              >
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="text-xs font-semibold text-gray-900">{mouvement.nom}</span>
                                  {!mouvement.encore_membre && (
                                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${estRadie
                                      ? 'border-red-200 bg-red-50 text-red-700'
                                      : 'border-gray-200 bg-gray-50 text-gray-600'}`}
                                    >
                                      {estRadie ? "Radié de l'école" : 'Sorti de la famille'}
                                    </span>
                                  )}
                                </div>

                                {etapes.map((etape) => (
                                  <div key={etape.cle} className="mt-1 text-[11px] leading-snug text-gray-600">
                                    <span className="font-medium text-gray-800">{etape.libelle}</span>
                                    {etape.mois?.nom ? ` : effet ${etape.mois.nom}` : ''}
                                    {etape.date_demande && (
                                      <>
                                        {' · demandée le '}{formatDateTime(etape.date_demande)}
                                        {etape.demandeur ? ` par ${etape.demandeur}` : ''}
                                      </>
                                    )}
                                    {etape.date_decision && (
                                      <>
                                        {' · validée le '}{formatDateTime(etape.date_decision)}
                                        {etape.validateur ? ` par ${etape.validateur}` : ''}
                                      </>
                                    )}
                                  </div>
                                ))}

                                <div className="mt-1 text-[11px] leading-snug">
                                  {mouvement.nb_mois_encaisses > 0 ? (
                                    <span className="text-gray-700">
                                      {mouvement.nb_mois_encaisses} mois encaissé{mouvement.nb_mois_encaisses > 1 ? 's' : ''}
                                      {' '}par la famille {periode} ({formatMontant(mouvement.montant_encaisse)})
                                      {mouvement.encore_membre
                                        ? ''
                                        : `, conservé${mouvement.nb_mois_encaisses > 1 ? 's' : ''} dans l'historique`}
                                    </span>
                                  ) : (
                                    <span className="font-medium text-gray-800">
                                      Aucun mois encaissé par la famille {periode}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Stats globales : Mensuel, Trimestriel, Annuel */}
                    {totaux?.total_mensuel && totaux?.total_annuel && (
                      <div className="grid grid-cols-3 gap-4">
                        {/* Carte 1 : MENSUEL */}
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-5 h-5 text-blue-600" />

                              {/* ✅ Tooltip si radiés non payés */}
                              {totaux?.total_mensuel?.a_des_radies_non_payes && (
                                <div className="relative group">
                                  <AlertCircle className="w-3 h-3 text-blue-600 cursor-help" />

                                  <div className="absolute left-0 top-full mt-2 w-56 bg-white border-2 border-blue-200 rounded-lg shadow-xl p-2 hidden group-hover:block z-50">
                                    <div className="text-[10px] text-left text-gray-700">
                                      <p className="font-semibold text-blue-700 mb-1">
                                        ℹ️ Composition modifiée
                                      </p>
                                      <div className="space-y-1">
                                        <div className="flex justify-between text-gray-500">
                                          <span>Avant radiation :</span>
                                          <span className="line-through">{formatMontant(totaux.total_mensuel?.du)}</span>
                                        </div>
                                        <div className="flex justify-between font-semibold text-gray-800">
                                          <span>Élèves actifs :</span>
                                          <span>{formatMontant(totaux.total_mensuel?.du_actifs)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="absolute left-4 bottom-full w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-white"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <span className="text-xs font-medium text-blue-600">MOIS SÉLECTIONNÉ</span>
                          </div>

                          {/* ✅ Affichage avec montant barré si radiés non payés */}
                          {totaux?.total_mensuel?.a_des_radies_non_payes ? (
                            <div className="space-y-1">
                              <div className="text-lg text-blue-400 line-through">
                                {formatMontant(totaux.total_mensuel?.du)}
                              </div>
                              <div className="text-2xl font-bold text-blue-700">
                                {formatMontant(totaux.total_mensuel?.du_actifs)}
                              </div>
                            </div>
                          ) : (
                            <div className="text-2xl font-bold text-blue-700">
                              {formatMontant(totaux.total_mensuel?.du)}
                            </div>
                          )}

                          <div className="text-xs text-blue-600 mt-1">
                            {selectedMois
                              ? `Montant dû pour ${selectedMoisNom}`
                              : 'Sélectionnez un mois'}
                          </div>
                          {moisSelectionneRapport?.commentaire_composition && (
                            <div className="mt-2 rounded border border-blue-200 bg-white/70 p-2 text-[10px] leading-relaxed text-blue-800">
                              {moisSelectionneRapport.commentaire_composition}
                            </div>
                          )}
                        </div>

                        {/* Carte 2 : TRIMESTRIEL */}
                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-5 h-5 text-purple-600" />

                              {/* ✅ Tooltip si radiés non payés dans le trimestre */}
                              {totaux?.total_trimestriel?.a_des_radies_non_payes && (
                                <div className="relative group">
                                  <AlertCircle className="w-3 h-3 text-purple-600 cursor-help" />

                                  <div className="absolute left-0 top-full mt-2 w-56 bg-white border-2 border-purple-200 rounded-lg shadow-xl p-2 hidden group-hover:block z-50">
                                    <div className="text-[10px] text-left text-gray-700">
                                      <p className="font-semibold text-purple-700 mb-1">
                                        ℹ️ Composition modifiée
                                      </p>
                                      <div className="space-y-1">
                                        <div className="flex justify-between text-gray-500">
                                          <span>Avant radiation :</span>
                                          <span className="line-through">{formatMontant(totaux.total_trimestriel?.du)}</span>
                                        </div>
                                        <div className="flex justify-between font-semibold text-gray-800">
                                          <span>Élèves actifs :</span>
                                          <span>{formatMontant(totaux.total_trimestriel?.du_actifs)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="absolute left-4 bottom-full w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-white"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <span className="text-xs font-medium text-purple-600">TRIMESTRIEL</span>
                          </div>

                          {/* ✅ Affichage avec montant barré si radiés non payés */}
                          {totaux?.total_trimestriel?.a_des_radies_non_payes ? (
                            <div className="space-y-1">
                              <div className="text-lg text-purple-400 line-through">
                                {formatMontant(totaux.total_trimestriel?.du)}
                              </div>
                              <div className="text-2xl font-bold text-purple-700">
                                {formatMontant(totaux.total_trimestriel?.du_actifs)}
                              </div>
                            </div>
                          ) : (
                            <div className="text-2xl font-bold text-purple-700">
                              {formatMontant(totaux.total_trimestriel?.du)}
                            </div>
                          )}

                          <div className="text-xs text-purple-600 mt-1">
                            Trimestre {selectedTrimestre}
                          </div>
                        </div>

                        {/* Carte 3 : ANNUEL (Solde du compte) */}
                        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-5 h-5 text-indigo-600" />

                              {/* Tooltip si élèves radiés */}
                              {totaux?.total_annuel?.nb_eleves_radies > 0 && (
                                <div className="relative group">
                                  <AlertCircle className="w-3 h-3 text-indigo-500 cursor-help ml-1" />

                                  <div className="absolute left-0 top-full mt-2 w-72 bg-white border-2 border-indigo-200 rounded-lg shadow-xl p-3 hidden group-hover:block z-50">
                                    <div className="text-xs text-left text-gray-700">
                                      <p className="font-semibold text-indigo-700 mb-2">
                                        ℹ️ Calcul après radiation
                                      </p>
                                      <div className="space-y-2 text-[10px]">
                                        <div className="flex justify-between">
                                          <span>Total initial :</span>
                                          <span className="font-semibold">{formatMontant(totaux.total_annuel.du)}</span>
                                        </div>
                                        <div className="flex justify-between text-green-600">
                                          <span>Déjà payé :</span>
                                          <span className="font-semibold">-{formatMontant(totaux.total_annuel.paye)}</span>
                                        </div>
                                        <div className="h-px bg-gray-300"></div>
                                        <div className="flex justify-between font-semibold">
                                          <span>Reste (avant radiation) :</span>
                                          <span>{formatMontant(totaux.total_annuel.reste_avant_radiation)}</span>
                                        </div>
                                        <div className="flex justify-between text-red-600">
                                          <span>Radiation élève :</span>
                                          <span className="font-semibold">-{formatMontant(totaux?.total_annuel?.montant_radie_restant)}</span>
                                        </div>
                                        <div className="h-px bg-gray-300"></div>
                                        <div className="flex justify-between font-bold text-indigo-700">
                                          <span>Nouveau reste :</span>
                                          <span>{formatMontant(totaux.total_annuel.reste)}</span>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="absolute left-4 bottom-full w-0 h-0 border-l-6 border-r-6 border-b-6 border-l-transparent border-r-transparent border-b-indigo-200"></div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <span className="text-xs font-medium text-indigo-600">SOLDE ANNUEL</span>
                          </div>

                          {/* Affichage avec montant barré si radiation */}
                          {totaux?.total_annuel?.nb_eleves_radies > 0 ? (
                            <div className="space-y-1">
                              <div className="text-lg text-gray-400 line-through flex items-center gap-2">
                                {formatMontant(totaux?.total_annuel?.reste_avant_radiation)}
                                <span className="text-[10px] text-red-600 font-semibold bg-red-50 px-1.5 py-0.5 rounded">
                                  Avant radiation
                                </span>
                              </div>
                              <div className="text-2xl font-bold text-indigo-700">
                                {formatMontant(totaux.total_annuel.reste)}
                              </div>
                            </div>
                          ) : (
                            <div className="text-2xl font-bold text-indigo-700">
                              {formatMontant(totaux.total_annuel.reste)}
                            </div>
                          )}

                          <div className="text-xs text-indigo-600 mt-1">
                            Reste à encaisser sur l'année
                          </div>

                          {/* Détails additionnels */}
                          {(totaux?.total_annuel?.du > totaux?.total_annuel?.reste) && totaux?.total_annuel?.du && totaux?.total_annuel?.reste && (
                            <div className="mt-2 pt-2 border-t border-indigo-300 space-y-1">
                              <div className="flex justify-between text-[10px] text-gray-600">
                                <span>Total dû réel :</span>
                                <span className="font-semibold">{formatMontant(totaux.total_annuel.du)}</span>
                              </div>
                              <div className="flex justify-between text-[10px] text-green-600">
                                <span>Déjà encaissé :</span>
                                <span className="font-semibold">{formatMontant(totaux.total_annuel.paye)}</span>
                              </div>

                              {/* Ligne radiation si applicable */}
                              {totaux.total_annuel?.nb_eleves_radies > 0 && (
                                <div className="flex justify-between text-[10px] text-red-600">
                                  <span>Radiation :</span>
                                  <span className="font-semibold">-{formatMontant(totaux.total_annuel.montant_radie_restant)}</span>
                                </div>
                              )}
                            </div>
                          )}
                          {familleAvecRetrait && (
                            <div className="mt-2 rounded border border-indigo-200 bg-white/70 p-2 text-[10px] text-indigo-800 space-y-1">
                              <div className="font-semibold">Composition du total annuel</div>
                              <div className="flex justify-between">
                                <span>Mois historiques conserves :</span>
                                <span className="font-semibold">{formatMontant(totalAnnuelData.montant_historique_conserve || 0)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Mois futurs recalcules :</span>
                                <span className="font-semibold">{formatMontant(totalAnnuelData.montant_recalcule || 0)}</span>
                              </div>
                              <div className="h-px bg-indigo-200 my-1" />
                              <div className="flex justify-between">
                                <span>Total dû réel :</span>
                                <span className="font-semibold">{formatMontant(totalAnnuelDuReel)}</span>
                              </div>
                            </div>
                          )}
                        </div>


                      </div>
                    )}


                    {/* Stats par statut */}
                    {rapportParMois.length > 0 && (
                      <div className="grid grid-cols-4 gap-4">
                        <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                          <div className="text-sm text-green-600 font-medium mb-1">Mois payés</div>
                          <div className="text-2xl font-bold text-green-700">
                            {rapportParMois.filter(m => m.statut === 'Payé').length}
                          </div>
                        </div>
                        {/* 🆕 NOUVELLE CARTE : Payés au prorata */}
                        <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                          <div className="text-sm text-blue-600 font-medium mb-1">Payés prorata</div>
                          <div className="text-2xl font-bold text-blue-700">
                            {rapportParMois.filter(m => m.statut === 'Payé au prorata').length}
                          </div>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                          <div className="text-sm text-orange-600 font-medium mb-1">Paiements partiels</div>
                          <div className="text-2xl font-bold text-orange-700">
                            {rapportParMois.filter(m => m.statut === 'Partiel').length}
                          </div>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                          <div className="text-sm text-red-600 font-medium mb-1">Non payés</div>
                          <div className="text-2xl font-bold text-red-700">
                            {rapportParMois.filter(m => m.statut === 'Non payé').length}
                          </div>
                        </div>
                      </div>
                    )}



                    {/* 🆕 TABLEAU DÉTAILLÉ DES PAIEMENTS PAR MOIS */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b-2 border-gray-200">
                          <tr>
                            <th className="py-3 px-4 text-left font-semibold text-gray-700">Mois</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-700">Statut</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-700">Lecture comptable</th>
                            <th className="py-3 px-4 text-right font-semibold text-gray-700">Montant du mois</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-700">Enregistré par</th>
                            <th className="py-3 px-4 text-left font-semibold text-gray-700">Date & Heure</th>
                            <th className="py-3 px-4 text-center font-semibold text-gray-700">Reçu</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rapportParMois.map((mois) => {
                            const pourcentage = mois.total_du > 0
                              ? (mois.total_paye / mois.total_du) * 100
                              : 0;
                            const sorties = mois.eleves_sortis || mois.eleves_radies || [];
                            // Le bandeau « montant avant intégration » nomme l'élève et
                            // chiffre le montant : le badge « Historique conservé », qui
                            // dit la même chose en plus vague, ferait doublon. On ne le
                            // masque que s'il n'a rien d'autre à signaler — une sortie
                            // conservée sur ce mois reste un fait à part entière.
                            const badgeHistoriqueRedondant = mois.nb_avant_integration > 0
                              && sorties.length === 0;

                            return (
                              <tr
                                key={mois.mois_id}
                                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                              >
                                {/* COLONNE 1 : MOIS */}
                                <td className="py-3 px-4">
                                  <div className="flex items-start gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400 mt-0.5" />
                                    <div>
                                      <span className="font-medium text-gray-900">{mois.mois_nom}</span>
                                    </div>
                                  </div>
                                </td>



                                {/* COLONNE 2 : STATUT */}
                                <td className="py-3 px-4">
                                  <div className="flex flex-col items-start gap-1">
                                    <BadgeStatutMois statut={mois.statut} />

                                    {mois.statut === 'Payé au prorata' && mois.nb_payes_prorata > 0 && (
                                      <span className="text-[10px] text-blue-600">
                                        {mois.nb_payes_prorata} élève{mois.nb_payes_prorata > 1 ? 's' : ''}
                                      </span>
                                    )}

                                    {mois.statut === 'Non scolarisé' && mois.nb_eleves_non_scolarises > 0 && (
                                      <span className="text-[10px] text-gray-500">
                                        {mois.nb_eleves_non_scolarises} élève{mois.nb_eleves_non_scolarises > 1 ? 's' : ''}
                                      </span>
                                    )}

                                    {mois.statut === 'Partiel (Non scolarisé)' && mois.nb_eleves_non_scolarises > 0 && (
                                      <span className="text-[10px] text-gray-500">
                                        {mois.nb_eleves_non_scolarises} élève{mois.nb_eleves_non_scolarises > 1 ? 's' : ''} non scolarisé{mois.nb_eleves_non_scolarises > 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </td>

                                {/* COLONNE 3 : LECTURE COMPTABLE */}
                                <td className="py-3 px-4 min-w-[260px]">
                                  {/* Le montant retenu est inferieur au tarif initial : une part a ete
                                      rendue non recouvrable par une radiation. Sa place est ici, dans la
                                      lecture comptable, et non dans la colonne des montants trop etroite. */}
                                  {mois.montant_non_recouvre > 0 && (
                                    <div className="mb-2 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                      <UserMinus className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                                      <div className="min-w-0 text-[11px] leading-snug text-gray-600">
                                        Tarif initial <span className="font-semibold text-gray-800">{formatMontant(mois.total_du_historique)}</span>
                                        {' · '}
                                        <span className="font-semibold text-gray-800">{formatMontant(mois.montant_non_recouvre)}</span> non recouvré
                                        {mois.nb_eleves_radies > 0 && (
                                          <> — {mois.nb_eleves_radies} élève{mois.nb_eleves_radies > 1 ? 's' : ''} radié{mois.nb_eleves_radies > 1 ? 's' : ''}</>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Le prorata, dit pour ce qu'il est.
                                      Sans ce bloc, ces lignes tombaient dans
                                      « valeur d'alors » et l'écran expliquait un
                                      montant réduit par un changement de réduction
                                      qui n'avait jamais eu lieu. Et surtout : un
                                      élève au prorata est SOLDÉ, pas en retard. */}
                                  {(mois.lignes_prorata || []).length > 0 && (
                                    <div className="mb-2 flex items-start gap-2 rounded-lg border border-[#cfe3ec] bg-[#eef7fb] px-3 py-2">
                                      <Calculator className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#246990]" />
                                      <div className="min-w-0 flex-1 text-[11px] leading-snug text-gray-700">
                                        {/* Le titre ne doit rien affirmer sur le
                                            paiement : choisir le prorata a
                                            l'inscription fixe ce qui est DU, cela
                                            n'encaisse rien. Dire « a payé au
                                            prorata » quand personne n'a rien versé
                                            faisait croire le mois réglé. */}
                                        <span className="font-semibold text-[#246990]">
                                          {mois.lignes_prorata.length === 1
                                            ? 'Un élève au prorata'
                                            : `${mois.lignes_prorata.length} élèves au prorata`}
                                        </span>
                                        {mois.lignes_prorata.map((ligne) => (
                                          <span key={ligne.eleve_id} className="block mt-1">
                                            <span className="font-medium text-gray-800">{ligne.eleve}</span>
                                            {ligne.solde ? ' a payé ' : ' doit '}
                                            <span className="font-semibold">{formatMontant(ligne.montant_prorata)}</span>
                                            {ligne.montant_initial > ligne.montant_prorata && ` au lieu de ${formatMontant(ligne.montant_initial)}`}
                                            {ligne.depuis && `, arrivé le ${ligne.depuis}`}
                                            {' — '}
                                            {ligne.solde
                                              ? <span className="font-medium text-emerald-700">sa situation est soldée, pas partielle</span>
                                              : <span className="text-gray-600">reste à régler</span>}
                                            .
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Un élève non scolarisé ce mois-là ne compte pas dans le
                                      total, et c'est la moitié de l'explication quand un mois
                                      est plus bas que le montant habituel de la famille.
                                      Le compte existait déjà mais ne s'affichait que si le
                                      mois entier était non scolarisé — donc jamais dans le cas
                                      qui pose question, celui d'un seul élève absent. */}
                                  {mois.nb_eleves_non_scolarises > 0 && mois.statut !== 'Non scolarisé' && (
                                    <div className="mb-2 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                      <UserMinus className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                                      <div className="min-w-0 flex-1 text-[11px] leading-snug text-gray-600">
                                        <span className="font-semibold text-gray-800">
                                          {mois.nb_eleves_non_scolarises === 1
                                            ? 'Un élève non scolarisé ce mois-là'
                                            : `${mois.nb_eleves_non_scolarises} élèves non scolarisés ce mois-là`}
                                        </span>
                                        {(mois.eleves_non_scolarises || []).slice(0, 3).map((eleve) => (
                                          <span key={eleve.eleve_id} className="block mt-1">
                                            <span className="font-medium text-gray-800">{eleve.eleve}</span>
                                            {' n\'était pas encore à l\'école : il ne compte pas dans ce mois'}
                                            {eleve.montant_regle_actuelle > 0
                                              && `, alors qu'il y compterait pour ${formatMontant(eleve.montant_regle_actuelle)} aujourd'hui`}.
                                          </span>
                                        ))}
                                        {(mois.eleves_non_scolarises || []).length > 3 && (
                                          <span className="block mt-1">
                                            + {(mois.eleves_non_scolarises || []).length - 3} autre(s)
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {mois.nb_avant_integration > 0 && (() => {
                                    // La base de l'opération est le total des lignes du mois,
                                    // pas le montant mensuel habituel de la famille. Les deux
                                    // diffèrent dès qu'une ligne est figée à un ancien tarif :
                                    // ici la ligne de l'élève vaut son tarif d'avant l'entrée.
                                    // Reconstituer la base depuis les deux valeurs du rapport
                                    // garantit que l'égalité affichée est toujours vraie.
                                    const totalLignes = Math.round(
                                      (mois.total_du || 0) + (mois.montant_avant_integration || 0)
                                    );
                                    const nombre = (valeur) => Math.round(valeur || 0).toLocaleString('fr-FR');
                                    const noms = (mois.eleves_avant_integration || [])
                                      .map((eleve) => eleve.nom)
                                      .join(', ');
                                    const pluriel = mois.nb_avant_integration > 1;
                                    const nomMois = String(mois.mois_nom || '').toLowerCase();
                                    // « du mois d'octobre » mais « du mois de novembre ».
                                    const articleMois = /^[aeiouyàâéèêëîïôöûü]/.test(nomMois) ? "d'" : 'de ';

                                    return (
                                      <div className="mb-2 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                        <History className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                                        {/* Un décompte plutôt qu'une phrase : chaque montant
                                            porte son libellé sur sa ligne. La version en prose
                                            énonçait la soustraction puis la reformulait, ce qui
                                            donnait l'impression de retrancher deux fois. */}
                                        <div className="min-w-0 flex-1 text-[11px] leading-snug text-gray-600">
                                          <div className="flex justify-between gap-3">
                                            <span>Total des lignes du mois</span>
                                            <span className="font-medium text-gray-800">{nombre(totalLignes)}</span>
                                          </div>
                                          <div className="flex justify-between gap-3">
                                            <span className="min-w-0">
                                              Réglé{pluriel ? 's' : ''} avant intégration — {noms}
                                            </span>
                                            <span className="whitespace-nowrap font-medium text-gray-800">
                                              − {nombre(mois.montant_avant_integration)}
                                            </span>
                                          </div>
                                          <div className="mt-1 flex justify-between gap-3 border-t border-gray-300 pt-1">
                                            <span className="font-semibold text-gray-800">Dû par la famille</span>
                                            <span className="whitespace-nowrap font-semibold text-gray-900">
                                              {formatMontant(mois.total_du)}
                                            </span>
                                          </div>
                                          <div className="mt-1 text-gray-500">
                                            {pluriel ? 'Les versements de ' : 'Le versement de '}
                                            <span className="font-medium text-gray-700">{noms}</span>
                                            {pluriel ? ', réglés avant leur intégration, n\'appartiennent' : ', réglé avant son intégration, n\'appartient'}
                                            {' '}pas à la famille : {pluriel ? 'ils ne comptent' : 'il ne compte'} pas
                                            dans les totaux du mois {articleMois}{nomMois}.
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {(mois.composition_historique && !badgeHistoriqueRedondant)
                                    || mois.composition_recalculee
                                    || (mois.commentaire_composition && !badgeHistoriqueRedondant) ? (
                                    <div className="w-full max-w-[320px]">
                                      <div className="grid grid-cols-[minmax(0,1fr)_1.25rem] items-start gap-2">
                                        <div className="flex flex-wrap gap-1.5">
                                          {mois.composition_historique && !badgeHistoriqueRedondant && (
                                            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-semibold text-amber-700">
                                              Historique conservé
                                            </span>
                                          )}
                                          {mois.composition_recalculee && (
                                            <span className="inline-flex items-center rounded-full border border-[#246990]/20 bg-[#eef7fb] px-2 py-1 text-[11px] font-semibold text-[#246990]">
                                              Recalculé après changement
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex justify-end pt-0.5">
                                          {(mois.composition_historique || mois.composition_recalculee || sorties.length > 0) && (
                                            <InfoTooltip position="right" width="w-80">
                                              <span className="block space-y-2">
                                                <span className="block font-semibold text-[#246990]">
                                                  {mois.composition_historique ? 'Mois conservé avant changement' : 'Mois recalculé après changement'}
                                                </span>
                                                <span className="block">
                                                  {mois.composition_historique
                                                    ? 'Ce mois garde son montant historique, car le paiement était déjà engagé ou se situe avant le mois d effet du changement de composition.'
                                                    : 'Ce mois est recalculé avec la composition validée de la famille à partir du mois d effet.'}
                                                </span>
                                                {sorties.length > 0 && (
                                                  <span className="block border-t border-gray-100 pt-2 space-y-1">
                                                    <span className="block font-semibold text-gray-600">Élève(s) sorti(s) conservé(s) dans l historique :</span>
                                                    {sorties.map((eleve, idx) => (
                                                      <span key={idx} className="flex justify-between gap-3">
                                                        <span>{eleve.nom}</span>
                                                        <span className="font-semibold">{formatMontant(eleve.montant)}</span>
                                                      </span>
                                                    ))}
                                                  </span>
                                                )}
                                                <span className="border-t border-gray-100 pt-2 flex justify-between">
                                                  <span>Total du mois :</span>
                                                  <span className="font-semibold">{formatMontant(mois.total_du)}</span>
                                                </span>
                                              </span>
                                            </InfoTooltip>
                                          )}
                                        </div>
                                      </div>
                                      {mois.commentaire_composition && !badgeHistoriqueRedondant && (
                                        <span className="mt-1 block max-w-[260px] text-[11px] leading-snug text-gray-500">
                                          {mois.commentaire_composition}
                                        </span>
                                      )}
                                    </div>
                                  ) : mois.nb_lignes_valeur_anterieure > 0 ? (() => {
                                    /* Une phrase, pas un relevé. Le gestionnaire ne se
                                       demande pas « quelles lignes divergent », il se
                                       demande « pourquoi ce mois n'est pas au même montant
                                       que les autres ». On répond à cette question-là. */
                                    const lignes = mois.lignes_valeur_anterieure || [];
                                    const toutesReglees = lignes.every((ligne) => ligne.engage);
                                    const pluriel = lignes.length > 1;

                                    return (
                                      <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2">
                                        <History className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-orange-600" />
                                        <div className="min-w-0 flex-1 text-[11px] leading-snug text-orange-900">
                                          <span className="font-semibold">
                                            {toutesReglees
                                              ? 'Ce mois a été réglé avant un changement de réduction'
                                              : 'Ce mois porte encore un montant antérieur'}
                                          </span>

                                          <span className="block mt-1 text-orange-800">
                                            Il est à {formatMontant(mois.total_du)} parce que{' '}
                                            {lignes.slice(0, 2).map((ligne, index) => (
                                              <React.Fragment key={ligne.eleve_id}>
                                                {index > 0 && ' et '}
                                                <span className="font-semibold">{ligne.eleve}</span>
                                                {' y compte pour '}
                                                <span className="font-semibold">{formatMontant(ligne.montant_porte)}</span>
                                              </React.Fragment>
                                            ))}
                                            {lignes.length > 2 && `, et ${lignes.length - 2} autre${lignes.length > 3 ? 's' : ''} élève${lignes.length > 3 ? 's' : ''}`}
                                            {' — le montant qui s\'appliquait quand ce mois a été engagé.'}
                                          </span>

                                          {/* « Ce mois vaudrait X » se confondait avec le total
                                              de la famille, qui compte tous les membres. Ici on
                                              ne compte que les élèves présents ce mois-là : un
                                              absent n'y figure pas. Nommer la référence évite
                                              de comparer deux nombres proches qui ne mesurent
                                              pas la même chose. */}
                                          <span className="block mt-1 text-orange-800">
                                            Avec la réduction d'aujourd'hui, {pluriel ? 'ils compteraient' : 'il compterait'} pour{' '}
                                            {lignes.slice(0, 2).map((ligne) => formatMontant(ligne.montant_regle_actuelle)).join(' et ')},
                                            {' '}et ce mois vaudrait{' '}
                                            <span className="font-semibold">{formatMontant(mois.montant_selon_regle_actuelle)}</span>
                                            {' pour les élèves présents ce mois-là'}
                                            {mois.nb_eleves_non_scolarises > 0
                                              && ` — le total de la famille, lui, compte aussi ${mois.nb_eleves_non_scolarises === 1 ? "l'élève absent" : `les ${mois.nb_eleves_non_scolarises} élèves absents`}`}.
                                          </span>

                                          <span className="block mt-1 text-orange-700">
                                            {toutesReglees
                                              ? "Un mois déjà encaissé n'est pas recalculé : la facture reste celle qui a été payée."
                                              : "Ce montant suivra la règle actuelle dès la prochaine mise à jour de la famille."}
                                            {' Le détail des changements est dans « Valeur de famille », en haut de cette fiche.'}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })() : !(mois.montant_non_recouvre > 0) && !(mois.nb_avant_integration > 0)
                                    && !(mois.nb_eleves_non_scolarises > 0)
                                    && !((mois.lignes_prorata || []).length > 0) && (
                                    <span className="text-gray-400 text-xs italic">Standard</span>
                                  )}
                                </td>



                                {/* COLONNE 4 : MONTANT */}
                                <td className="py-3 px-4 text-right">
                                  {(() => {
                                    const estPaye = String(mois.statut || '').toLowerCase().startsWith('pay');
                                    const estPartiel = String(mois.statut || '').toLowerCase().includes('partiel');
                                    const montantPrincipal = estPaye ? mois.total_paye : mois.total_du;

                                    return (
                                      <div className="flex justify-end">
                                        <div className="space-y-1 text-right">
                                          {estPartiel ? (
                                            <>
                                              <div className="font-semibold text-orange-700">
                                                {formatMontant(mois.total_paye)} / {formatMontant(mois.total_du)}
                                              </div>
                                              <div className="w-24 bg-gray-200 rounded-full h-1.5 ml-auto">
                                                <div
                                                  className="bg-orange-500 h-1.5 rounded-full transition-all"
                                                  style={{ width: `${mois.total_du > 0 ? pourcentage : 0}%` }}
                                                />
                                              </div>
                                            </>
                                          ) : (
                                            <div className={`font-semibold ${estPaye ? 'text-green-700' : 'text-red-700'}`}>
                                              {formatMontant(montantPrincipal)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </td>



                                {/* COLONNE 4 : PAYÉ PAR */}
                                <td className="py-3 px-4">
                                  {mois.paye_par ? (
                                    // Pas d'icone : l'en-tete de colonne porte deja le sens, et le nom
                                    // s'enroule sur plusieurs lignes — la pastille y paraissait perdue.
                                    <span className="text-xs leading-snug text-gray-700">{mois.paye_par}</span>
                                  ) : (
                                    <span className="text-gray-400 text-xs italic">-</span>
                                  )}
                                </td>

                                {/* COLONNE 5 : DATE & HEURE */}
                                <td className="py-3 px-4">
                                  {mois.date_paiement_complete ? (
                                    <div className="text-xs space-y-0.5">
                                      <div className="text-gray-700 font-medium">
                                        {new Date(mois.date_paiement_complete).toLocaleDateString('fr-FR', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric'
                                        })}
                                      </div>
                                      <div className="text-gray-500">
                                        {new Date(mois.date_paiement_complete).toLocaleTimeString('fr-FR', {
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </div>
                                    </div>
                                  ) : mois.date_paiement ? (
                                    <div className="text-xs text-gray-700">
                                      {new Date(mois.date_paiement).toLocaleDateString('fr-FR', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                      })}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400 text-xs italic">-</span>
                                  )}
                                </td>

                                {/* COLONNE 6 : BOUTON TÉLÉCHARGER REÇU */}
                                <td className="py-3 px-4 text-center">
                                  {(mois.statut === 'Payé' || mois.statut === 'Payé au prorata') ? (
                                    <button
                                      onClick={() => handleTelechargerRecu(mois)}
                                      className="inline-flex items-center px-3 py-1.5 bg-[#246990] text-white text-xs font-medium rounded-lg hover:bg-[#1f5c7d] transition-colors"
                                    >
                                      <Download className="w-3 h-3 mr-1" />
                                      Télécharger
                                    </button>

                                  ) : (
                                    <span className="text-gray-400 text-xs italic">-</span>
                                  )}
                                </td>


                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>


            </div>




            {/* Colonne droite - Gestion du paiement */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm sticky top-6">
                <div className="p-4 bg-[#eef7fb] border-b border-[#246990]/20">
                  <h2 className="font-medium text-[#246990] text-lg flex items-center">
                    Gestion du paiement
                  </h2>
                </div>

                <div className="p-4 space-y-4">

                  {/* Type de paiement */}
                  {/* Section Type de paiement */}
                  <div className="space-y-3">
                    {/* Option 1 : Solder le compte annuel */}
                    <div
                      onClick={() => setTypePaiement('solde_annuel')}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${typePaiement === 'solde_annuel'
                          ? 'border-[#ae7467] bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      {/* Radio custom */}
                      <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${typePaiement === 'solde_annuel'
                          ? 'border-[#ae7467] bg-white'
                          : 'border-gray-300 bg-white'
                        }`}>
                        {typePaiement === 'solde_annuel' && (
                          <div className="w-3 h-3 bg-[#ae7467] rounded-full"></div>
                        )}
                      </div>

                      {/* Contenu existant */}
                      <div className="flex-1">
                        <div className="grid grid-cols-[minmax(0,1fr)_118px] items-start gap-3">
                          <div className="font-medium text-sm flex items-center gap-2">
                            <span>Solder le compte annuel</span>

                            {totaux?.total_annuel?.nb_eleves_radies > 0 && (
                              <div className="relative group">
                                <AlertCircle className="w-3 h-3 text-blue-500 cursor-help" />

                                <div className="absolute right-0 bottom-full mb-2 w-64 bg-white border-2 border-blue-200 rounded-lg shadow-xl p-2 hidden group-hover:block z-50">
                                  <div className="text-[10px] text-left text-gray-700">
                                    <p className="font-semibold text-blue-700 mb-1">
                                      ℹ️ Montant après radiation
                                    </p>
                                    <div className="space-y-1">

                                      <div className="flex justify-between">
                                        <span>Avant radiation :</span>
                                        <span className="line-through">{formatMontant(totaux?.total_annuel?.reste_avant_radiation)}</span>  {/* ✅ CORRIGÉ */}
                                      </div>


                                      <div className="flex justify-between text-red-600">
                                        <span>Radiation :</span>
                                        <span className="font-semibold">-{formatMontant(totaux.total_annuel.montant_radie_restant)}</span>
                                      </div>
                                      <div className="h-px bg-gray-300"></div>
                                      <div className="flex justify-between font-bold text-green-700">
                                        <span>À payer :</span>
                                        <span>{formatMontant(totaux?.total_annuel?.reste || 0)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="absolute right-4 top-full w-0 h-0 border-l-6 border-r-6 border-t-6 border-l-transparent border-r-transparent border-t-blue-200"></div>
                                </div>
                              </div>
                            )}
                          </div>

                          <span className="inline-flex min-w-[118px] justify-center text-xs bg-red-100 text-[#ae7467] px-2 py-0.5 rounded">
                            TOTAL ANNÉE
                          </span>
                        </div>

                        <div className="text-xs text-gray-600 mt-1">
                          Payer tout ce qui reste à payer pour l'année complète
                        </div>

                        {totaux?.total_annuel?.nb_eleves_radies > 0 ? (
                          <div className="mt-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-gray-400 line-through">
                                {formatMontant(totaux.total_annuel.reste_avant_radiation)}
                              </div>
                              <span className="text-[9px] bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-semibold">
                                Avant radiation
                              </span>
                            </div>
                            <div className="text-xl font-bold text-red-600">
                              {formatMontant(totaux?.total_annuel?.reste || 0)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xl font-bold text-red-600 mt-2">
                            {formatMontant(totaux?.total_annuel?.reste || 0)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Option 2 : Payer le total des mois sélectionnés */}
                    <div
                      onClick={() => setTypePaiement('mois_selectionnes')}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${typePaiement === 'mois_selectionnes'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      {/* Radio custom */}
                      <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${typePaiement === 'mois_selectionnes'
                          ? 'border-blue-500 bg-white'
                          : 'border-gray-300 bg-white'
                        }`}>
                        {typePaiement === 'mois_selectionnes' && (
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        )}
                      </div>

                      {/* Contenu */}
                      <div className="flex-1">
                        <div className="grid grid-cols-[minmax(0,1fr)_118px] items-start gap-3">
                          <span className="font-medium text-sm">Payer le total des mois sélectionnés</span>
                          <span className="inline-flex min-w-[118px] justify-center text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                            AUTO
                          </span>
                        </div>

                        <div className="text-xs text-gray-600 mt-1">
                          Le montant sera calculé automatiquement selon les mois cochés ci-dessous
                        </div>

                        {/* 🆕 AFFICHAGE DU MONTANT CALCULÉ */}
                        {moisSelectionnes.length > 0 ? (
                          <div className="mt-2 p-2 bg-blue-100 rounded border border-blue-300">
                            <div className="text-xs text-blue-700 mb-1">
                              {moisSelectionnes.length} mois sélectionné{moisSelectionnes.length > 1 ? 's' : ''} :
                            </div>
                            <div className="text-lg font-bold text-blue-800">
                              {formatMontant(
                                moisSelectionnes.reduce((total, moisId) => {
                                  const moisInfo = rapportParMois.find(m => m.mois_id === moisId);
                                  if (moisInfo) {
                                    return total + (moisInfo.total_du - moisInfo.total_paye);
                                  }
                                  return total;
                                }, 0)
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 flex items-start gap-1 text-xs text-orange-600">
                            <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <span>Cochez des mois ci-dessous pour voir le montant</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Option 3 : Paiement partiel */}
                    <div
                      onClick={() => setTypePaiement('partiel')}
                      className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${typePaiement === 'partiel'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      {/* Radio custom */}
                      <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${typePaiement === 'partiel'
                          ? 'border-orange-500 bg-white'
                          : 'border-gray-300 bg-white'
                        }`}>
                        {typePaiement === 'partiel' && (
                          <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        )}
                      </div>

                      {/* Contenu existant */}
                      <div className="flex-1">
                        <div className="grid grid-cols-[minmax(0,1fr)_118px] items-start gap-3">
                          <span className="font-medium text-sm">Paiement partiel</span>
                          <span className="inline-flex min-w-[118px] justify-center text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                            MONTANT LIBRE
                          </span>
                        </div>

                        <div className="text-xs text-gray-600 mt-1">
                          Choisir le montant à payer (sélectionnez les mois concernés ci-dessous)
                        </div>
                      </div>
                    </div>
                  </div>



                  {/* Montant partiel */}
                  {/* Montant partiel */}
                  {typePaiement === 'partiel' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Montant à payer
                      </label>
                      <input
                        type="number"
                        value={montantPaiement}
                        onChange={(e) => {
                          const value = e.target.value;
                          // 🆕 Accepter seulement des entiers (pas de décimales)
                          if (value === '' || /^\d+$/.test(value)) {
                            setMontantPaiement(value);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent"
                        placeholder="Entrez le montant (entier uniquement)"
                        min="0"
                        step="1"
                        max={statsGlobales?.reste_a_payer}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        Maximum : {formatMontant(totaux?.total_annuel?.reste || statsGlobales?.reste_a_payer || 0)}
                      </div>
                      {/* 🆕 AVERTISSEMENT si montant avec décimales */}
                      {montantPaiement && !Number.isInteger(parseFloat(montantPaiement)) && (
                        <div className="text-xs text-orange-600 mt-1 flex items-center">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Le montant sera arrondi à {Math.round(parseFloat(montantPaiement))} FCFA
                        </div>
                      )}
                    </div>
                  )}

                  {/* Répartition si paiement partiel */}
                  {/* Répartition si paiement partiel */}
                  {typePaiement === 'partiel' && montantPaiement > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="text-xs font-medium text-blue-700 mb-2">
                        Répartition proportionnelle :
                      </div>
                      <div className="space-y-1">
                        {calculerRepartition().map((eleve) => (
                          <div key={eleve.id} className="flex justify-between text-xs">
                            <span className="text-blue-600">{eleve.prenom} :</span>
                            <span className="font-medium text-blue-700">
                              {formatMontant(eleve.montant_attribue)}
                            </span>
                          </div>
                        ))}
                      </div>
                      {/* 🆕 TOTAL DE VÉRIFICATION */}
                      <div className="mt-2 pt-2 border-t border-blue-300 flex justify-between text-xs font-bold">
                        <span className="text-blue-700">TOTAL :</span>
                        <span className="text-blue-800">
                          {formatMontant(
                            calculerRepartition().reduce((sum, e) => sum + e.montant_attribue, 0)
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Mode de paiement */}
                  {/* Mode de paiement */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mode de paiement <span className="text-red-500">*</span>
                    </label>

                    <div className="relative">
                      <Search className="absolute left-3 top-3.5 h-4 w-4 text-gray-400 pointer-events-none" />

                      <input
                        type="text"
                        placeholder="Rechercher un mode de paiement..."
                        value={isModePaiementDropdownOpen
                          ? (modePaiementSearchTerm || '')
                          : (modesPaiement.find(m => m.id.toString() === String(modePaiement))?.nom || '')}
                        onChange={(e) => {
                          setModePaiementSearchTerm(e.target.value);
                          setIsModePaiementDropdownOpen(true);
                        }}
                        onFocus={() => {
                          setModePaiementSearchTerm('');
                          setIsModePaiementDropdownOpen(true);
                        }}
                        disabled={loading}
                        className="w-full pl-10 pr-10 py-3 text-base font-medium border-2 border-gray-200 rounded-lg 
                bg-white hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10 
                outline-none transition-all
                disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                      />

                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* ✅ Dropdown vers le BAS */}
                      {isModePaiementDropdownOpen && (
                        <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                          {(() => {
                            const filteredModes = modesPaiement.filter(mode =>
                              mode.nom.toLowerCase().includes((modePaiementSearchTerm || '').toLowerCase())
                            );

                            if (filteredModes.length === 0) {
                              return (
                                <div className="p-4 text-center text-gray-500 text-sm">
                                  Aucun mode de paiement trouvé
                                </div>
                              );
                            }

                            return filteredModes.map((mode) => (
                              <div
                                key={mode.id}
                                onClick={() => {
                                  setModePaiement(mode.id.toString());
                                  setModePaiementSearchTerm('');
                                  setIsModePaiementDropdownOpen(false);
                                }}
                                className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 transition-colors"
                              >
                                <div className="font-medium text-gray-900 text-base">
                                  {mode.nom}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                    </div>
                  </div>


                  {/* Mois concernés */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mois concernés par le paiement <span className="text-red-500">*</span>
                    </label>
                    <div className="text-xs text-blue-600 mb-2 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Cochez les mois que vous souhaitez payer (vous pouvez en sélectionner plusieurs)
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                      {rapportParMois.map((mois) => {
                        const badge = getMoisStatutBadge(mois.mois_id);
                        const isChecked = moisSelectionnes.includes(mois.mois_id);

                        return (
                          <div
                            key={mois.mois_id}
                            onClick={() => !badge?.disabled && toggleMois(mois.mois_id)}
                            className={`flex items-center justify-between p-2 rounded border transition-colors ${badge?.disabled
                                ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                                : 'hover:bg-gray-50 border-gray-200 cursor-pointer'
                              }`}
                          >
                            <div className="flex items-center flex-1">
                              {/* ✅ Checkbox custom */}
                              <div className={`mr-2 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${badge?.disabled
                                  ? 'border-gray-300 bg-gray-100'
                                  : isChecked
                                    ? 'border-[#246990] bg-[#246990]'
                                    : 'border-gray-300 bg-white'
                                }`}>
                                {isChecked && !badge?.disabled && (
                                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>

                              <span className={`text-sm ${badge?.disabled ? 'text-gray-500' : 'text-gray-700'}`}>
                                {mois.mois_nom}
                              </span>
                            </div>

                            {badge && (
                              <span className={`text-xs px-2 py-1 rounded border ${badge.class}`}>
                                {badge.text}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Observations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observations (optionnel)
                    </label>
                    <textarea
                      value={observations}
                      onChange={(e) => setObservations(e.target.value)}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#246990] focus:border-transparent resize-none"
                      placeholder="Ajoutez une note..."
                    />
                  </div>

                  {/* Boutons d'action */}
                  <div className="flex space-x-3">
                    <button
                      onClick={handlePaiement}
                      disabled={loadingPaiement || !modePaiement || moisSelectionnes.length === 0}
                      className="flex-1 bg-[#246990] text-white px-4 py-2.5 rounded-lg hover:bg-[#1d5475] transition-colors flex items-center justify-center disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {loadingPaiement ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Traitement en cours...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Valider le paiement
                        </>
                      )}
                    </button>
                    <button
                      onClick={resetPaiementForm}
                      disabled={loadingPaiement}
                      className="px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Réinitialiser"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Message d'info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <AlertCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-blue-700">
                        Le montant sera réparti proportionnellement entre les élèves selon leurs montants dus.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>



          </div>
            </>
          ) : (
            <div className="space-y-6 mb-6">
              {(() => {
                const annexeData = annexesFamille[activeCategoriePaiement];
                const statsAnnexe = annexeData?.stats_globales || {};
                const rapportAnnexe = annexeData?.rapport_par_mois || [];
                const elevesConcernes = annexeData?.eleves_concernes || [];
                const elevesNonConcernes = annexeData?.eleves_non_concernes || [];
                const totalSelectionne = getTotalAnnexeSelectionne(annexeData);
                const montantAEncaisser = typePaiement === 'partiel'
                  ? Math.round(Number(montantPaiement || 0))
                  : totalSelectionne;
                const paiementsPonctuels = annexesPonctuelsFamille[activeCategoriePaiement] || [];
                const elevesPonctuelsDisponibles = getElevesPonctuelsDisponibles(annexeData);
                const montantUnitairePonctuel = Math.round(Number(String(paiementPonctuelFamille.montant || '').replace(/[^0-9]/g, '')));
                const totalPonctuelFamille = getTotalPonctuelFamille();

                if (loadingAnnexes && !annexeData) {
                  return (
                    <div className="rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
                      <Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin text-[#246990]" />
                      <p className="text-sm font-semibold text-gray-700">Chargement des frais {categoriePaiementActive.label.toLowerCase()}...</p>
                    </div>
                  );
                }

                if (!annexeData) {
                  return (
                    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                      <p className="text-sm text-gray-600">Selectionnez cette categorie pour charger les frais annexes de la famille.</p>
                    </div>
                  );
                }

                if (!statsAnnexe.has_configured_fees && elevesPonctuelsDisponibles.length === 0) {
                  return (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
                        <div>
                          <h3 className="font-semibold text-amber-900">Aucun frais de {categoriePaiementActive.label.toLowerCase()} configure</h3>
                          <p className="mt-1 text-sm text-amber-800">
                            Aucune classe des eleves actifs de cette famille n'a un montant de {categoriePaiementActive.label.toLowerCase()} configure.
                            Configurez ces frais dans Classes & Matieres pour activer le paiement groupe.
                          </p>
                          {elevesNonConcernes.length > 0 && (
                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                              {elevesNonConcernes.map((eleve) => (
                                <div key={eleve.id} className="rounded border border-amber-100 bg-white px-3 py-2 text-xs text-gray-700">
                                  <span className="font-semibold">{eleve.prenom} {eleve.nom}</span>
                                  <span className="ml-2 rounded bg-gray-100 px-2 py-0.5 text-gray-600">{eleve.classe_nom || 'Classe non renseignee'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <>
                    <div className="rounded-lg border border-[#246990]/20 bg-white shadow-sm overflow-hidden">
                      <div className="border-b border-gray-200 bg-[#eef7fb] px-4 py-3 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990] text-white">
                            {React.createElement(categoriePaiementActive.icon, { className: 'h-5 w-5' })}
                          </span>
                          <div>
                            <h3 className="font-semibold text-gray-900">{categoriePaiementActive.label} famille</h3>
                            <p className="text-xs text-gray-600">Frais annexes separes de la scolarite. Les reductions famille ne sont pas appliquees ici.</p>
                          </div>
                        </div>
                        <span className="rounded-full border border-[#246990]/20 bg-white px-3 py-1 text-xs font-semibold text-[#246990]">
                          {statsAnnexe.nb_eleves_concernes} eleve(s) concerne(s)
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                          <p className="text-xs font-semibold uppercase text-gray-500">Total attendu</p>
                          <p className="mt-1 text-xl font-bold text-gray-900">{formatMontant(statsAnnexe.total_du)}</p>
                        </div>
                        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                          <p className="text-xs font-semibold uppercase text-green-700">Deja paye</p>
                          <p className="mt-1 text-xl font-bold text-green-700">{formatMontant(statsAnnexe.total_paye)}</p>
                        </div>
                        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                          <p className="text-xs font-semibold uppercase text-red-700">Reste a payer</p>
                          <p className="mt-1 text-xl font-bold text-red-700">{formatMontant(statsAnnexe.reste_a_payer)}</p>
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                          <p className="text-xs font-semibold uppercase text-blue-700">Selection actuelle</p>
                          <p className="mt-1 text-xl font-bold text-blue-700">{formatMontant(totalSelectionne)}</p>
                        </div>
                      </div>
                    </div>

                    {!statsAnnexe.has_configured_fees && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                        <p className="font-semibold text-amber-900">Aucun frais mensuel de {categoriePaiementActive.label.toLowerCase()} configure</p>
                        <p className="mt-1">
                          Le paiement groupe mensuel est indisponible pour cette categorie, mais vous pouvez toujours encaisser un ticket ponctuel pour une date precise.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <div className="lg:col-span-2 space-y-6">
                        <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-sm font-semibold text-gray-900">Eleves concernes</h3>
                            <p className="text-xs text-gray-500">Seuls les eleves dont la classe a un frais configure sont inclus.</p>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {elevesConcernes.map((eleve) => (
                              <div key={eleve.id} className="grid grid-cols-1 md:grid-cols-[1fr_140px_150px] gap-3 px-4 py-3 text-sm items-center">
                                <div>
                                  <p className="font-semibold text-gray-900">{eleve.prenom} {eleve.nom}</p>
                                  <p className="text-xs text-gray-500">{eleve.matricule || 'Matricule non renseigne'}</p>
                                </div>
                                <span className="justify-self-start md:justify-self-center rounded-md bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-100">
                                  {eleve.classe_nom || 'Classe non renseignee'}
                                </span>
                                <span className="justify-self-start md:justify-self-end font-bold text-[#246990]">
                                  {formatMontant(eleve.montant_annexe)} / mois
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-sm font-semibold text-gray-900">Mois a payer</h3>
                            <p className="text-xs text-gray-500">Cochez les mois a encaisser pour toute la famille concernee.</p>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-4 max-h-72 overflow-y-auto">
                            {rapportAnnexe.map((mois) => {
                              const disabled = Number(mois.reste_a_payer || 0) <= 0;
                              const checked = moisSelectionnes.includes(mois.mois_id);
                              return (
                                <button
                                  key={mois.mois_id}
                                  type="button"
                                  disabled={disabled}
                                  onClick={() => {
                                    setMoisSelectionnes((prev) => checked
                                      ? prev.filter((id) => id !== mois.mois_id)
                                      : [...prev, mois.mois_id]
                                    );
                                  }}
                                  className={`text-left rounded-lg border p-3 transition ${disabled
                                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                      : checked
                                        ? 'border-[#246990] bg-[#eef7fb] text-[#246990] shadow-sm'
                                        : 'border-gray-200 bg-white hover:border-[#246990]/40 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-sm">{mois.mois_nom}</span>
                                    <span className={`h-5 w-5 rounded border flex items-center justify-center ${checked && !disabled ? 'bg-[#246990] border-[#246990]' : 'bg-white border-gray-300'}`}>
                                      {checked && !disabled && <Check className="h-3 w-3 text-white" />}
                                    </span>
                                  </div>
                                  <div className="mt-2 space-y-1 text-xs">
                                    <div className="flex justify-between"><span>Du</span><span className="font-semibold">{formatMontant(mois.total_du)}</span></div>
                                    <div className="flex justify-between"><span>Paye</span><span className="font-semibold text-green-700">{formatMontant(mois.total_paye)}</span></div>
                                    <div className="flex justify-between"><span>Reste</span><span className="font-semibold text-red-700">{formatMontant(mois.reste_a_payer)}</span></div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4">
                          <h3 className="text-sm font-semibold text-gray-900 mb-3">Paiement {categoriePaiementActive.label.toLowerCase()}</h3>

                          {/* Cartes-radio identiques a la scolarite, en version compacte :
                              cette colonne fait un tiers de large, le badge passe sous le
                              titre au lieu d'etre aligne a droite. */}
                          <div className="space-y-3">
                            {[
                              {
                                valeur: 'mois_selectionnes',
                                actif: typePaiement !== 'partiel',
                                titre: 'Payer les mois sélectionnés',
                                description: 'Montant calculé automatiquement',
                                badge: 'AUTO',
                                bordure: 'border-[#246990] bg-blue-50',
                                pastille: 'border-[#246990]',
                                point: 'bg-[#246990]',
                                badgeStyle: 'bg-blue-100 text-blue-700'
                              },
                              {
                                valeur: 'partiel',
                                actif: typePaiement === 'partiel',
                                titre: 'Paiement partiel',
                                description: 'Saisir un montant libre',
                                badge: 'MONTANT LIBRE',
                                bordure: 'border-orange-500 bg-orange-50',
                                pastille: 'border-orange-500',
                                point: 'bg-orange-500',
                                badgeStyle: 'bg-orange-100 text-orange-700'
                              }
                            ].map((option) => (
                              <div
                                key={option.valeur}
                                onClick={() => setTypePaiement(option.valeur)}
                                className={`flex items-start gap-3 rounded-lg border-2 p-3 cursor-pointer transition-all ${
                                  option.actif ? option.bordure : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 bg-white ${
                                  option.actif ? option.pastille : 'border-gray-300'
                                }`}>
                                  {option.actif && <div className={`h-3 w-3 rounded-full ${option.point}`} />}
                                </div>

                                <div className="min-w-0 flex-1">
                                  <span className="block text-sm font-medium text-gray-900">{option.titre}</span>
                                  <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${option.badgeStyle}`}>
                                    {option.badge}
                                  </span>
                                  <span className="mt-1 block text-xs text-gray-600">{option.description}</span>
                                </div>
                              </div>
                            ))}
                          </div>

                          {typePaiement === 'partiel' && (
                            <div className="mt-4">
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Montant a encaisser</label>
                              <input
                                type="number"
                                min="0"
                                value={montantPaiement}
                                onChange={(event) => setMontantPaiement(event.target.value)}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10 outline-none"
                                placeholder="0"
                              />
                            </div>
                          )}

                          <div className="mt-4">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Mode de paiement</label>

                            {/* Champ de recherche filtrante, comme dans la scolarite : un select
                                natif obligerait a parcourir la liste systeme, dont le rendu varie
                                d'une plateforme a l'autre. Les etats sont ceux deja en place. */}
                            <div className="relative">
                              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />

                              <input
                                type="text"
                                placeholder="Rechercher un mode de paiement..."
                                value={isModePaiementDropdownOpen
                                  ? (modePaiementSearchTerm || '')
                                  : (modesPaiement.find(m => m.id.toString() === String(modePaiement))?.nom || '')}
                                onChange={(e) => {
                                  setModePaiementSearchTerm(e.target.value);
                                  setIsModePaiementDropdownOpen(true);
                                }}
                                onFocus={() => {
                                  setModePaiementSearchTerm('');
                                  setIsModePaiementDropdownOpen(true);
                                }}
                                className="w-full rounded-lg border-2 border-gray-200 bg-white py-2 pl-9 pr-9 text-sm outline-none transition-all hover:border-[#246990] focus:border-[#246990] focus:ring-4 focus:ring-[#246990]/10"
                              />

                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ChevronDown className="h-4 w-4 text-gray-400" />
                              </div>

                              {isModePaiementDropdownOpen && (
                                <div className="absolute top-full z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border-2 border-gray-200 bg-white shadow-lg">
                                  {(() => {
                                    const filtres = modesPaiement.filter((mode) =>
                                      mode.nom.toLowerCase().includes((modePaiementSearchTerm || '').toLowerCase())
                                    );

                                    if (filtres.length === 0) {
                                      return (
                                        <div className="p-4 text-center text-sm text-gray-500">
                                          Aucun mode de paiement trouvé
                                        </div>
                                      );
                                    }

                                    return filtres.map((mode) => (
                                      <div
                                        key={mode.id}
                                        onClick={() => {
                                          setModePaiement(mode.id.toString());
                                          setModePaiementSearchTerm('');
                                          setIsModePaiementDropdownOpen(false);
                                        }}
                                        className="cursor-pointer border-b px-4 py-2.5 text-sm font-medium text-gray-900 transition-colors last:border-b-0 hover:bg-blue-50"
                                      >
                                        {mode.nom}
                                      </div>
                                    ));
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Observations</label>
                            <textarea
                              value={observations}
                              onChange={(event) => setObservations(event.target.value)}
                              rows="3"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10 outline-none resize-none"
                              placeholder="Note optionnelle..."
                            />
                          </div>

                          <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm">
                            <div className="flex justify-between gap-3"><span className="text-gray-600">Mois selectionnes</span><span className="font-semibold">{moisSelectionnes.length}</span></div>
                            <div className="mt-1 flex justify-between gap-3"><span className="text-gray-600">A encaisser</span><span className="font-bold text-[#246990]">{formatMontant(montantAEncaisser)}</span></div>
                          </div>

                          <button
                            type="button"
                            onClick={handlePaiementAnnexe}
                            disabled={loadingPaiement || !modePaiement || moisSelectionnes.length === 0 || montantAEncaisser <= 0}
                            className="mt-4 w-full rounded-lg bg-[#246990] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1d5475] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {loadingPaiement ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            Valider le paiement
                          </button>
                        </div>

                        <div className="rounded-lg border border-[#246990]/20 bg-white shadow-sm overflow-hidden">
                          <div className="border-b border-gray-200 bg-[#eef7fb] px-4 py-3">
                            <h3 className="text-sm font-semibold text-gray-900">Ticket ponctuel</h3>
                            <p className="text-xs text-gray-600">
                              {activeCategoriePaiement === 'transport'
                                ? 'Encaisser un trajet d une journee ou demi-journee pour les eleves selectionnes.'
                                : 'Encaisser un repas ponctuel pour les eleves selectionnes.'}
                            </p>
                          </div>

                          <div className="space-y-3 p-4">
                            <div className="grid grid-cols-1 gap-3">
                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Date du service</label>
                                <input
                                  type="date"
                                  value={paiementPonctuelFamille.date_service}
                                  onChange={(event) => handlePaiementPonctuelFamilleChange('date_service', event.target.value)}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10 outline-none"
                                />
                              </div>

                              {activeCategoriePaiement === 'transport' && (
                                <>
                                  <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1">Type de trajet</label>
                                    <div className="grid grid-cols-2 gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
                                      {[
                                        { id: 'journee', label: 'Journee' },
                                        { id: 'demi_journee', label: 'Demi-journee' }
                                      ].map((option) => (
                                        <button
                                          key={option.id}
                                          type="button"
                                          onClick={() => handlePaiementPonctuelFamilleChange('type_paiement', option.id)}
                                          className={`rounded-md px-2 py-2 text-xs font-semibold ${paiementPonctuelFamille.type_paiement === option.id ? 'bg-[#246990] text-white' : 'text-gray-600 hover:bg-white'}`}
                                        >
                                          {option.label}
                                        </button>
                                      ))}
                                    </div>
                                  </div>

                                  {paiementPonctuelFamille.type_paiement === 'demi_journee' && (
                                    <div>
                                      <label className="block text-xs font-semibold text-gray-600 mb-1">Sens</label>
                                      <div className="grid grid-cols-2 gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
                                        {[
                                          { id: 'aller', label: 'Aller' },
                                          { id: 'retour', label: 'Retour' }
                                        ].map((option) => (
                                          <button
                                            key={option.id}
                                            type="button"
                                            onClick={() => handlePaiementPonctuelFamilleChange('sens_transport', option.id)}
                                            className={`rounded-md px-2 py-2 text-xs font-semibold ${paiementPonctuelFamille.sens_transport === option.id ? 'bg-[#246990] text-white' : 'text-gray-600 hover:bg-white'}`}
                                          >
                                            {option.label}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}

                              <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Montant unitaire</label>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  value={paiementPonctuelFamille.montant}
                                  onChange={(event) => handlePaiementPonctuelFamilleChange('montant', event.target.value.replace(/[^0-9]/g, ''))}
                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10 outline-none"
                                  placeholder="Ex: 1000"
                                />
                              </div>
                            </div>

                            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                              <div className="mb-2 flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-gray-700">Eleves concernes</span>
                                <button
                                  type="button"
                                  onClick={() => setElevesPonctuelsSelectionnes(elevesPonctuelsDisponibles.map((eleve) => Number(eleve.id || eleve.eleve_id)).filter(Boolean))}
                                  className="text-xs font-semibold text-[#246990] hover:underline"
                                >
                                  Tout selectionner
                                </button>
                              </div>
                              <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
                                {elevesPonctuelsDisponibles.length === 0 ? (
                                  <p className="text-xs text-gray-500">Aucun eleve actif disponible.</p>
                                ) : elevesPonctuelsDisponibles.map((eleve) => {
                                  const id = Number(eleve.id || eleve.eleve_id);
                                  const checked = elevesPonctuelsSelectionnes.includes(id);
                                  return (
                                    <div
                                      key={`ponctuel-${id}`}
                                      onClick={() => toggleElevePonctuel(id)}
                                      className={`flex cursor-pointer items-center justify-between gap-3 rounded-md border-2 bg-white px-3 py-2 text-xs transition-colors ${
                                        checked ? 'border-[#246990] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <span className="min-w-0">
                                        <span className="block font-semibold text-gray-900">{eleve.prenom} {eleve.nom}</span>
                                        <span className="text-gray-500">{eleve.classe_nom || 'Classe non renseignee'}</span>
                                      </span>

                                      {/* Case dessinee, comme la liste des mois de la scolarite */}
                                      <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-all ${
                                        checked ? 'border-[#246990] bg-[#246990]' : 'border-gray-300 bg-white'
                                      }`}>
                                        {checked && (
                                          <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            <textarea
                              value={paiementPonctuelFamille.observations}
                              onChange={(event) => handlePaiementPonctuelFamilleChange('observations', event.target.value)}
                              rows="2"
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/10 outline-none resize-none"
                              placeholder="Observation optionnelle..."
                            />

                            <div className="rounded-lg border border-[#246990]/20 bg-[#246990]/5 p-3 text-sm">
                              <div className="flex justify-between gap-3"><span className="text-gray-600">Montant unitaire</span><span className="font-semibold">{formatMontant(montantUnitairePonctuel || 0)}</span></div>
                              <div className="mt-1 flex justify-between gap-3"><span className="text-gray-600">Eleves selectionnes</span><span className="font-semibold">{elevesPonctuelsSelectionnes.length}</span></div>
                              <div className="mt-2 flex justify-between gap-3 border-t border-[#246990]/20 pt-2"><span className="font-semibold text-gray-800">Total ticket</span><span className="font-bold text-[#246990]">{formatMontant(totalPonctuelFamille)}</span></div>
                            </div>

                            <button
                              type="button"
                              onClick={handlePaiementPonctuelFamille}
                              disabled={loadingPaiement || !modePaiement || elevesPonctuelsSelectionnes.length === 0 || totalPonctuelFamille <= 0}
                              className="w-full rounded-lg bg-[#246990] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1d5475] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              {loadingPaiement ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                              Encaisser le ticket ponctuel
                            </button>
                          </div>
                        </div>

                        <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                          <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
                            <h3 className="text-sm font-semibold text-gray-900">Historique ponctuel</h3>
                            <p className="text-xs text-gray-500">Tickets journaliers ou demi-journee deja encaisses.</p>
                          </div>
                          {paiementsPonctuels.length === 0 ? (
                            <div className="p-4 text-xs text-gray-500">Aucun ticket ponctuel enregistre.</div>
                          ) : (
                            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                              {paiementsPonctuels.map((paiement) => (
                                <div key={`historique-ponctuel-${paiement.id}`} className="p-3 text-xs">
                                  <div className="flex items-start justify-between gap-3">
                                    <div>
                                      <p className="font-semibold text-gray-900">{paiement.eleve_prenom} {paiement.eleve_nom}</p>
                                      <p className="text-gray-500">{paiement.date_service_formatee || paiement.date_service} - {paiement.type_label || '-'}</p>
                                    </div>
                                    <span className="font-bold text-[#246990]">{formatMontant(paiement.montant)}</span>
                                  </div>
                                  <div className="mt-2 flex items-center justify-between gap-2">
                                    <span className="text-gray-500">{paiement.date_paiement_formatee || '-'}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleTelechargerRecuAnnexePonctuelFamille(paiement)}
                                      className="inline-flex items-center gap-1 rounded-md bg-[#246990] px-2 py-1 text-white hover:bg-[#1d5475]"
                                    >
                                      <Download className="h-3 w-3" />
                                      Recu
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs text-blue-800">
                          Les frais de {categoriePaiementActive.label.toLowerCase()} sont des frais annexes. Ils ne modifient pas les reductions famille et seront classes separement dans le portefeuille.
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </>
      )}
      {showModalModif && (
        <ModalModifierFamille
          famille={selectedFamille}
          statsGlobales={statsGlobales}
          elevesFamille={elevesFamille}
          interSiteInfo={interSiteInfo}
          moisScolaires={moisScolaires}
          rapportParMois={rapportParMois}
          onClose={() => setShowModalModif(false)}
          onSave={async (formData) => {
            try {
              console.log('💾 Sauvegarde des modifications...');

              // 1. Sauvegarder les modifications
              const result = await api.familles.modifierMontantMensuel(selectedFamille.id, formData);

              if (result.success && result.validation_required) {
                showNotification('success', result.message || 'Demande envoyee au Centre de Support');
                setShowModalModif(false);
                return true;
              }

              if (result.success) {
                console.log('✅ Modification réussie, rechargement des données...');

                // 2. Recharger COMPLÈTEMENT les données de la famille
                await loadFamilleDetails(selectedFamille.id);

                // 3. Afficher le message de succès
                showNotification('success', `Montant modifié avec succès : ${formData.nouveau_montant_mensuel} FCFA`);

                // 4. Fermer le modal APRÈS le rechargement
                setShowModalModif(false);

                return true;
              } else {
                console.error('❌ Erreur modification:', result.message);
                showNotification('error', result.message || 'Erreur lors de la modification');
                return false;
              }
            } catch (error) {
              console.error('❌ Erreur modification:', error);
              showNotification('error', 'Erreur lors de la modification');
              return false;
            }
          }}
        />
      )}



      {/* 🆕 MODAL AJOUTER ÉLÈVE */}
      {eleveReductionAModifier && (
        <ModalModifierReductionEleve
          eleve={eleveReductionAModifier}
          anneeScolaireId={anneeScolaireId}
          onClose={() => setEleveReductionAModifier(null)}
          onSuccess={(result) => {
            setEleveReductionAModifier(null);
            showNotification('success', result?.message || 'Demande envoyée au Centre de Support.');
            if (selectedFamille?.id) loadFamilleDetails(selectedFamille.id);
          }}
        />
      )}

      {showModalAjouterEleve && (
        <ModalAjouterEleve
          famille={selectedFamille}
          anneeScolaireId={anneeScolaireId}
          onClose={() => setShowModalAjouterEleve(false)}
          onSuccess={async (data) => {
            if (data?.validation_required) {
              showNotification('success', data.message || 'Demande envoyee au Centre de Support. L eleve sera ajoute apres validation.');
              setShowModalAjouterEleve(false);
              return;
            }
            console.log('✅ Élève ajouté:', data);

            // Afficher le message de succès
            showNotification('success', `Élève ajouté avec succès à la famille ${selectedFamille.nom} !`);

            // Recharger les données de la famille
            await loadFamilleDetails(selectedFamille.id);

            // Recharger les stats du mois si un mois est sélectionné
            if (selectedMois) {
              await loadStatsMois(selectedMois);
            }

            // Fermer le modal
            setShowModalAjouterEleve(false);
          }}
        />
      )}

      <LongToast
        show={longToast.show}
        title={longToast.title}
        message={longToast.message}
        type={longToast.type}
        onClose={closeLongToast}
      />

      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, visible: false })}
        />
      )}


      {/* 🆕 DIALOG DE CONFIRMATION RETRAIT ÉLÈVE */}
      {showConfirmDialog && eleveARetirer && (
        <ConfirmDialog
          isOpen={showConfirmDialog}
          onClose={() => {
            setShowConfirmDialog(false);
            setEleveARetirer(null);
            setMotifRetraitEleve('');
          }}
          onConfirm={async () => {
            setShowConfirmDialog(false);
            await handleRetirerEleve(eleveARetirer.id);
            setEleveARetirer(null);
            setMotifRetraitEleve('');
          }}
          title={`Demander le retrait de ${eleveARetirer.prenom} ${eleveARetirer.nom} ?`}
          description={
            <div className="text-left space-y-2">
              <p className="font-medium text-gray-700">Cette demande sera envoyee au Centre de Support.</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-2">
                <li>Le retrait sera applique uniquement apres validation.</li>
                <li>L'eleve redeviendra individuel a partir du mois d'effet.</li>
                <li>Les mois payes, partiels, au prorata ou soldes seront conserves.</li>
                <li>Si la famille passe sous le minimum, elle sera marquee a regulariser avant archivage automatique.</li>
              </ul>
              <p className="text-sm text-gray-500 italic pt-2">
                Par defaut, le mois d'effet est le prochain mois totalement non paye.
              </p>
            </div>
          }
          confirmText="Envoyer la demande"
          cancelText="Annuler"
          type="warning"
          isLoading={retraitLoading}
          confirmDisabled={retraitLoading || !motifRetraitEleve.trim()}
        >
          <div className="mt-4 text-left">
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Motif de la demande *
            </label>
            <textarea
              value={motifRetraitEleve}
              onChange={(event) => setMotifRetraitEleve(event.target.value)}
              className="min-h-[96px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
              placeholder="Ex: changement de responsabilite familiale, enfant sorti du foyer fiscal..."
              disabled={retraitLoading}
            />
            <p className="mt-2 text-xs text-gray-500">
              Ce motif sera conserve dans la demande speciale et dans l'historique de la famille.
            </p>
          </div>
        </ConfirmDialog>
      )}



      {/* DIALOG CONFIRMATION ACTIVATION INTER-SITE */}
      {showConfirmInterSite && (
        <ConfirmDialog
          isOpen={showConfirmInterSite}
          onClose={() => setShowConfirmInterSite(false)}
          onConfirm={async () => {
            setShowConfirmInterSite(false);
            await handleActiverInterSite();
          }}
          title={`Activer "${selectedFamille?.nom}" en inter-site ?`}
          description={
            <div className="text-left space-y-2">
              <p className="font-medium text-gray-700">Cette action va :</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-2">
                <li>Rendre cette famille visible par tous les sites du groupe</li>
                <li>Permettre aux autres sites de rattacher leurs élèves à cette famille</li>
                <li>Ce site deviendra le <strong>site de référence</strong> de la famille</li>
              </ul>
              {/* La phrase se construit sur la valeur relue du serveur, et dit ce
                  qui est vrai plutôt qu'une formule unique : une valeur nulle ne
                  se propage pas, et une valeur que personne ne suit ne réduit rien. */}
              {!apercuActivationIS ? (
                <p className="text-sm text-gray-500 pt-2">Lecture de la valeur en cours…</p>
              ) : apercuActivationIS.reduction_mensuel > 0 || apercuActivationIS.reduction_inscription > 0 ? (
                <div className="pt-2 space-y-1">
                  <p className="text-sm text-gray-600">
                    La valeur famille actuelle
                    ({new Intl.NumberFormat('fr-FR').format(Math.round(apercuActivationIS.reduction_mensuel))} FCFA/mois)
                    sera portée par le groupe et propagée aux autres sites.
                  </p>
                  {apercuActivationIS.nb_suivent === 0 && (
                    <p className="text-sm text-amber-800">
                      Aucun des {apercuActivationIS.nb_eleves} élèves de cette famille ne suit cette valeur :
                      elle sera propagée sans réduire quoi que ce soit ici.
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600 pt-2">
                  Cette famille ne porte aucune valeur de réduction : il n'y a rien à propager pour l'instant.
                </p>
              )}
            </div>
          }
          confirmText="Activer Inter-Site"
          cancelText="Annuler"
          type="question"
          isLoading={activationLoading}
        />
      )}


      {/* 🆕 MODAL MOIS NON SCOLARISÉS */}
      {showModalMoisNonScolarises && (
        <ModalMoisNonScolarises
          famille={selectedFamille}
          anneeScolaireId={anneeScolaireId}
          moisScolaires={moisScolaires}
          elevesFamille={elevesFamille}
          showNotification={showNotification}  // 🆕 AJOUTER CETTE LIGNE
          onClose={() => setShowModalMoisNonScolarises(false)}
          onSuccess={async (data) => {
            // 🆕 AFFICHER UN MESSAGE ADAPTÉ
            if (data.nombre_ignores > 0) {
              const detailsIgnores = data.mois_ignores
                .map(m => `${m.eleve} - ${m.mois}`)
                .join(', ');

              showNotification(
                'warning',
                `${data.nombre_modifications} mois enregistré(s). ${data.nombre_ignores} mois ignoré(s) (déjà payés) : ${detailsIgnores}`
              );
            } else {
              showNotification('success', 'Mois non scolarisés enregistrés avec succès !');
            }

            await loadFamilleDetails(selectedFamille.id);
            if (selectedMois) await loadStatsMois(selectedMois);
            setShowModalMoisNonScolarises(false);
          }}
        />
      )}


      {/* ============ MODAL RATTACHER FAMILLE INTER-SITE ============ */}
      {showModalRattacher && (
        <ModalRattacherFamilleInterSite
          anneeScolaireId={anneeScolaireId}
          onClose={() => setShowModalRattacher(false)}
          onSuccess={async (data) => {
            showNotification('success', `${data.nb_rattaches} élève(s) rattaché(s) avec succès !`);
            setShowModalRattacher(false);

            // Recharger les données
            if (selectedFamille) {
              await loadFamilleDetails(selectedFamille.id);
              await loadInterSiteInfo(selectedFamille.id);
            } else {
              // Si on vient de la liste, recharger la liste
              await loadInitialData();
            }
          }}
        />
      )}


      {/* ============ MODAL ARCHIVER FAMILLE INTER-SITE ============ */}


      {showConfirmArchiver && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Archive className="w-5 h-5 text-amber-500" />
                Archiver la famille {selectedFamille?.nom}
              </h2>
            </div>
            <div className="p-5 space-y-4">


              {/* Explication */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <span className="text-xs text-amber-700">
                  L'archivage va désactiver la famille. Les paiements existants seront conservés intacts.
                  Vous pourrez restaurer la famille depuis la page <strong>Archives</strong>.
                </span>
              </div>

              {/* Avertissement inter-site */}
              {interSiteInfo?.is_intersite && (
                <div className="p-3 bg-red-50 border border-red-300 rounded-lg flex items-start gap-2">
                  <Network className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-red-700">
                    <p className="font-semibold">Cette famille est actuellement liée au réseau inter-sites</p>
                    <p className="mt-1">
                      L'archivage va <strong>couper la liaison inter-site</strong> et désactiver la famille dans le Hub.
                      Les sites distants perdront la connexion avec cette famille et leurs familles locales
                      avec moins de 2 membres seront automatiquement archivées.
                    </p>
                    <p className="mt-1">
                      {interSiteInfo?.is_reference ? (
                        <>Si vous souhaitez uniquement retirer la famille du réseau inter-sites sans l'archiver,
                          utilisez plutôt l'option <strong>"Désactiver Inter-Site"</strong> dans le menu Actions.</>
                      ) : (
                        <>La désactivation de l'inter-site ne peut être effectuée que par le <strong>site de référence</strong>.
                          Si vous souhaitez couper la liaison inter-site sans archiver cette famille,
                          contactez l'administrateur du site de référence pour qu'il désactive l'inter-site.</>
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* Motif */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif d'archivage <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <input
                    type="text"
                    placeholder="Sélectionner un motif..."
                    value={motifArchivage}
                    onFocus={() => setIsMotifDropdownOpen(true)}
                    readOnly
                    className="w-full px-4 py-3 pr-10 text-base font-medium border-2 border-gray-200 rounded-lg 
                            bg-white hover:border-amber-400 focus:border-amber-400 focus:ring-4 focus:ring-amber-400/10 
                            outline-none transition-all cursor-pointer"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {isMotifDropdownOpen && (
                    <div className="absolute z-20 w-full top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg overflow-hidden">
                      {[
                        'Famille a quitté l\'établissement',
                        'Élèves transférés vers un autre site',
                        'Fin de scolarité de tous les enfants',
                        'Dissolution de la famille nombreuse',
                        'Erreur de création',
                        'Autre'
                      ].map((motif) => (
                        <div
                          key={motif}
                          onClick={() => {
                            setMotifArchivage(motif);
                            setIsMotifDropdownOpen(false);
                          }}
                          className={`px-4 py-3 hover:bg-amber-50 cursor-pointer border-b last:border-b-0 transition-colors
                          ${motifArchivage === motif ? 'bg-amber-100 font-semibold' : ''}`}
                        >
                          <div className="font-medium text-gray-900 text-sm">{motif}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

              {/* Checkbox radier les élèves */}
              <div className="p-3 border border-gray-200 rounded-lg space-y-3">
                <label className="flex items-start gap-3 cursor-pointer" onClick={() => setRadierElevesArchivage(!radierElevesArchivage)}>
                  <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${radierElevesArchivage ? 'border-red-500 bg-red-500' : 'border-gray-300 bg-white'
                    }`}>
                    {radierElevesArchivage && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">Radier également les élèves</span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Les élèves seront marqués comme radiés de l'établissement
                    </p>
                  </div>
                </label>

                {/* Explication selon le choix */}
                {radierElevesArchivage ? (
                  <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700 flex items-start gap-2">
                    <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Archivage + Radiation :</p>
                      <p className="mt-0.5">La famille sera archivée et les {elevesFamille?.filter(e => e.statut_eleve === 'actif').length || 0} élève(s) actif(s) seront radiés. Ils apparaîtront dans la liste des élèves radiés.</p>
                      <p className="mt-1">Lors d'une restauration, les élèves redeviendront actifs et seront <strong>automatiquement réintégrés</strong> dans la famille, sauf s'ils ont été rattachés à une autre famille entre-temps.</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 flex items-start gap-2">
                    <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Archivage seul (sans radiation) :</p>
                      <p className="mt-0.5">La famille sera archivée mais les élèves resteront <strong>actifs</strong> dans l'établissement en tant qu'élèves individuels. Leurs réductions famille seront retirées et les paiements non réglés seront recalculés au tarif normal.</p>
                      <p className="mt-1">Lors d'une restauration, les élèves seront <strong>automatiquement réintégrés</strong> dans la famille, sauf s'ils ont été rattachés à une autre famille entre-temps.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Boutons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowConfirmArchiver(false); setMotifArchivage(''); setRadierElevesArchivage(true); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    handleArchiverFamille();
                  }}
                  disabled={!motifArchivage || archivageLoading}
                  className="flex-1 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {archivageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                  Archiver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* ============ MODAL DESACTIVER FAMILLE INTER-SITE ============ */}
      {showConfirmDesactiverIS && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <Network className="w-5 h-5 text-red-500" />
                Désactiver l'inter-site
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-red-700 space-y-1">
                  <p className="font-semibold">Cette action va :</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-1">
                    <li>Retirer cette famille du réseau inter-sites</li>
                    <li>Sur les sites distants avec <strong>moins de 2 membres</strong> : la famille sera <strong>archivée automatiquement</strong> et les élèves retirés de la famille (mais pas radiés)</li>
                    <li>Sur les sites distants avec <strong>2 membres ou plus</strong> : la famille <strong>restera active</strong> en tant que famille locale indépendante</li>
                    <li>La famille sur ce site (site de référence) <strong>ne sera pas modifiée</strong></li>
                  </ul>
                </div>
              </div>

              {vueConsolidee?.membres_distants?.length > 0 && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-xs font-semibold text-blue-700 mb-2">Sites distants concernés :</div>
                  {(() => {
                    const parSite = {};
                    vueConsolidee.membres_distants.forEach(m => {
                      if (!parSite[m.site_subdomain]) parSite[m.site_subdomain] = 0;
                      parSite[m.site_subdomain]++;
                    });
                    return Object.entries(parSite).map(([site, count]) => (
                      <div key={site} className="flex items-center justify-between text-xs py-1">
                        <span className="text-blue-600 font-medium">{site}</span>
                        <span className={`font-semibold ${count < 2 ? 'text-amber-600' : 'text-green-600'}`}>
                          {count} membre{count > 1 ? 's' : ''} — {count < 2 ? 'Sera archivée' : 'Restera active'}
                        </span>
                      </div>
                    ));
                  })()}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif de désactivation <span className="text-red-500">*</span>
                </label>
                <select
                  value={motifDesactivationIS}
                  onChange={(e) => setMotifDesactivationIS(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-400"
                >
                  <option value="">Sélectionner un motif...</option>
                  <option value="Les élèves ne sont plus répartis entre sites">Les élèves ne sont plus répartis entre sites</option>
                  <option value="Élèves distants radiés ou transférés">Élèves distants radiés ou transférés</option>
                  <option value="Fin de collaboration inter-sites">Fin de collaboration inter-sites</option>
                  <option value="Erreur d'activation">Erreur d'activation</option>
                  <option value="Restructuration de la famille">Restructuration de la famille</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowConfirmDesactiverIS(false); setMotifDesactivationIS(''); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDesactiverInterSite}
                  disabled={!motifDesactivationIS || desactivationLoading}
                  className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {desactivationLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Network className="w-4 h-4" />}
                  Désactiver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}




      {/* ============ MODAL RELIER AU HUB UNE ANCIENNE FAMILLE INTER-SITE ============ */}
      {showConfirmRelierHub && (
        <ConfirmDialog
          isOpen={showConfirmRelierHub}
          onClose={() => setShowConfirmRelierHub(false)}
          onConfirm={async () => {
            setShowConfirmRelierHub(false);
            await handleRelierAuHub();
          }}
          title={`Re-lier "${selectedFamille?.nom}" au Hub Inter-Site ?`}
          description={
            <div className="text-left space-y-2">
              <p className="font-medium text-gray-700">Cette famille était précédemment inter-site. Cette action va :</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 ml-2">
                <li>Réactiver la liaison avec le Hub inter-sites</li>
                <li>Rendre cette famille visible aux autres sites du groupe</li>
                <li>Ce site redeviendra le <strong>site de référence</strong></li>
                <li>Les autres sites pourront à nouveau y rattacher leurs élèves</li>
              </ul>
              <p className="text-sm text-gray-500 italic pt-2">
                Les réductions actuelles seront propagées vers les sites distants qui rattacheront des élèves.
              </p>
            </div>
          }
          confirmText="Re-lier au Hub"
          cancelText="Annuler"
          type="question"
          isLoading={relierHubLoading}
        />
      )}


      <GuideInterSiteDetail
        isOpen={showGuideInterSiteDetail}
        onClose={() => setShowGuideInterSiteDetail(false)}
        interSiteInfo={interSiteInfo}
        famille={selectedFamille}
      />


    </div>

  );
};

export default FamillesNombreuses;














